import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Routes, Route, Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatDisplayDate, formatDisplayDateTime, formatDisplayDateWithWeekday, formatRelativeTime } from "../utils/dateFormat";
import { 
  LayoutDashboard, Type, Palette, Image, Mail, 
  LogOut, Settings, MessageSquare, Languages, Eye, 
  Plus, Trash2, Save, RefreshCw, X, Search,
  GripVertical, FileText, ArrowUp, ArrowDown, Layers,
  AlignLeft, AlignCenter, AlignRight, ImageIcon, Calendar,
  Upload, Loader2, Pencil, Copy, ChevronUp, ChevronDown,
  Users, UserPlus, Key, CreditCard, Link2, Receipt, ExternalLink, Check,
  BarChart2, Activity, Filter, AlertTriangle, HelpCircle,
  ChevronLeft, ChevronRight, CheckCircle, Bell, History, Shield, AlertCircle,
  Database, Download
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CalendarManagement from "../components/admin/CalendarManagement";
import PreventivoPage from "../components/admin/PreventivoPage";
import PaymentLinkPage from "../components/admin/PaymentLinkPage";
import UpdatePage from "../components/admin/UpdatePage";
import ReportsPage from "../components/admin/ReportsPage";
import MessagesPage from "../components/admin/MessagesPage";
import DeleteConfirmDialog from "../components/admin/DeleteConfirmDialog";
import { PageInfoInline, PageInfoUnderTabs, PageInfoIcon } from "../components/admin/PageInfo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import ConfirmModal from "../components/ui/ConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_TYPES = [
  { value: "h1", label: "Heading 1 (H1)", icon: "H1" },
  { value: "h2", label: "Heading 2 (H2)", icon: "H2" },
  { value: "h3", label: "Heading 3 (H3)", icon: "H3" },
  { value: "paragraph", label: "Paragraph", icon: "P" },
  { value: "image", label: "Image", icon: "🖼" },
  { value: "gallery", label: "Gallery", icon: "📸" },
  { value: "button", label: "Button (CTA)", icon: "BTN" },
  { value: "spacer", label: "Spacer", icon: "—" }
];

const GALLERY_LAYOUTS = [
  { value: "grid", label: "Grid" },
  { value: "slider", label: "Slider" },
  { value: "columns-2", label: "2 Columns" },
  { value: "columns-3", label: "3 Columns" },
  { value: "columns-4", label: "4 Columns" }
];

const SECTION_TYPES = [
  { value: "hero", label: "Hero Section" },
  { value: "custom", label: "Custom Section" },
  { value: "gallery", label: "Gallery" },
  { value: "booking", label: "Booking Calendar" },
  { value: "contact", label: "Contact Form" }
];

const IMAGE_LAYOUTS = [
  { value: "full", label: "Full Width (100%)" },
  { value: "left", label: "Float Left (50%)" },
  { value: "right", label: "Float Right (50%)" }
];

const IMAGE_SIZING = [
  { value: "cover", label: "Responsive (cover)" },
  { value: "contain", label: "Responsive (contain)" },
  { value: "actual", label: "Actual Size" }
];

const TEXT_ALIGNMENTS = [
  { value: "left", label: "Left", icon: AlignLeft },
  { value: "center", label: "Center", icon: AlignCenter },
  { value: "right", label: "Right", icon: AlignRight }
];

const FONT_WEIGHTS = ["300", "400", "500", "600", "700"];
const FONTS = ["Cormorant Garamond", "Manrope", "Georgia", "Garamond", "Times New Roman", "Playfair Display", "Lora", "Merriweather"];

const LANGUAGES = {
  en: "English", it: "Italiano", es: "Español", fr: "Français",
  de: "Deutsch", pt: "Português", ru: "Русский", "zh-Hans": "简体中文", 
  ko: "한국어", ja: "日本語", ar: "العربية", hi: "हिन्दी"
};

// Language order for consistent display
const LANGUAGE_ORDER = ["en", "it", "es", "fr", "de", "pt", "ru", "zh-Hans", "ko", "ja", "ar", "hi"];

// Image Uploader Component with drag & drop
function ImageUploader({ value, onChange, getAuthHeaders, className = "", id = null }) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // ID univoco per ogni istanza - FIX per bug upload multipli
  const inputId = id || `image-upload-${Math.random().toString(36).substr(2, 9)}`;

  const handleUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo JPG, PNG o WebP sono permessi");
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande. Max 5MB");
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      onChange(res.data.url);
      toast.success("Immagine caricata");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload fallito");
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(e.target.files[0]);
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer
          ${dragActive ? "border-[#D4AF37] bg-[#FDF9EF]" : "border-[#D4AF37] hover:border-[#D4AF37]"}
          ${uploading ? "pointer-events-none opacity-50" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => document.getElementById(inputId)?.click()}
      >
        <input
          id={inputId}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          onChange={handleFileInput}
          className="hidden"
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 py-4">
            <Loader2 className="animate-spin" size={20} />
            <span className="text-sm text-[#78716C]">Caricamento...</span>
          </div>
        ) : (
          <>
            <Upload size={24} className="mx-auto mb-2 text-[#78716C]" />
            <p className="text-sm text-[#78716C]">
              Trascina un'immagine qui o <span className="text-[#D4AF37] font-medium">clicca per caricare</span>
            </p>
            <p className="text-xs text-[#A8A29E] mt-1">JPG, PNG, WebP • Max 5MB</p>
          </>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#78716C]">oppure URL:</span>
        <Input 
          placeholder="https://example.com/image.jpg" 
          value={value || ""} 
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
        />
      </div>
      
      {value && (
        <div className="relative bg-[#F9F8F6] p-2 rounded-lg">
          <img src={value.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value} alt="Preview" className="max-h-32 mx-auto object-contain" />
          <button 
            onClick={() => onChange("")}
            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function useAuth() {
  const navigate = useNavigate();
  // IMPORTANTE: Admin usa chiavi separate (admin_token, admin_user)
  // Fallback ai vecchi token generici per retrocompatibilità
  const [token, setToken] = useState(
    localStorage.getItem("admin_token") || localStorage.getItem("token")
  );
  const [user, setUser] = useState(() => {
    try {
      const adminUser = localStorage.getItem("admin_user");
      if (adminUser) return JSON.parse(adminUser);
      // Fallback al vecchio formato
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  
  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    // Redirect cleaning users to their dedicated dashboard
    if (user.role === "cleaning") {
      navigate("/admin/cleaning");
    }
  }, [token, user.role, navigate]);

  const logout = () => {
    // Rimuovi token Admin
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    // Rimuovi anche vecchi token generici
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser({});
    navigate("/admin");
  };

  const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });
  return { token, user, logout, getAuthHeaders };
}

// Dashboard Overview
function DashboardOverview() {
  const { getAuthHeaders } = useAuth();
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Calendario mensile - stato
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [calendarBlocked, setCalendarBlocked] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Stato sincronizzazione per dashboard
  const [syncStatusDashboard, setSyncStatusDashboard] = useState(null);
  const [importedBookingsStatsDashboard, setImportedBookingsStatsDashboard] = useState(null);
  
  // Smart polling: traccia la versione del calendario per evitare fetch inutili
  const [lastKnownVersion, setLastKnownVersion] = useState(0);
  
  // Ultime prenotazioni per sezione Update preview
  const [latestBookings, setLatestBookings] = useState([]);
  const [latestBookingsLoading, setLatestBookingsLoading] = useState(false);

  // Ricarica dati dashboard quando si naviga alla pagina (per aggiornare dopo modifiche in altre sezioni)
  // Ricarica dati dashboard quando si naviga alla pagina (per aggiornare dopo modifiche in altre sezioni)
  useEffect(() => { 
    fetchDashboardData(); 
    fetchLatestBookings();
  }, [location.pathname]);
  
  // Fetch ultime prenotazioni per sezione Update
  const fetchLatestBookings = async () => {
    setLatestBookingsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/updates/bookings`, getAuthHeaders());
      // Prendi fino a 10, già ordinate per data più recente
      setLatestBookings((res.data.bookings || []).slice(0, 10));
    } catch (error) {
      console.error("Error fetching latest bookings:", error);
    } finally {
      setLatestBookingsLoading(false);
    }
  };
  
  // Fetch sync status per dashboard
  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const [statusRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/sync-status`, getAuthHeaders()),
          axios.get(`${API}/admin/sync-logs/imported-bookings-stats`, getAuthHeaders())
        ]);
        setSyncStatusDashboard(statusRes.data);
        setImportedBookingsStatsDashboard(statsRes.data);
      } catch (error) {
        console.error("Error fetching sync status:", error);
      }
    };
    fetchSyncStatus();
  }, []);
  
  // Carica dati calendario quando cambia il mese
  useEffect(() => { fetchCalendarData(); }, [calendarMonth.year, calendarMonth.month]);
  
  // Smart polling: controlla versione ogni 120 secondi
  useEffect(() => {
    const checkCalendarVersion = async () => {
      try {
        const res = await axios.get(`${API}/calendar/version`, getAuthHeaders());
        const serverVersion = res.data.version;
        
        if (serverVersion > lastKnownVersion) {
          console.log(`📊 Dashboard: Calendar version changed (${lastKnownVersion} → ${serverVersion}), refreshing data...`);
          setLastKnownVersion(serverVersion);
          fetchDashboardData();
          fetchCalendarData();
        }
      } catch (error) {
        console.error("Error checking calendar version:", error);
      }
    };
    
    // Polling ogni 120 secondi
    const interval = setInterval(checkCalendarVersion, 120000);
    
    // Carica versione iniziale
    checkCalendarVersion();
    
    return () => clearInterval(interval);
  }, [lastKnownVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/dashboard-data`, getAuthHeaders());
      setDashboardData(res.data);
    } catch (error) { 
      console.error("Error fetching dashboard data:", error); 
    } finally {
      setLoading(false);
    }
  };
  
  // Carica bookings e blocked dates per il mese corrente (READ-ONLY)
  const fetchCalendarData = async () => {
    try {
      setCalendarLoading(true);
      const [bookingsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/calendar/blocked`, getAuthHeaders())
      ]);
      setCalendarBookings(bookingsRes.data || []);
      setCalendarBlocked(blockedRes.data || []);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setCalendarLoading(false);
    }
  };
  
  // Navigazione mese
  const goToPrevMonth = () => {
    setCalendarMonth(prev => {
      const newMonth = prev.month - 1;
      if (newMonth < 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: newMonth };
    });
  };
  
  const goToNextMonth = () => {
    setCalendarMonth(prev => {
      const newMonth = prev.month + 1;
      if (newMonth > 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: newMonth };
    });
  };
  
  const goToToday = () => {
    const now = new Date();
    setCalendarMonth({ year: now.getFullYear(), month: now.getMonth() });
  };
  
  // Genera i giorni del mese per il calendario
  const generateMonthDays = useMemo(() => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Padding iniziale (Lun=0, Dom=6)
    let startPadding = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    
    // Padding giorni vuoti
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    // Giorni del mese
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month, d);
      // Usa formato locale (non UTC) per evitare sfasamento fuso orario
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({
        day: d,
        date: dateStr,
        dateObj
      });
    }
    
    return days;
  }, [calendarMonth]);
  
  // Helper: verifica se una data è oggi
  const isToday = useCallback((dateStr) => {
    // Usa data locale (non UTC) per evitare sfasamento fuso orario
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return dateStr === today;
  }, []);
  
  // Helper: verifica se una data è passata
  const isPast = useCallback((dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    return date < today;
  }, []);
  
  // Helper: ottiene booking per una data
  const getBookingForDate = useCallback((dateStr) => {
    return calendarBookings.find(b => {
      if (!b.check_in || !b.check_out) return false;
      const status = b.status || "";
      if (!["paid", "confirmed", "ical"].includes(status) && !b.source?.startsWith("ical_")) return false;
      return dateStr >= b.check_in && dateStr < b.check_out;
    });
  }, [calendarBookings]);
  
  // Helper: verifica se è giorno di checkout
  const isCheckOutDay = useCallback((dateStr) => {
    return calendarBookings.some(b => 
      b.check_out === dateStr && 
      (b.status === "paid" || b.status === "confirmed" || b.status === "ical" || b.source?.startsWith("ical_"))
    );
  }, [calendarBookings]);
  
  // Helper: verifica se è giorno di checkin
  const isCheckInDay = useCallback((dateStr) => {
    return calendarBookings.some(b => 
      b.check_in === dateStr && 
      (b.status === "paid" || b.status === "confirmed" || b.status === "ical" || b.source?.startsWith("ical_"))
    );
  }, [calendarBookings]);
  
  // Helper: verifica se è bloccato manualmente (include booking-generated blocks)
  const isManuallyBlocked = useCallback((dateStr) => {
    return calendarBlocked.some(b => 
      b.date === dateStr && 
      (b.source === "manual" || b.source === "booking")
    );
  }, [calendarBlocked]);
  
  // Helper: verifica se è bloccato da iCal (blocchi senza booking corrispondente)
  const isICalBlocked = useCallback((dateStr) => {
    return calendarBlocked.some(b => 
      b.date === dateStr && 
      (b.source === "ical" || b.source?.startsWith("ical_") || b.source === "channel_manager")
    );
  }, [calendarBlocked]);
  
  // Helper: verifica se è bloccato (qualsiasi fonte)
  const isBlocked = useCallback((dateStr) => {
    return calendarBlocked.some(b => b.date === dateStr);
  }, [calendarBlocked]);
  
  // Helper: verifica se è booking iCal
  const isICalBooking = useCallback((booking) => {
    if (!booking) return false;
    return booking.status === "ical" || booking.source?.startsWith("ical_") || booking.source === "ical";
  }, []);
  
  // Nome mese in italiano
  const monthNames = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                      "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  
  // Stato per modale note (read-only) - deprecato, usare bookingModal
  const [noteModal, setNoteModal] = useState({ open: false, note: "", title: "" });
  
  // Stato per modale dettagli prenotazione completi (read-only)
  const [bookingModal, setBookingModal] = useState({ open: false, booking: null });
  
  // Formatta data corrente per display (es. "Mercoledì 8 Gennaio 2025")
  const formatCurrentDate = () => {
    const now = new Date();
    const weekdays = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
    const months = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", 
                    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
    return `${weekdays[now.getDay()]} ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "adesso";
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours}h fa`;
    if (diffDays === 1) return "ieri";
    if (diffDays < 7) return `${diffDays} giorni fa`;
    return formatDisplayDate(date);
  };

  // Format date - use centralized utility
  const formatDate = (dateStr) => {
    return formatDisplayDate(dateStr);
  };

  // Format date with time for logs - use centralized utility
  const formatDateTime = (dateStr) => {
    return formatDisplayDateTime(dateStr);
  };

  // Get activity icon
  const getActivityIcon = (type) => {
    switch(type) {
      case "booking_paid": return "✅";
      case "payment": return "💳";
      case "message": return "💬";
      case "booking_pending": return "⏳";
      default: return "📌";
    }
  };

  // Get status badge style
  const getStatusStyle = (status) => {
    const styles = {
      paid: "bg-green-100 text-green-800",
      confirmed: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      failed: "bg-red-100 text-red-800",
      expired: "bg-gray-100 text-gray-600",
      sent: "bg-blue-100 text-blue-800",
      message: "bg-purple-100 text-purple-800",
      inquiry: "bg-purple-100 text-purple-800"
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl">Dashboard</h1>
          <PageInfoInline pageKey="dashboard" />
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
        </div>
      </div>
    );
  }

  const data = dashboardData || {};
  const todayData = data.today_and_upcoming || {};
  const activities = data.last_activities || [];
  const errorsCount = data.errors_count || 0;
  const errorsList = data.errors_list || [];
  const reports = data.reports || {};
  const systemStatus = data.system_status || {};
  const occupancy = data.occupancy || {};
  const revenue = data.revenue || {};
  const pendingAlerts = data.pending_alerts || [];
  const bookingSources = data.booking_sources || {};
  const bookingSourcesYear = data.booking_sources_year || new Date().getFullYear().toString();
  const annualKpis = data.annual_kpis || {};
  
  // Colori per le piattaforme (dinamico)
  const platformColors = {
    airbnb: "#b2b2b2",      // Grigio scuro per Airbnb
    form: "#f5f5f5",        // Grigio chiaro per Form
    booking_com: "#003580",
    vrbo: "#3b5998",
    sito_web: "#D4AF37",
    manuali: "#f5f5f5",
    expedia: "#FFC72C",
    tripadvisor: "#34E0A1",
    // Colore di default per piattaforme non elencate
    default: "#A8A29E"
  };
  
  // Icone/Emoji per le piattaforme
  const platformIcons = {
    airbnb: "🏠",
    booking_com: "📘",
    vrbo: "🏡",
    sito_web: "🌐",
    manuali: "✏️",
    expedia: "✈️",
    tripadvisor: "🦉",
    default: "📋"
  };
  
  // Nomi display per le piattaforme
  const platformNames = {
    ical: "iCal",
    airbnb: "Airbnb",
    form: "Form",
    booking_com: "Booking.com",
    vrbo: "Vrbo",
    sito_web: "Sito Web",
    sito: "Sito",
    manuali: "Manuali",
    expedia: "Expedia",
    tripadvisor: "TripAdvisor"
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Dashboard</h1>
        <PageInfoInline pageKey="dashboard" />
      </div>

      {/* 1. OGGI & PROSSIMI GIORNI */}
      <div className="admin-card border-l-4 border-l-[#D4AF37] bg-gradient-to-r from-[#FFFDF7] to-white">
        {/* Titolo centrato - senza icona */}
        <h2 className="dashboard-heading text-center mb-6">Prossimi Soggiorni</h2>
        
        {/* "Oggi: [data completa]" - centrato e bold, dimensione aumentata */}
        <p className="text-lg md:text-xl font-heading font-bold text-[#1C1917] capitalize text-center mb-8">
          Oggi: {formatCurrentDate()}
        </p>
        
        {/* Prossimi 4 Soggiorni - Grid responsive */}
        <div className="w-full overflow-hidden mb-8">
          {todayData.next_stays && todayData.next_stays.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 w-full">
              {todayData.next_stays.slice(0, 4).map((stay, idx) => (
                <div 
                  key={idx} 
                  className="bg-white border border-[#D4AF37] rounded-xl p-4 hover:border-[#D4AF37] hover:shadow-md transition-all flex flex-col h-[300px] min-w-0"
                >
                  {/* Nome cliente - centrato, BOLD, wrap su 2 righe se necessario */}
                  <p className="font-heading text-lg font-bold text-[#1C1917] text-center mb-3 line-clamp-2 leading-tight" title={stay.name}>
                    {stay.name}
                  </p>
                  
                  {/* Check-in e Check-out - centrati */}
                  <div className="flex flex-col items-center gap-2 mb-3">
                    {/* Check-in: RGB(233, 81, 145) - testo rosa */}
                    <span className="text-base font-semibold" style={{ color: 'rgb(233, 81, 145)' }}>
                      {formatDate(stay.check_in)}
                    </span>
                    {/* Check-out: solo testo viola (senza triangolo) */}
                    <span className="text-base font-semibold" style={{ color: 'rgb(124, 58, 237)' }}>
                      {formatDate(stay.check_out)}
                    </span>
                  </div>
                  
                  {/* Dettagli - centrati, font più piccolo */}
                  <div className="text-center text-sm text-[#57534E] space-y-0.5 flex-grow">
                    {/* Ospiti con età bambini inline */}
                    <p className="text-xs">
                      {stay.adults > 0 && <span>{stay.adults} adult{stay.adults > 1 ? 'i' : 'o'}</span>}
                      {stay.children > 0 && (
                        <span>
                          {stay.adults > 0 && ' · '}
                          {stay.children} bambin{stay.children > 1 ? 'i' : 'o'}
                          {stay.children_ages && ` (${stay.children_ages})`}
                        </span>
                      )}
                      {stay.infants > 0 && <span> · {stay.infants} neonat{stay.infants > 1 ? 'i' : 'o'}</span>}
                      {!stay.adults && !stay.children && !stay.infants && <span>-</span>}
                    </p>
                    {/* Notti */}
                    <p className="text-xs">{stay.nights || 0} nott{(stay.nights || 0) === 1 ? 'e' : 'i'}</p>
                  </div>
                  
                  {/* Importi - solo se > 0, altrimenti spazio vuoto per mantenere altezza */}
                  <div className="text-center mt-auto pt-3 border-t border-[#D4AF37] min-h-[50px] flex flex-col justify-center">
                    {stay.total_paid > 0 ? (
                      <>
                        <p className="text-lg font-bold text-[#78716C]">€{stay.total_paid.toFixed(2)}</p>
                        {stay.price_per_night > 0 && (
                          <p className="text-xs text-[#78716C]">€{stay.price_per_night.toFixed(2)} / notte</p>
                        )}
                      </>
                    ) : null}
                  </div>
                  
                  {/* Link Dettagli - centrato, oro, bold */}
                  <div className="pt-3 mt-2 border-t border-[#D4AF37] text-center">
                    <button
                      onClick={() => setBookingModal({ 
                        open: true, 
                        booking: {
                          ...stay,
                          status: stay.status || 'confirmed',
                          name: stay.name,
                          customer_name: stay.name,
                          grand_total: stay.total_paid,
                          base_total: stay.total_paid
                        }
                      })}
                      className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
                    >
                      Dettagli
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#78716C] italic bg-white p-6 rounded-xl border border-[#D4AF37] text-center">
              Nessun soggiorno programmato
            </p>
          )}
        </div>
        
        <Link to="/admin/dashboard/calendar" className="text-xs md:text-sm text-[#D4AF37] hover:underline flex items-center justify-center gap-1 mt-4 font-bold">
          Apri Calendario Booking <ExternalLink size={14} />
        </Link>
      </div>

      {/* CALENDARIO MENSILE - Identico al Booking Calendar (READ-ONLY) */}
      <div className="admin-card">
        {/* Header con navigazione mese - Mese centrato + bold, "Mese corrente" a sinistra */}
        <div className="flex flex-col gap-2 mb-4">
          {/* Riga superiore: Mese corrente (sx) + Calendario completo (dx) */}
          <div className="flex items-center justify-between">
            <button 
              onClick={goToToday}
              className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
            >
              Mese corrente
            </button>
            <Link to="/admin/dashboard/calendar" className="text-xs md:text-sm text-[#D4AF37] hover:underline flex items-center gap-1 font-bold">
              Calendario completo <ExternalLink size={14} />
            </Link>
          </div>
          
          {/* Riga navigazione: frecce + mese centrato bold */}
          <div className="flex items-center justify-center gap-3">
            <button 
              onClick={goToPrevMonth}
              className="p-1.5 rounded-lg hover:bg-[#F5F5F4] transition-colors"
              title="Mese precedente"
            >
              <ChevronLeft size={20} className="text-[#78716C]" />
            </button>
            <h2 className="dashboard-heading min-w-[160px] md:min-w-[200px] text-center">
              {monthNames[calendarMonth.month]} {calendarMonth.year}
            </h2>
            <button 
              onClick={goToNextMonth}
              className="p-1.5 rounded-lg hover:bg-[#F5F5F4] transition-colors"
              title="Mese successivo"
            >
              <ChevronRight size={20} className="text-[#78716C]" />
            </button>
          </div>
        </div>
        
        {/* Calendario Grid - FULL WIDTH su desktop, compatto su mobile */}
        <div className="w-full">
          {calendarLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
            </div>
          ) : (
            <>
              {/* Header giorni settimana - stile Booking Calendar */}
              <div className="grid grid-cols-7 border-b border-[#E7E5E4] bg-[#F9F8F6]">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d, i) => (
                  <div key={i} className="text-xs md:text-sm font-medium text-[#78716C] text-center py-2 md:py-3">
                    <span className="hidden md:inline">{d}</span>
                    <span className="md:hidden">{d.charAt(0)}</span>
                  </div>
                ))}
              </div>
              
              {/* Grid giorni - FULL WIDTH su desktop */}
              <div className="grid grid-cols-7 gap-1 p-1 md:p-2">
                {generateMonthDays.map((day, idx) => {
                  // Cella vuota per padding
                  if (!day) {
                    return <div key={`pad-${idx}`} className="h-12 md:h-20" />;
                  }
                  
                  const dateStr = day.date;
                  const booking = getBookingForDate(dateStr);
                  const isCheckOut = isCheckOutDay(dateStr);
                  const isCheckIn = isCheckInDay(dateStr);
                  const past = isPast(dateStr);
                  const today = isToday(dateStr);
                  const manualBlocked = isManuallyBlocked(dateStr);
                  const icalBlocked = isICalBlocked(dateStr);
                  const anyBlocked = isBlocked(dateStr);
                  const isIcal = booking && isICalBooking(booking);
                  
                  // Determina classe background - IDENTICO a PriceCalendarGrid
                  let cellClass = "h-12 md:h-20 rounded-lg flex flex-col items-center justify-center relative overflow-hidden transition-all ";
                  let textClass = "text-[#1C1917]";
                  let showBlockedPattern = false;
                  
                  if (past) {
                    // Date passate: grigie
                    cellClass += "bg-gray-100";
                    textClass = "text-gray-400";
                  } else if (booking) {
                    if (isIcal) {
                      // iCal booking = GRIGIO
                      cellClass += "bg-gray-400";
                      textClass = "text-white";
                    } else if (booking.status === "paid") {
                      // Pagato = VERDE
                      cellClass += "bg-green-600";
                      textClass = "text-white";
                    } else {
                      // Confermato = CIANO
                      cellClass += "bg-cyan-200 border-2 border-cyan-400";
                      textClass = "text-cyan-900";
                    }
                  } else if (icalBlocked) {
                    // Blocco iCal senza booking corrispondente = GRIGIO (come iCal booking)
                    cellClass += "bg-gray-400";
                    textClass = "text-white";
                  } else if (manualBlocked) {
                    // Bloccato manualmente = ROSSO con pattern
                    cellClass += "bg-red-50";
                    textClass = "text-red-400";
                    showBlockedPattern = true;
                  } else if (anyBlocked) {
                    // Altro blocco = ROSSO con pattern
                    cellClass += "bg-red-50";
                    textClass = "text-red-400";
                    showBlockedPattern = true;
                  } else {
                    // Disponibile = BIANCO
                    cellClass += "bg-white border border-[#E7E5E4]";
                  }
                  
                  // Ring dorato per OGGI
                  if (today) {
                    cellClass += " ring-2 ring-[#D4AF37] ring-offset-1";
                  }
                  
                  // Ottieni booking per check-in (mostra nome e info complete)
                  const checkInBooking = isCheckIn ? calendarBookings.find(b => b.check_in === dateStr) : null;
                  
                  // Ottieni info blocco iCal se non c'è booking
                  const icalBlockInfo = !booking && icalBlocked 
                    ? calendarBlocked.find(b => b.date === dateStr && (b.source === "ical" || b.source?.startsWith("ical_")))
                    : null;
                  
                  // Info aggiuntive per il giorno (come in Booking)
                  const displayName = booking ? (
                    isIcal 
                      ? (booking.platform_name || "Airbnb")
                      : (booking.name || booking.customer_name || "")
                  ) : (icalBlockInfo ? (icalBlockInfo.platform_name || "Airbnb") : "");
                  const displayAmount = booking?.grand_total || booking?.base_total || 0;
                  const hasNote = booking?.notes || booking?.special_requests;
                  
                  // Calcola notti per il booking
                  const bookingNights = booking && booking.check_in && booking.check_out
                    ? Math.ceil((new Date(booking.check_out) - new Date(booking.check_in)) / (1000 * 60 * 60 * 24))
                    : 0;
                  
                  // Determina se la cella è cliccabile (booking o blocco iCal)
                  const isClickable = (booking || icalBlockInfo) && !past;
                  
                  return (
                    <div 
                      key={dateStr} 
                      className={`${cellClass} ${isClickable ? 'cursor-pointer hover:opacity-90' : ''}`}
                      title={displayName || (anyBlocked ? "Bloccato" : "Disponibile")}
                      onClick={() => {
                        if (booking && !past) {
                          setBookingModal({ open: true, booking: booking });
                        } else if (icalBlockInfo && !past) {
                          // Mostra info blocco iCal
                          setBookingModal({ 
                            open: true, 
                            booking: {
                              status: "ical",
                              source: icalBlockInfo.source,
                              platform_name: icalBlockInfo.platform_name || "Airbnb",
                              check_in: icalBlockInfo.date,
                              check_out: null, // Non abbiamo checkout per singoli blocchi
                              notes: icalBlockInfo.reason || ""
                            }
                          });
                        }
                      }}
                    >
                      {/* Pattern diagonale per BLOCCHI - identico a PriceCalendarGrid */}
                      {showBlockedPattern && (
                        <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_4px,rgba(239,68,68,0.1)_4px,rgba(239,68,68,0.1)_8px)] rounded-lg" />
                      )}
                      
                      {/* Triangolo VIOLA per check-out - identico a PriceCalendarGrid (z-30 priorità massima) */}
                      {isCheckOut && (
                        <div 
                          className={`absolute top-0 left-0 w-0 h-0 border-t-[16px] md:border-t-[20px] border-t-violet-600 border-r-[16px] md:border-r-[20px] border-r-transparent z-30 ${past ? 'opacity-50' : ''}`}
                          title="Check-out"
                        />
                      )}
                      
                      {/* Numero giorno */}
                      <span className={`font-semibold text-sm md:text-lg relative z-10 ${textClass}`}>{day.day}</span>
                      
                      {/* Info prenotazione o blocco iCal (visibili su desktop) */}
                      {(booking || icalBlocked) && !past && (
                        <div className={`hidden md:flex flex-col items-center w-full px-0.5 relative z-10 ${textClass}`}>
                          {/* Nome abbreviato */}
                          <span className="text-[8px] leading-tight truncate w-full text-center">
                            {displayName.split(' ')[0]?.substring(0, 8) || "Airbnb"}
                          </span>
                          {/* Notti (solo per booking) */}
                          {booking && bookingNights > 0 && (
                            <span className="text-[7px] leading-tight opacity-70">
                              ({bookingNights})
                            </span>
                          )}
                          {/* Importo (se > 0) */}
                          {displayAmount > 0 && (
                            <span className="text-[7px] leading-tight opacity-80">
                              €{displayAmount.toFixed(0)}
                            </span>
                          )}
                          {/* Indicatore Note */}
                          {hasNote && (
                            <span className="text-[6px] opacity-70 mt-0.5">Note</span>
                          )}
                        </div>
                      )}
                      
                      {/* Nome abbreviato su mobile (solo nel giorno check-in o blocco iCal) */}
                      {(checkInBooking || icalBlocked) && !past && (
                        <span className={`md:hidden text-[7px] leading-none truncate w-full px-0.5 text-center relative z-10 ${textClass}`}>
                          {checkInBooking 
                            ? (isICalBooking(checkInBooking) 
                                ? "Air"
                                : (checkInBooking.name || checkInBooking.customer_name || "").substring(0, 4))
                            : "Air"
                          }
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        
        {/* Legenda - IDENTICA al calendario Booking */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mt-4 pt-3 border-t border-[#D4AF37] text-[10px] md:text-xs text-[#78716C]">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 md:w-4 md:h-4 rounded bg-green-600"></span> Pagato
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 md:w-4 md:h-4 rounded bg-cyan-200 border-2 border-cyan-400"></span> Confermato
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 md:w-4 md:h-4 rounded bg-gray-400"></span> iCal
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 md:w-4 md:h-4 rounded bg-red-50 border border-red-200 relative overflow-hidden">
              <span className="absolute inset-0 bg-[repeating-linear-gradient(45deg,transparent,transparent_2px,rgba(239,68,68,0.15)_2px,rgba(239,68,68,0.15)_4px)]"></span>
            </span> Bloccato
          </span>
          <span className="flex items-center gap-1">
            <span className="w-0 h-0 border-t-[10px] md:border-t-[12px] border-t-violet-600 border-r-[10px] md:border-r-[12px] border-r-transparent"></span> Check-out
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 md:w-4 md:h-4 rounded ring-2 ring-[#D4AF37] ring-offset-1 bg-white"></span> Oggi
          </span>
        </div>
      </div>
      
      {/* Stato Sincronizzazione - Dashboard con Health Check per Piattaforma */}
      {syncStatusDashboard && (
        <div className="mt-6 bg-white border border-[#D4AF37] p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1" />
            <h2 className="dashboard-heading">Stato Sincronizzazione</h2>
            <div className="flex-1 flex justify-end">
              <PageInfoIcon pageKey="stato_sync" />
            </div>
          </div>
          
          {/* Riepilogo generale - 3 colonne come in Sync */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
            {/* Ultima Sincronizzazione */}
            <div className="p-3 md:p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4]">
              <p className="text-[10px] md:text-xs text-[#78716C] uppercase tracking-wide mb-1">Ultima Sincronizzazione</p>
              <p className="text-base md:text-lg font-semibold text-[#1C1917]">
                {syncStatusDashboard.last_sync 
                  ? formatDisplayDateTime(syncStatusDashboard.last_sync)
                  : 'Mai eseguita'}
              </p>
            </div>

            {/* Nuove Prenotazioni 7 giorni */}
            <div className="p-3 md:p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4]">
              <p className="text-[10px] md:text-xs text-[#78716C] uppercase tracking-wide mb-1">Nuove Prenot. 7 GG</p>
              <p className="text-xl md:text-2xl font-bold text-[#1C1917]">
                {importedBookingsStatsDashboard?.seven_days?.count || 0}
              </p>
            </div>

            {/* Prenotazioni Totali */}
            <div className="p-3 md:p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4]">
              <p className="text-[10px] md:text-xs text-[#78716C] uppercase tracking-wide mb-1">Sync Prenotazioni Totali</p>
              <p className="text-xl md:text-2xl font-bold text-[#1C1917]">
                {importedBookingsStatsDashboard?.total?.count || 0}
              </p>
            </div>
          </div>

          {/* Dettaglio per Piattaforma */}
          {(syncStatusDashboard.platforms || []).length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-[#78716C] uppercase tracking-wide mb-2 text-center">Piattaforme Configurate</p>
              <div className="space-y-2">
                {(syncStatusDashboard.platforms || []).map((platform, idx) => {
                  // Calcola ore dall'ultima sync per warning visivo
                  let syncAgeHours = 0;
                  let syncWarningLevel = "ok"; // "ok", "warning" (>3h), "critical" (>5h)
                  
                  if (platform.last_sync) {
                    const lastSyncDate = new Date(platform.last_sync);
                    const now = new Date();
                    syncAgeHours = (now - lastSyncDate) / (1000 * 60 * 60);
                    
                    if (syncAgeHours > 5) {
                      syncWarningLevel = "critical";
                    } else if (syncAgeHours > 3) {
                      syncWarningLevel = "warning";
                    }
                  }
                  
                  // Determina colori e badge basati su status E warning temporale
                  let rowClass = "bg-[#FAFAF9] border-[#E7E5E4]";
                  let statusBadge = { text: platform.status, class: "text-yellow-700" };
                  
                  if (platform.status === "error") {
                    rowClass = "bg-red-50 border-red-200";
                    statusBadge = { text: "✗ Errore", class: "text-red-600 font-bold" };
                  } else if (platform.status === "never_synced") {
                    rowClass = "bg-gray-50 border-gray-200";
                    statusBadge = { text: "Mai sync", class: "text-gray-500" };
                  } else if (platform.status === "success" || platform.status === "success_with_changes") {
                    if (syncWarningLevel === "critical") {
                      rowClass = "bg-red-50 border-red-300";
                      statusBadge = { text: "⚠ Sync > 5h", class: "text-red-600 font-bold" };
                    } else if (syncWarningLevel === "warning") {
                      rowClass = "bg-orange-50 border-orange-300";
                      statusBadge = { text: "⚠ Sync > 3h", class: "text-orange-600 font-bold" };
                    } else {
                      rowClass = "bg-green-50 border-green-200";
                      statusBadge = { text: "✓ OK", class: "text-green-700 font-bold" };
                    }
                  }
                  
                  const bookingsCount = platform.total_bookings || 0;
                  const coverageDate = platform.coverage_end ? formatDisplayDate(platform.coverage_end) : null;
                  const lastSyncTime = platform.last_sync ? formatRelativeTime(platform.last_sync) : "Mai";
                  
                  // Estrai numero e unità dal tempo relativo per bold separato
                  const syncTimeParts = lastSyncTime.match(/^(\d+)\s*(.+)$/);
                  const syncTimeNumber = syncTimeParts ? syncTimeParts[1] : null;
                  const syncTimeUnit = syncTimeParts ? syncTimeParts[2] : lastSyncTime;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`px-6 py-5 rounded-lg border ${rowClass}`}
                    >
                      <div className="flex items-center text-[16px]">
                        {/* Nome piattaforma */}
                        <div className="flex-none w-[120px]">
                          <span className="font-bold text-[#1C1917]">{platform.platform_name}</span>
                        </div>
                        
                        {/* Separatore */}
                        <span className="text-[#C4C0BC] mx-4">|</span>
                        
                        {/* Stato */}
                        <div className="flex-none w-[100px] text-center">
                          <span className={`font-bold ${statusBadge.class}`}>{statusBadge.text}</span>
                        </div>
                        
                        {/* Separatore */}
                        <span className="text-[#C4C0BC] mx-4">|</span>
                        
                        {/* Prenotazioni */}
                        <div className="flex-none w-[140px] text-center">
                          <span className="text-[#44403C]">
                            <span className="font-bold">{bookingsCount}</span> prenotazioni
                          </span>
                        </div>
                        
                        {/* Separatore */}
                        <span className="text-[#C4C0BC] mx-4">|</span>
                        
                        {/* Copertura calendario */}
                        <div className="flex-1 text-center">
                          <span className="text-[#44403C]" title="Data massima coperta dalle prenotazioni importate">
                            Calendario sincronizzato fino al <span className="font-bold">{coverageDate || '—'}</span>
                          </span>
                        </div>
                        
                        {/* Separatore */}
                        <span className="text-[#C4C0BC] mx-4">|</span>
                        
                        {/* Ultima sync */}
                        <div className="flex-none w-[180px] text-right">
                          <span className={`${syncWarningLevel === "critical" ? "text-red-600" : syncWarningLevel === "warning" ? "text-orange-600" : "text-[#78716C]"}`}>
                            Ultima sync: {syncTimeNumber ? (
                              <><span className="font-bold">{syncTimeNumber}</span> {syncTimeUnit}</>
                            ) : (
                              <span className="font-bold">{lastSyncTime}</span>
                            )}
                          </span>
                        </div>
                        
                        {/* Messaggio errore (se presente) */}
                        {platform.status === "error" && platform.status_message && (
                          <>
                            <span className="text-[#C4C0BC] mx-4">|</span>
                            <span className="text-red-600 text-sm">{platform.status_message}</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-4 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4] text-center">
              <p className="text-[#78716C]">Nessuna piattaforma configurata.</p>
              <p className="text-sm text-[#A8A29E] mt-1">
                Puoi abilitare le piattaforme in{" "}
                <Link to="/admin/dashboard/calendar?tab=source" className="text-[#D4AF37] hover:underline font-medium">
                  Data Source
                </Link>
              </p>
            </div>
          )}
          
          {/* Link "Vai a Sync" in fondo */}
          <div className="mt-5 text-center">
            <Link 
              to="/admin/dashboard/calendar?tab=sync" 
              className="text-sm font-bold text-[#D4AF37] hover:text-[#B8972E] inline-flex items-center gap-1"
            >
              Vai a Sync <ExternalLink size={14} />
            </Link>
          </div>
        </div>
      )}
      
      {/* SEZIONE UPDATE - Preview ultime 10 prenotazioni rilevate */}
      <div className="admin-card border-l-4 border-l-[#D4AF37]">
        {/* Header con titolo centrato e icona info a destra */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex-1" />
          <h2 className="dashboard-heading">Ultime Prenotazioni da iCal</h2>
          <div className="flex-1 flex justify-end">
            <PageInfoIcon pageKey="update_preview" />
          </div>
        </div>
        
        {latestBookingsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
          </div>
        ) : latestBookings.length === 0 ? (
          <p className="text-sm text-[#78716C] italic text-center py-4">Nessuna prenotazione rilevata</p>
        ) : (
          <>
            {/* Lista prenotazioni - responsive (fino a 10) */}
            <div className="space-y-3">
              {latestBookings.slice(0, 10).map((booking, idx) => {
                const nights = booking.check_in && booking.check_out
                  ? Math.ceil((new Date(booking.check_out) - new Date(booking.check_in)) / (1000 * 60 * 60 * 24))
                  : 0;
                
                // Determina la fonte da mostrare
                const source = (booking.source || "").toLowerCase();
                const isIcal = source === "ical";
                const platform = booking.platform || "";
                const guestName = booking.name || booking.guest_name || "";
                
                // Costruisci "Prenotazione da: X + Nome"
                let sourceType = "";
                let sourceDetail = "";
                let sourceColor = "bg-gray-100 text-gray-800";
                
                if (isIcal) {
                  sourceType = "iCal";
                  sourceDetail = platform || "Piattaforma";
                  sourceColor = "bg-green-100 text-green-800";
                } else if (source === "booking_form" || source === "form") {
                  sourceType = "Form";
                  sourceDetail = guestName || "Ospite";
                  sourceColor = "bg-blue-100 text-blue-800";
                } else if (source === "payment_link") {
                  sourceType = "Payment by link";
                  sourceDetail = guestName || "Ospite";
                  sourceColor = "bg-purple-100 text-purple-800";
                } else if (source === "manual") {
                  sourceType = "Manuale";
                  sourceDetail = guestName || "Ospite";
                  sourceColor = "bg-gray-100 text-gray-800";
                } else {
                  sourceType = "Altro";
                  sourceDetail = guestName || "N/A";
                }
                
                // Calcola ospiti totali (solo per form/payment_link)
                const adults = booking.adults || 0;
                const children = booking.children || 0;
                const infants = booking.infants || 0;
                const totalGuests = adults + children + infants;
                const hasGuestInfo = !isIcal && totalGuests > 0;
                
                // Determina se il booking è non letto (is_unseen dal backend)
                const isUnseen = booking.is_unseen === true;
                
                // Classi per riga normale vs non letta
                const rowClasses = isUnseen
                  ? "p-3 rounded-lg border-2 border-[#D4AF37] transition-colors"
                  : "p-3 bg-[#FAFAF9] rounded-lg border border-[#E7E5E4] hover:border-[#D4AF37] transition-colors";
                
                // Stile inline per background oro chiaro (non letto)
                const rowStyle = isUnseen ? { backgroundColor: 'rgba(212, 175, 55, 0.15)' } : {};
                
                return (
                  <div 
                    key={booking.id || booking.request_id || idx}
                    className={rowClasses}
                    style={rowStyle}
                  >
                    {/* Riga 1: Fonte • Dettaglio + Timestamp sulla destra */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Badge fonte */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${sourceColor}`}>
                          {sourceType}
                        </span>
                        {/* Prenotazione da: Tipo • Dettaglio */}
                        <span className="text-sm font-medium text-[#1C1917]">
                          Prenotazione da: {sourceType} <span className="text-[#78716C]">•</span> {sourceDetail}
                        </span>
                        {/* Badge non letto */}
                        {isUnseen && (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: '#D4AF37' }}>
                            1
                          </span>
                        )}
                      </div>
                      
                      {/* Timestamp sulla destra */}
                      <span className="text-xs text-[#78716C] whitespace-nowrap">
                        {formatRelativeTime(booking.created_at || booking.imported_at)}
                      </span>
                    </div>
                    
                    {/* Riga 2: Ospite (se disponibile e non iCal) */}
                    {guestName && !isIcal && (
                      <div className="mb-2">
                        <span className="text-xs text-[#78716C]">Ospite: </span>
                        <span className="text-sm font-medium text-[#1C1917]">{guestName}</span>
                      </div>
                    )}
                    
                    {/* Riga 3: Date, Notti, Ospiti */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      {/* Check-in / Check-out */}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#78716C]">Check-in:</span>
                        <span className="font-medium" style={{ color: 'rgb(233, 81, 145)' }}>
                          {formatDisplayDate(booking.check_in)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#78716C]">Check-out:</span>
                        <span className="font-medium" style={{ color: 'rgb(124, 58, 237)' }}>
                          {formatDisplayDate(booking.check_out)}
                        </span>
                      </div>
                      
                      {/* Notti */}
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-[#78716C]">Notti:</span>
                        <span className="font-medium text-[#1C1917]">{nights}</span>
                      </div>
                      
                      {/* Ospiti (solo se disponibili) */}
                      {hasGuestInfo && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-[#78716C]">Ospiti:</span>
                          <span className="font-medium text-[#1C1917]">
                            {totalGuests} ({adults} ad{adults !== 1 ? '.' : '.'}{children > 0 ? `, ${children} bam.` : ''}{infants > 0 ? `, ${infants} neo.` : ''})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Link "Vai a Update" */}
            <div className="mt-5 text-center">
              <Link 
                to="/admin/dashboard/update" 
                className="text-sm font-bold text-[#D4AF37] hover:text-[#B8972E] inline-flex items-center gap-1"
              >
                Vai a Update <ExternalLink size={14} />
              </Link>
            </div>
          </>
        )}
      </div>
      
      {/* Modale Note (READ-ONLY) */}
      {noteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setNoteModal({ open: false, note: "", title: "" })}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">{noteModal.title}</h3>
              <button onClick={() => setNoteModal({ open: false, note: "", title: "" })} className="text-[#78716C] hover:text-[#1C1917]">
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-[#57534E] whitespace-pre-wrap">{noteModal.note || "Nessuna nota disponibile"}</p>
          </div>
        </div>
      )}
      
      {/* Modale Dettagli Prenotazione Completi (READ-ONLY) */}
      {bookingModal.open && bookingModal.booking && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBookingModal({ open: false, booking: null })}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl">Dettagli Prenotazione</h3>
              <button onClick={() => setBookingModal({ open: false, booking: null })} className="text-[#78716C] hover:text-[#1C1917]">
                <X size={20} />
              </button>
            </div>
            
            {(() => {
              const b = bookingModal.booking;
              const isIcal = b.status === "ical" || b.source?.startsWith("ical_") || b.source === "ical";
              const nights = b.check_in && b.check_out 
                ? Math.ceil((new Date(b.check_out) - new Date(b.check_in)) / (1000 * 60 * 60 * 24))
                : 0;
              
              return (
                <div className="space-y-4">
                  {/* Status Badge */}
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      b.status === 'paid' ? 'bg-green-100 text-green-800' :
                      b.status === 'confirmed' ? 'bg-cyan-100 text-cyan-800' :
                      isIcal ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {b.status === 'paid' ? 'Pagato' :
                       b.status === 'confirmed' ? 'Confermato' :
                       isIcal ? 'iCal Import' :
                       b.status || 'Pending'}
                    </span>
                    {b.booking_number && (
                      <span className="text-sm text-[#78716C]">#{b.booking_number}</span>
                    )}
                  </div>
                  
                  {/* Nome Ospite / Piattaforma */}
                  <div>
                    <p className="text-xs text-[#78716C] uppercase tracking-wide">
                      {isIcal ? 'Piattaforma' : 'Nome Ospite'}
                    </p>
                    <p className="font-heading text-lg font-semibold">
                      {isIcal ? (b.platform_name || 'Airbnb') : (b.name || b.customer_name || 'N/A')}
                    </p>
                  </div>
                  
                  {/* Ospiti */}
                  {!isIcal && (b.adults || b.children || b.infants) && (
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Numero Ospiti</p>
                      <p className="font-medium">
                        {b.adults > 0 && <span>{b.adults} Adult{b.adults > 1 ? 'i' : 'o'}</span>}
                        {b.children > 0 && <span className="ml-2">{b.children} Bambin{b.children > 1 ? 'i' : 'o'}</span>}
                        {b.infants > 0 && <span className="ml-2">{b.infants} Neonat{b.infants > 1 ? 'i' : 'o'}</span>}
                      </p>
                    </div>
                  )}
                  
                  {/* Date */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Check-in</p>
                      <p className="font-medium" style={{ color: 'rgb(233, 81, 145)' }}>
                        {b.check_in ? formatDisplayDate(b.check_in) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Check-out</p>
                      <p className="font-medium" style={{ color: 'rgb(124, 58, 237)' }}>
                        {b.check_out ? formatDisplayDate(b.check_out) : 'N/A'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Notti */}
                  {nights > 0 && (
                    <div className="bg-[#F9F8F6] p-3 rounded-lg flex items-center justify-center gap-2">
                      <Calendar size={18} className="text-[#D4AF37]" />
                      <span className="font-medium">{nights} nott{nights === 1 ? 'e' : 'i'}</span>
                    </div>
                  )}
                  
                  {/* Contatti */}
                  {!isIcal && (b.email || b.phone) && (
                    <div>
                      <p className="text-xs text-[#78716C] uppercase tracking-wide">Contatti</p>
                      {b.email && <p className="text-sm">{b.email}</p>}
                      {b.phone && <p className="text-sm">{b.phone}</p>}
                    </div>
                  )}
                  
                  {/* Importi */}
                  {!isIcal && (b.grand_total > 0 || b.base_total > 0) && (
                    <div className="border-t border-[#D4AF37] pt-4">
                      <p className="text-xs text-[#78716C] uppercase tracking-wide mb-2">Importo</p>
                      <p className="font-heading text-2xl text-[#D4AF37] font-bold">
                        €{(b.grand_total || b.base_total || 0).toFixed(2)}
                      </p>
                      {b.price_per_night > 0 && (
                        <p className="text-xs text-[#78716C]">€{b.price_per_night.toFixed(2)} / notte</p>
                      )}
                    </div>
                  )}
                  
                  {/* Note */}
                  {(b.notes || b.special_requests) && (
                    <div className="bg-[#FEF3C7] border border-[#F59E0B] p-3 rounded-lg">
                      <p className="text-xs text-[#92400E] uppercase tracking-wide mb-1 flex items-center gap-1">
                        <FileText size={14} /> Note
                      </p>
                      <p className="text-sm text-[#92400E] whitespace-pre-wrap">
                        {b.notes || b.special_requests}
                      </p>
                    </div>
                  )}
                  
                  {/* Source */}
                  {b.source && (
                    <div className="text-xs text-[#A8A29E] pt-2 border-t border-[#D4AF37]">
                      Fonte: {b.source}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ULTIME ATTIVITÀ - Full width, 10 eventi con link alla sezione esatta */}
      <div className="admin-card border-l-4 border-l-[#D4AF37]">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1" />
          <h2 className="dashboard-heading">Ultime Attività</h2>
          <div className="flex-1 flex justify-end">
            <PageInfoIcon pageKey="ultime_attivita" />
          </div>
        </div>
        {activities.length === 0 ? (
          <p className="text-sm text-[#78716C] italic text-center py-4">Nessuna attività recente</p>
        ) : (
          <div className="space-y-2">
            {activities.slice(0, 10).map((activity, idx) => (
              <div key={idx} className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${activity.is_positive !== false ? "bg-[#FAFAF9] border-[#E7E5E4]" : "bg-amber-50 border-amber-200"}`}>
                {/* Icona minimal */}
                <span className={`flex-shrink-0 ${activity.is_positive !== false ? "text-green-600" : "text-amber-600"}`}>
                  {activity.is_positive !== false ? <Check size={16} strokeWidth={2.5} /> : <AlertTriangle size={16} strokeWidth={2.5} />}
                </span>
                
                {/* Contenuto principale */}
                <div className="flex-1 min-w-0">
                  {/* Riga 1: Titolo evento + Riferimento */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium text-[#1C1917]">{activity.title || activity.label}</span>
                    {activity.reference && (
                      <span className="text-sm text-[#57534E]">— {activity.reference}</span>
                    )}
                    {activity.detail && (
                      <span className="text-xs text-[#78716C]">({activity.detail})</span>
                    )}
                  </div>
                  
                  {/* Riga 2: Sezione admin con link */}
                  {activity.admin_section && (
                    <Link 
                      to={activity.admin_link || "#"} 
                      className="text-xs font-semibold text-[#D4AF37] hover:text-[#B8972E] hover:underline mt-0.5 inline-flex items-center gap-1"
                    >
                      Vai a: {activity.admin_section}
                    </Link>
                  )}
                </div>
                
                {/* Timestamp */}
                <span className="text-xs text-[#78716C] whitespace-nowrap">
                  {formatRelativeTime(activity.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ERRORI SISTEMA - Full width, lista dettagliata con link */}
      <div className="admin-card border-l-4 border-l-[#D4AF37]">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1" />
          <h2 className="dashboard-heading">Errori Sistema</h2>
          <div className="flex-1 flex justify-end">
            <PageInfoIcon pageKey="errori_sistema" />
          </div>
        </div>
        
        {errorsList.length === 0 ? (
          <p className="text-sm text-[#78716C] italic text-center py-4">Nessun errore rilevato</p>
        ) : (
          <div className="space-y-2">
            {errorsList.slice(0, 10).map((error, idx) => (
              <div key={idx} className="flex items-start gap-3 py-2.5 px-3 bg-red-50 rounded-lg border border-red-200">
                {/* Icona X rossa */}
                <span className="flex-shrink-0 text-red-600 mt-0.5">
                  <X size={16} strokeWidth={2.5} />
                </span>
                
                {/* Contenuto errore */}
                <div className="flex-1 min-w-0">
                  {/* Riga 1: Titolo errore */}
                  <p className="text-sm font-medium text-red-800">{error.title || error.description}</p>
                  
                  {/* Riga 2: Descrizione + Dettaglio */}
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-red-600 mt-0.5">
                    {error.description && error.title && <span>{error.description}</span>}
                    {error.detail && <span className="text-red-500">• {error.detail}</span>}
                  </div>
                  
                  {/* Riga 3: Sezione admin con link */}
                  {error.admin_section && (
                    <Link 
                      to={error.admin_link || "#"} 
                      className="text-xs font-semibold text-red-700 hover:text-red-900 hover:underline mt-1 inline-flex items-center gap-1"
                    >
                      Vai a: {error.admin_section}
                    </Link>
                  )}
                </div>
                
                {/* Timestamp */}
                <span className="text-xs text-red-600 whitespace-nowrap">
                  {formatRelativeTime(error.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 4. REPORT - 4 colonne */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {/* Transactions */}
        <div className="admin-card min-h-[200px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="dashboard-heading text-center">Transactions</h2>
            <Link to="/admin/dashboard/payments" className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold">Vedi tutto</Link>
          </div>
          <div className="space-y-2 flex-1">
            {(reports.transactions || []).length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[120px]">
                <p className="text-sm text-[#78716C] italic">Nessuna Transaction</p>
              </div>
            ) : (
              (reports.transactions || []).map((t, idx) => (
                <div key={idx} className="text-xs py-1.5 border-b border-[#D4AF37] last:border-0">
                  <div className="flex justify-between">
                    <span className="truncate max-w-[100px]">{t.customer_name || t.email || "N/A"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${getStatusStyle(t.payment_status)}`}>
                      {t.payment_status}
                    </span>
                  </div>
                  <div className="text-[#78716C]">€{(t.amount || 0).toFixed(2)}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Payment Link */}
        <div className="admin-card min-h-[200px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="dashboard-heading text-center">Payment Link</h2>
            <Link to="/admin/dashboard/payment-link?tab=list" className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold">Vedi tutto</Link>
          </div>
          <div className="space-y-2 flex-1">
            {(reports.payment_links || []).length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[120px]">
                <p className="text-sm text-[#78716C] italic">Nessun Payment Link</p>
              </div>
            ) : (
              (reports.payment_links || []).map((pl, idx) => (
                <div key={idx} className="text-xs py-1.5 border-b border-[#D4AF37] last:border-0">
                  <div className="flex justify-between">
                    <span className="truncate max-w-[100px] font-medium">{pl.customer_name || "N/A"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      pl.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                      pl.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {pl.payment_status === 'paid' ? 'Pagato' : 'In attesa'}
                    </span>
                  </div>
                  <div className="flex justify-between text-[#78716C]">
                    <span>€{pl.amount}</span>
                    <span>{formatRelativeTime(pl.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Tracking */}
        <div className="admin-card min-h-[200px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="dashboard-heading text-center">Tracking</h2>
            <Link to="/admin/dashboard/payments?tab=tracking" className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold">Vedi tutto</Link>
          </div>
          <div className="space-y-2 flex-1">
            {(reports.tracking || []).length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[120px]">
                <p className="text-sm text-[#78716C] italic">Nessun Tracking</p>
              </div>
            ) : (
              (reports.tracking || []).map((t, idx) => (
                <div key={idx} className="text-xs py-1.5 border-b border-[#D4AF37] last:border-0">
                  <div className="flex justify-between">
                    <span className="truncate max-w-[100px]">{t.name || "N/A"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${getStatusStyle(t.status)}`}>
                      {t.status}
                    </span>
                  </div>
                  <div className="text-[#78716C]">{formatRelativeTime(t.created_at)}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Logs */}
        <div className="admin-card min-h-[200px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <h2 className="dashboard-heading text-center">Logs</h2>
            <Link to="/admin/dashboard/payments?tab=logs" className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold">Vedi tutto</Link>
          </div>
          <div className="space-y-2 flex-1">
            {(reports.logs || []).length === 0 ? (
              <div className="flex items-center justify-center h-full min-h-[120px]">
                <p className="text-sm text-[#78716C] italic">Nessun Log</p>
              </div>
            ) : (
              (reports.logs || []).map((l, idx) => (
                <div key={idx} className="text-xs py-1.5 border-b border-[#D4AF37] last:border-0">
                  <div className="flex justify-between">
                    <span className="truncate max-w-[100px]">{l.from_name || l.recipient || "N/A"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${getStatusStyle(l.status)}`}>
                      {l.status}
                    </span>
                  </div>
                  <div className="text-[#78716C]">{formatRelativeTime(l.timestamp)}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* KPI BUSINESS - 4 riquadri: Occupazione, Ricavo, Guadagno, Prenotazioni */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* 1. Occupazione */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="dashboard-heading">Occupazione</h2>
            <Link 
              to="/admin/dashboard/reports?tab=occupazione" 
              className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
            >
              Vedi tutto
            </Link>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-[#78716C] mb-2">
              Anno {annualKpis.year || new Date().getFullYear()}
            </p>
            <p className="font-heading text-3xl text-[#1C1917]">{annualKpis.occupancy_rate || 0}%</p>
            <p className="text-xs text-[#78716C] mt-1">
              {annualKpis.occupied_nights || 0} / {annualKpis.total_nights || 365} notti
            </p>
          </div>
        </div>

        {/* 2. Ricavo */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="dashboard-heading">Ricavo</h2>
            <Link 
              to="/admin/dashboard/reports?tab=importi-incassati" 
              className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
            >
              Vedi tutto
            </Link>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-[#78716C] mb-2">
              Anno {annualKpis.year || new Date().getFullYear()}
            </p>
            <p className="font-heading text-3xl text-[#1C1917]">€{(annualKpis.ricavo_lordo || 0).toLocaleString("it-IT", {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
          </div>
        </div>

        {/* 3. Guadagno */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="dashboard-heading">Guadagno</h2>
            <Link 
              to="/admin/dashboard/reports?tab=guadagni" 
              className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
            >
              Vedi tutto
            </Link>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-[#78716C] mb-2">
              Anno {annualKpis.year || new Date().getFullYear()}
            </p>
            <p className="font-heading text-3xl text-[#1C1917]">€{(annualKpis.guadagno_netto || 0).toLocaleString("it-IT", {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
          </div>
        </div>

        {/* 4. Prenotazioni */}
        <div className="admin-card">
          <div className="flex items-center justify-between mb-2">
            <h2 className="dashboard-heading">Prenotazioni</h2>
            <Link 
              to="/admin/dashboard/calendar" 
              className="text-xs md:text-sm text-[#D4AF37] hover:underline font-bold"
            >
              Vedi tutto
            </Link>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-[#78716C] mb-2">
              Anno {bookingSourcesYear}
            </p>
            
            {Object.keys(bookingSources).length === 0 ? (
              <p className="text-sm text-[#A8A29E]">Nessuna prenotazione</p>
            ) : (
              <>
                <div className="flex flex-col items-center space-y-1">
                  {Object.entries(bookingSources).map(([key, count]) => (
                    <div key={key} className="flex items-baseline gap-3">
                      <span className="font-heading text-xl text-[#1C1917] min-w-[50px] text-right">
                        {platformNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <span className="font-heading text-3xl text-[#1C1917] min-w-[40px] text-left">{count}</span>
                    </div>
                  ))}
                </div>
                
                {/* Barra progresso dinamica */}
                <div className="mt-3 h-2 bg-[#F5F5F4] rounded-full overflow-hidden flex">
                  {(() => {
                    const total = Object.values(bookingSources).reduce((sum, v) => sum + v, 0);
                    if (total === 0) return <div className="w-full bg-[#E7E5E4]"></div>;
                    return Object.entries(bookingSources).map(([key, count]) => {
                      const pct = (count / total) * 100;
                      const color = platformColors[key] || platformColors.default;
                      return (
                        <div 
                          key={key} 
                          style={{width: `${pct}%`, backgroundColor: color}} 
                          title={`${platformNames[key] || key}: ${count} (${pct.toFixed(1)}%)`}
                        />
                      );
                    });
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 5. QUICK ACTIONS */}
      <div className="admin-card">
        <h2 className="dashboard-heading text-center mb-4">Accesso Rapido</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3">
          <Link to="/admin/dashboard/update" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Bell size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Update</span>
          </Link>
          <Link to="/admin/dashboard/calendar" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Calendar size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Booking</span>
          </Link>
          <Link to="/admin/dashboard/payments" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Activity size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Monitoring</span>
          </Link>
          <Link to="/admin/dashboard/preventivo" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <FileText size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Proposal</span>
          </Link>
          <Link to="/admin/dashboard/payment-links" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Link2 size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Payment Link</span>
          </Link>
          <Link to="/admin/dashboard/reports" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <BarChart2 size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Report</span>
          </Link>
          <Link to="/admin/dashboard/emails" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <MessageSquare size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Email Form</span>
          </Link>
          <Link to="/admin/dashboard/messages" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Mail size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Messages</span>
          </Link>
          <Link to="/admin/dashboard/sections" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Layers size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Sections</span>
          </Link>
          <Link to="/admin/dashboard/typography" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Type size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Typography</span>
          </Link>
          <Link to="/admin/dashboard/legal" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <FileText size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Legal</span>
          </Link>
          <Link to="/admin/dashboard/account" className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#FFFDF7] transition-colors">
            <Settings size={24} className="text-[#78716C]" />
            <span className="text-sm font-medium">Account</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon }) {
  return (
    <div className="admin-card">
      <div className="flex items-center justify-between mb-4"><span className="text-[#78716C]">{icon}</span></div>
      <p className="text-3xl font-heading">{value}</p>
      <p className="text-sm text-[#78716C] mt-1">{title}</p>
    </div>
  );
}

// Sortable Section Item Component for Drag & Drop
function SortableSectionItem({ section, isSelected, onSelect, onDuplicate, onMoveUp, onMoveDown }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.section_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto"
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`admin-card p-3 cursor-pointer flex items-center gap-2 transition-all ${isSelected ? "border-2 border-[#D4AF37] bg-[#F5E6B3]" : "hover:border-[#D4AF37]"}`}
      onClick={() => onSelect(section)}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-[#E7E5E4] rounded">
        <GripVertical size={16} className="text-[#78716C]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${isSelected ? "text-[#92710F]" : "text-[#1C1917]"}`}>{section.section_name}</p>
        <p className={`text-xs ${isSelected ? "text-[#A88B2B]" : "text-[#A8A29E]"}`}>{section.blocks?.length || 0} blocks • {section.section_type}</p>
      </div>
      <div className="flex flex-col gap-0.5">
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp && onMoveUp(section.section_id); }}
          className="p-1 hover:bg-[#E7E5E4] rounded text-[#78716C] hover:text-[#1C1917]"
          title="Move up"
        >
          <ArrowUp size={12} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown && onMoveDown(section.section_id); }}
          className="p-1 hover:bg-[#E7E5E4] rounded text-[#78716C] hover:text-[#1C1917]"
          title="Move down"
        >
          <ArrowDown size={12} />
        </button>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDuplicate(section.section_id); }}
        className="p-1.5 hover:bg-[#FEF3C7] rounded text-[#78716C] hover:text-[#D4AF37]"
        title="Duplicate section"
      >
        <Copy size={14} />
      </button>
    </div>
  );
}

// Sortable Gallery Image Component - for drag & drop reordering
function SortableGalleryImage({ image, index, onDelete, onEdit, selectedLang }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: image.image_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto"
  };

  const imageUrl = image.url?.startsWith("/api") 
    ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` 
    : image.url;

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <div className="relative">
        <img 
          src={imageUrl} 
          alt={image.alt?.[selectedLang] || ""} 
          className="w-full h-24 object-cover rounded-lg border border-[#D4AF37]"
        />
        {/* Drag handle */}
        <div 
          {...attributes} 
          {...listeners} 
          className="absolute top-1 left-1 bg-black/50 text-white p-1 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <GripVertical size={12} />
        </div>
        {/* Action buttons */}
        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button 
            onClick={() => onEdit(image)}
            className="bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600"
            title="Edit caption/alt"
          >
            <Pencil size={10} />
          </button>
          <button 
            onClick={() => onDelete(image.image_id)}
            className="bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
            title="Remove image"
          >
            <X size={10} />
          </button>
        </div>
        {/* Caption preview */}
        {(image.caption?.[selectedLang] || image.alt?.[selectedLang]) && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] p-1 truncate rounded-b-lg">
            {image.caption?.[selectedLang] || image.alt?.[selectedLang]}
          </div>
        )}
      </div>
      <p className="text-[10px] text-[#78716C] text-center mt-1">#{index + 1}</p>
    </div>
  );
}

// Sortable Block Item - for drag & drop reordering of blocks within a section
function SortableBlockItem({ block, children, onMoveUp, onMoveDown, isFirst, isLast }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: block.block_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto"
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group mb-3">
      {/* Drag Handle & Reorder Controls - Always visible on left side */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-60 hover:opacity-100 transition-opacity">
        <button 
          {...attributes} 
          {...listeners}
          className="p-1.5 bg-[#E7E5E4] rounded cursor-grab active:cursor-grabbing hover:bg-[#D4AF37] hover:text-white transition-colors"
          title="Drag to reorder"
        >
          <GripVertical size={16} />
        </button>
        <button 
          onClick={onMoveUp}
          disabled={isFirst}
          className={`p-1.5 bg-[#E7E5E4] rounded hover:bg-[#D4AF37] hover:text-white transition-colors ${isFirst ? 'opacity-30 cursor-not-allowed' : ''}`}
          title="Move up"
        >
          <ChevronUp size={16} />
        </button>
        <button 
          onClick={onMoveDown}
          disabled={isLast}
          className={`p-1.5 bg-[#E7E5E4] rounded hover:bg-[#D4AF37] hover:text-white transition-colors ${isLast ? 'opacity-30 cursor-not-allowed' : ''}`}
          title="Move down"
        >
          <ChevronDown size={16} />
        </button>
      </div>
      <div className="ml-12">{children}</div>
    </div>
  );
}

// Section Management - IMPROVED
function SectionManagement() {
  const { getAuthHeaders } = useAuth();
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedLang, setSelectedLang] = useState("en");
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [newSection, setNewSection] = useState({ section_name: "", section_type: "custom", background_color: "#F9F8F6", padding: "py-24", show_in_nav: true, show_in_home: true, menu_label: {} });
  
  // Logo state
  const [logoConfig, setLogoConfig] = useState({ logo_url: null, logo_size: 40, show_in_footer: true });
  const [showLogoSection, setShowLogoSection] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const [newBlock, setNewBlock] = useState({ 
    block_type: "paragraph", 
    content: {}, 
    text_align: "left",
    text_color: null,
    font_size: null,  // Block-level override
    font_weight: null, // Block-level override
    image_url: "", 
    image_layout: "full", 
    image_sizing: "cover",
    image_alt: {},
    button_text: {}, 
    button_link: "", 
    button_style: "primary",
    // Gallery block
    gallery_title: {},
    gallery_description: {},
    gallery_layout: "grid",
    gallery_images: []
  });
  const [translating, setTranslating] = useState(false);
  const [showEditBlock, setShowEditBlock] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  
  // Gallery image editing state
  const [editingGalleryImage, setEditingGalleryImage] = useState(null);
  const [galleryImageBlockId, setGalleryImageBlockId] = useState(null);

  useEffect(() => { fetchSections(); fetchLogoConfig(); }, []);

  const fetchSections = async () => {
    try {
      const res = await axios.get(`${API}/sections`);
      setSections(res.data);
      if (res.data.length > 0 && !selectedSection) setSelectedSection(res.data[0]);
      else if (selectedSection) {
        const updated = res.data.find(s => s.section_id === selectedSection.section_id);
        if (updated) setSelectedSection(updated);
      }
    } catch (error) { toast.error("Failed to fetch sections"); }
  };

  const fetchLogoConfig = async () => {
    try {
      const res = await axios.get(`${API}/logo-config`);
      setLogoConfig(res.data);
    } catch (error) { console.error("Failed to fetch logo config"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ["image/jpeg", "image/png", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo file JPG, PNG o SVG sono consentiti");
      return;
    }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/logo-upload`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      setLogoConfig({ ...logoConfig, logo_url: res.data.logo_url });
      toast.success("Logo caricato!");
    } catch (error) { toast.error("Errore nel caricamento del logo"); }
    finally { setUploadingLogo(false); }
  };

  const saveLogoConfig = async () => {
    setSavingLogo(true);
    try {
      await axios.put(`${API}/admin/logo-config`, logoConfig, getAuthHeaders());
      toast.success("Configurazione logo salvata!");
    } catch (error) { toast.error("Errore nel salvataggio"); }
    finally { setSavingLogo(false); }
  };

  const createSection = async () => {
    if (!newSection.section_name.trim()) {
      toast.error("Section name is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/sections`, { ...newSection, order: sections.length, is_active: true, blocks: [] }, getAuthHeaders());
      toast.success("Section created!");
      setShowAddSection(false);
      setNewSection({ section_name: "", section_type: "custom", background_color: "#F9F8F6", padding: "py-24", show_in_nav: true });
      fetchSections();
    } catch (error) { toast.error("Failed to create section"); }
  };

  const updateSection = async (sectionId, update) => {
    try {
      await axios.put(`${API}/admin/sections/${sectionId}`, update, getAuthHeaders());
      fetchSections();
    } catch (error) { toast.error("Failed to update section"); }
  };

  const deleteSection = async (sectionId) => {
    const confirmed = await showConfirm({
      title: "Elimina Sezione",
      message: "Stai per eliminare questa sezione.",
      variant: "danger",
      confirmText: "Elimina",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/sections/${sectionId}`, getAuthHeaders());
      toast.success("Sezione eliminata");
      setSelectedSection(null);
      fetchSections();
    } catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  const addBlock = async () => {
    if (!selectedSection) return;
    
    // Validate based on block type
    if (newBlock.block_type === "image" && !newBlock.image_url) {
      toast.error("Image URL is required");
      return;
    }
    
    try {
      // Prepare block data with auto-translation
      let blockToAdd = { ...newBlock };
      
      // Auto-translate text content
      if (["h1", "h2", "h3", "paragraph"].includes(newBlock.block_type)) {
        const sourceText = newBlock.content?.[selectedLang];
        if (sourceText && sourceText.trim()) {
          toast.info("Translating to all languages...");
          const translations = await autoTranslateContent(sourceText, selectedLang);
          if (translations) {
            blockToAdd.content = translations;
          }
        }
      }
      
      // Auto-translate button text
      if (newBlock.block_type === "button") {
        const sourceText = newBlock.button_text?.[selectedLang];
        if (sourceText && sourceText.trim()) {
          toast.info("Translating button text...");
          const translations = await autoTranslateContent(sourceText, selectedLang);
          if (translations) {
            blockToAdd.button_text = translations;
          }
        }
      }
      
      // Auto-translate gallery title and description
      if (newBlock.block_type === "gallery") {
        if (newBlock.gallery_title?.[selectedLang]) {
          const titleTranslations = await autoTranslateContent(newBlock.gallery_title[selectedLang], selectedLang);
          if (titleTranslations) blockToAdd.gallery_title = titleTranslations;
        }
        if (newBlock.gallery_description?.[selectedLang]) {
          const descTranslations = await autoTranslateContent(newBlock.gallery_description[selectedLang], selectedLang);
          if (descTranslations) blockToAdd.gallery_description = descTranslations;
        }
      }
      
      // Auto-translate image alt text
      if (newBlock.block_type === "image" && newBlock.image_alt?.[selectedLang]) {
        const altTranslations = await autoTranslateContent(newBlock.image_alt[selectedLang], selectedLang);
        if (altTranslations) blockToAdd.image_alt = altTranslations;
      }
      
      await axios.post(`${API}/admin/sections/${selectedSection.section_id}/blocks`, blockToAdd, getAuthHeaders());
      toast.success("Block added with translations!");
      setShowAddBlock(false);
      setNewBlock({ 
        block_type: "paragraph", 
        content: {}, 
        text_align: "left",
        image_url: "", 
        image_layout: "full", 
        image_sizing: "cover",
        image_alt: {},
        button_text: {}, 
        button_link: "", 
        button_style: "primary" 
      });
      fetchSections();
    } catch (error) { toast.error("Failed to add block"); }
  };

  const updateBlockInSection = async (blockId, blockData) => {
    if (!selectedSection) return;
    const newBlocks = selectedSection.blocks.map(b => b.block_id === blockId ? { ...b, ...blockData } : b);
    await updateSection(selectedSection.section_id, { blocks: newBlocks });
  };

  // Save block with auto-translation
  const saveBlockWithTranslation = async (block) => {
    if (!selectedSection || !block) return;
    
    let blockToSave = { ...block };
    const originalBlock = selectedSection.blocks.find(b => b.block_id === block.block_id);
    
    // Check if text content changed and needs translation
    if (["h1", "h2", "h3", "paragraph"].includes(block.block_type)) {
      const currentText = block.content?.[selectedLang];
      const originalText = originalBlock?.content?.[selectedLang];
      
      if (currentText && currentText !== originalText && currentText.trim()) {
        toast.info("Auto-translating...");
        const translations = await autoTranslateContent(currentText, selectedLang);
        if (translations) {
          blockToSave.content = translations;
        }
      }
    }
    
    // Check if button text changed
    if (block.block_type === "button") {
      const currentText = block.button_text?.[selectedLang];
      const originalText = originalBlock?.button_text?.[selectedLang];
      
      if (currentText && currentText !== originalText && currentText.trim()) {
        toast.info("Auto-translating button...");
        const translations = await autoTranslateContent(currentText, selectedLang);
        if (translations) {
          blockToSave.button_text = translations;
        }
      }
    }
    
    // Check if image alt text changed
    if (block.block_type === "image") {
      const currentAlt = block.image_alt?.[selectedLang];
      const originalAlt = originalBlock?.image_alt?.[selectedLang];
      
      if (currentAlt && currentAlt !== originalAlt && currentAlt.trim()) {
        const translations = await autoTranslateContent(currentAlt, selectedLang);
        if (translations) {
          blockToSave.image_alt = translations;
        }
      }
    }
    
    await updateBlockInSection(block.block_id, blockToSave);
    toast.success("Saved with translations!");
  };

  const deleteBlock = async (blockId) => {
    if (!selectedSection) return;
    const confirmed = await showConfirm({
      title: "Elimina Blocco",
      message: "Stai per eliminare questo blocco.",
      variant: "danger",
      confirmText: "Elimina",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/sections/${selectedSection.section_id}/blocks/${blockId}`, getAuthHeaders());
      toast.success("Blocco eliminato");
      fetchSections();
    } catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  // Reorder blocks within a section
  const reorderBlocks = async (blockIds) => {
    if (!selectedSection) return;
    try {
      await axios.put(
        `${API}/admin/sections/${selectedSection.section_id}/blocks/reorder`, 
        blockIds, 
        getAuthHeaders()
      );
      fetchSections();
    } catch (error) { 
      console.error("Reorder failed:", error);
      toast.error("Failed to reorder blocks"); 
    }
  };

  // Move block up/down
  const moveBlock = async (blockIndex, direction) => {
    if (!selectedSection?.blocks) return;
    const blocks = [...selectedSection.blocks];
    const newIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    
    // Swap blocks
    [blocks[blockIndex], blocks[newIndex]] = [blocks[newIndex], blocks[blockIndex]];
    
    // Update order and save
    const blockIds = blocks.map(b => b.block_id);
    await reorderBlocks(blockIds);
    toast.success("Block moved");
  };

  // Handle block drag end
  const handleBlockDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !selectedSection?.blocks) return;

    const oldIndex = selectedSection.blocks.findIndex(b => b.block_id === active.id);
    const newIndex = selectedSection.blocks.findIndex(b => b.block_id === over.id);

    const reorderedBlocks = arrayMove(selectedSection.blocks, oldIndex, newIndex);
    const blockIds = reorderedBlocks.map(b => b.block_id);
    
    // Update UI immediately
    setSelectedSection({ ...selectedSection, blocks: reorderedBlocks });
    
    // Save to server
    await reorderBlocks(blockIds);
  };

  // Auto-translate content to all languages
  const autoTranslateContent = async (sourceText, sourceLang = "en") => {
    if (!sourceText || sourceText.trim() === "") return null;
    try {
      // Traduce automaticamente in background
      const res = await axios.post(`${API}/admin/translate-block`, { 
        content: { [sourceLang]: sourceText } 
      }, getAuthHeaders());
      return res.data.translations;
    } catch (error) {
      console.error("Auto-translation failed:", error);
      return null;
    }
  };

  // Legacy manual translate (kept for compatibility but hidden from UI)
  const translateBlock = async (block) => {
    const sourceText = block.content?.en || block.content?.it || block.button_text?.en || block.button_text?.it;
    if (!sourceText) { toast.error("Add text first"); return; }
    setTranslating(true);
    try {
      const sourceLang = block.content?.en || block.button_text?.en ? "en" : "it";
      const res = await axios.post(`${API}/admin/translate-block`, { content: { [sourceLang]: sourceText } }, getAuthHeaders());
      const field = block.block_type === "button" ? "button_text" : "content";
      await updateBlockInSection(block.block_id, { [field]: res.data.translations });
      toast.success("Translated to all languages!");
    } catch (error) { toast.error("Translation failed"); }
    finally { setTranslating(false); }
  };

  // Drag & Drop handler
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(s => s.section_id === active.id);
    const newIndex = sections.findIndex(s => s.section_id === over.id);

    const newOrder = arrayMove(sections, oldIndex, newIndex);
    setSections(newOrder); // Immediate UI update

    try {
      await axios.put(`${API}/admin/sections/reorder`, { section_ids: newOrder.map(s => s.section_id) }, getAuthHeaders());
      toast.success("Order updated");
    } catch (error) {
      toast.error("Failed to reorder");
      fetchSections(); // Revert on error
    }
  };

  // Move section up
  const moveSectionUp = async (sectionId) => {
    const idx = sections.findIndex(s => s.section_id === sectionId);
    if (idx <= 0) return; // Already at top
    
    const newOrder = [...sections];
    [newOrder[idx], newOrder[idx - 1]] = [newOrder[idx - 1], newOrder[idx]];
    setSections(newOrder);
    
    try {
      await axios.put(`${API}/admin/sections/reorder`, { section_ids: newOrder.map(s => s.section_id) }, getAuthHeaders());
      toast.success("Moved up");
    } catch (error) {
      toast.error("Failed to reorder");
      fetchSections();
    }
  };

  // Move section down
  const moveSectionDown = async (sectionId) => {
    const idx = sections.findIndex(s => s.section_id === sectionId);
    if (idx < 0 || idx >= sections.length - 1) return; // Already at bottom
    
    const newOrder = [...sections];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    setSections(newOrder);
    
    try {
      await axios.put(`${API}/admin/sections/reorder`, { section_ids: newOrder.map(s => s.section_id) }, getAuthHeaders());
      toast.success("Moved down");
    } catch (error) {
      toast.error("Failed to reorder");
      fetchSections();
    }
  };

  // Duplicate section
  const duplicateSection = async (sectionId) => {
    try {
      const res = await axios.post(`${API}/admin/sections/${sectionId}/duplicate`, {}, getAuthHeaders());
      toast.success("Section duplicated!");
      fetchSections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate section");
    }
  };

  const currentSection = sections.find(s => s.section_id === selectedSection?.section_id);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-heading text-3xl">Sections</h1>
        <div className="flex items-center gap-3">
          <PageInfoInline pageKey="sections" />
          <Dialog open={showAddSection} onOpenChange={setShowAddSection}>
            <DialogTrigger asChild>
              <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] active:bg-[#9A7B24] text-white font-semibold shadow-md focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2" data-testid="add-section-btn">
                <Plus size={20} className="mr-2" /> ADD NEW SECTION
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-heading text-2xl">Create New Section</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Section Name *</label>
                  <Input placeholder="e.g., About Us, Services, Rooms..." value={newSection.section_name} onChange={(e) => setNewSection({...newSection, section_name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Background Color</label>
                <div className="flex gap-2">
                  <Input type="color" value={newSection.background_color} onChange={(e) => setNewSection({...newSection, background_color: e.target.value})} className="w-16 h-10" />
                  <Input value={newSection.background_color} onChange={(e) => setNewSection({...newSection, background_color: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Padding</label>
                <Select value={newSection.padding} onValueChange={(v) => setNewSection({...newSection, padding: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="py-0">No Padding</SelectItem>
                    <SelectItem value="py-12">Small</SelectItem>
                    <SelectItem value="py-24">Medium</SelectItem>
                    <SelectItem value="py-32">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-[#E8F4FD] rounded-lg border-2 border-blue-300">
                  <div>
                    <label className="text-sm font-semibold text-[#1C1917]">Show in Home</label>
                    <p className="text-xs text-[#78716C]">Display in homepage</p>
                  </div>
                  <div className={`w-12 h-7 rounded-full cursor-pointer transition-colors ${newSection.show_in_home ? 'bg-blue-500' : 'bg-gray-300'}`}
                    onClick={() => setNewSection({...newSection, show_in_home: !newSection.show_in_home})}>
                    <div className={`w-6 h-6 mt-0.5 rounded-full bg-white shadow-md transition-transform ${newSection.show_in_home ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-[#F9F8F6] rounded-lg border-2 border-[#D4AF37]">
                  <div>
                    <label className="text-sm font-semibold text-[#1C1917]">Show in Menu</label>
                    <p className="text-xs text-[#78716C]">Link in navigation</p>
                  </div>
                  <div className={`w-12 h-7 rounded-full cursor-pointer transition-colors ${newSection.show_in_nav ? 'bg-[#D4AF37]' : 'bg-gray-300'}`}
                    onClick={() => setNewSection({...newSection, show_in_nav: !newSection.show_in_nav})}>
                    <div className={`w-6 h-6 mt-0.5 rounded-full bg-white shadow-md transition-transform ${newSection.show_in_nav ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                  </div>
                </div>
              </div>
              <Button onClick={createSection} className="w-full bg-[#1C1917] hover:bg-[#292524] active:bg-[#0C0A09] text-white font-semibold focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2" size="lg">
                <Plus size={18} className="mr-2" /> Create Section
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-6">
        {/* Section List with Drag & Drop */}
        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-[#78716C] font-medium">All Sections ({sections.length + 1})</p>
          <p className="text-xs text-[#78716C] italic">Use arrows ↑↓ or drag to reorder</p>
          
          {/* Logo Section - Fixed at top, not draggable */}
          <div 
            onClick={() => { setShowLogoSection(true); setSelectedSection(null); }}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${showLogoSection ? 'border-[#D4AF37] bg-[#F5E6B3]' : 'border-[#E7E5E4] bg-white hover:border-[#D4AF37]'}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1C1917] flex items-center justify-center">
                <ImageIcon size={18} className="text-white" />
              </div>
              <div className="flex-1">
                <p className={`font-semibold ${showLogoSection ? "text-[#92710F]" : "text-[#1C1917]"}`}>Logo</p>
                <p className={`text-xs ${showLogoSection ? "text-[#A88B2B]" : "text-[#A8A29E]"}`}>Configurazione logo sito</p>
              </div>
              <div className="px-2 py-0.5 bg-[#E7E5E4] text-[#78716C] text-xs rounded font-medium">FISSO</div>
            </div>
          </div>
          
          {sections.length === 0 && (
            <div className="admin-card text-center py-8 text-[#78716C]">
              <Layers size={32} className="mx-auto mb-2 opacity-50" />
              <p>No sections yet</p>
              <p className="text-sm">Click "Add New Section" to start</p>
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sections.map(s => s.section_id)} strategy={verticalListSortingStrategy}>
              {sections.map((section) => (
                <SortableSectionItem
                  key={section.section_id}
                  section={section}
                  isSelected={selectedSection?.section_id === section.section_id && !showLogoSection}
                  onSelect={(s) => { setSelectedSection(s); setShowLogoSection(false); }}
                  onDuplicate={duplicateSection}
                  onMoveUp={moveSectionUp}
                  onMoveDown={moveSectionDown}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {/* Section Editor */}
        <div className="md:col-span-3 space-y-6">
          {/* Logo Configuration Panel */}
          {showLogoSection && (
            <div className="admin-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl">Configurazione Logo</h2>
                <Button onClick={saveLogoConfig} disabled={savingLogo} className="bg-[#1C1917] hover:bg-[#292524] text-white">
                  {savingLogo ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />} Salva
                </Button>
              </div>
              
              <div className="space-y-6">
                {/* Logo Preview */}
                <div className="p-6 bg-[#F9F8F6] rounded-lg border-2 border-dashed border-[#D4AF37]">
                  <p className="text-sm font-medium text-[#44403C] mb-4">Anteprima Logo</p>
                  <div className="flex items-center gap-6">
                    <div className="p-4 bg-white rounded-lg border border-[#D4AF37]">
                      {logoConfig.logo_url ? (
                        <img 
                          src={logoConfig.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${logoConfig.logo_url}` : logoConfig.logo_url} 
                          alt="Logo" 
                          style={{ height: `${logoConfig.logo_size}px` }}
                          className="object-contain"
                        />
                      ) : (
                        <div 
                          style={{ height: `${logoConfig.logo_size}px`, width: `${logoConfig.logo_size * 2.5}px` }}
                          className="bg-gray-300 flex items-center justify-center text-gray-500 text-xs font-medium rounded"
                        >
                          LOGO
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-[#1C1917] rounded-lg">
                      {logoConfig.logo_url ? (
                        <img 
                          src={logoConfig.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${logoConfig.logo_url}` : logoConfig.logo_url} 
                          alt="Logo" 
                          style={{ height: `${logoConfig.logo_size}px` }}
                          className="object-contain invert"
                        />
                      ) : (
                        <div 
                          style={{ height: `${logoConfig.logo_size}px`, width: `${logoConfig.logo_size * 2.5}px` }}
                          className="bg-gray-600 flex items-center justify-center text-gray-400 text-xs font-medium rounded"
                        >
                          LOGO
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-[#78716C] mt-3">Sinistra: header | Destra: footer/admin</p>
                </div>

                {/* Upload Logo */}
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-2 block">Carica Logo</label>
                  <p className="text-xs text-[#78716C] mb-3">
                    Caricare logo in formato .svg, .jpg o .png.
                    I file .jpg e .png verranno gestiti dal sistema.
                  </p>
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept=".jpg,.jpeg,.png,.svg" 
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <div className="px-4 py-2 bg-[#1C1917] text-white rounded-lg hover:bg-[#292524] transition-colors flex items-center gap-2">
                        {uploadingLogo ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                        <span>{uploadingLogo ? "Caricamento..." : "Seleziona File"}</span>
                      </div>
                    </label>
                    {logoConfig.logo_url && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLogoConfig({ ...logoConfig, logo_url: null })}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="mr-1" /> Rimuovi
                      </Button>
                    )}
                  </div>
                </div>

                {/* Logo Size */}
                <div>
                  <label className="text-sm font-medium text-[#44403C] mb-2 block">Dimensione Logo (pixel)</label>
                  <p className="text-xs text-[#78716C] mb-3">Altezza del logo in pixel. Sarà uguale in header e footer.</p>
                  <div className="flex items-center gap-4">
                    <Input 
                      type="number" 
                      value={logoConfig.logo_size} 
                      onChange={(e) => setLogoConfig({ ...logoConfig, logo_size: parseInt(e.target.value) || 40 })}
                      className="w-24"
                      min={20}
                      max={120}
                    />
                    <span className="text-sm text-[#78716C]">px</span>
                    <input 
                      type="range" 
                      min={20} 
                      max={120} 
                      value={logoConfig.logo_size}
                      onChange={(e) => setLogoConfig({ ...logoConfig, logo_size: parseInt(e.target.value) })}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Show in Footer Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#F9F8F6] rounded-lg border border-[#D4AF37]">
                  <div>
                    <label className="text-sm font-semibold text-[#1C1917]">Mostra nel Footer</label>
                    <p className="text-xs text-[#78716C]">Attiva/disattiva la visualizzazione del logo nel footer</p>
                  </div>
                  <div 
                    className={`w-12 h-7 rounded-full cursor-pointer transition-colors ${logoConfig.show_in_footer ? 'bg-green-500' : 'bg-gray-300'}`}
                    onClick={() => setLogoConfig({ ...logoConfig, show_in_footer: !logoConfig.show_in_footer })}
                  >
                    <div className={`w-6 h-6 mt-0.5 rounded-full bg-white shadow-md transition-transform ${logoConfig.show_in_footer ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {currentSection && !showLogoSection ? (
            <>
              {/* Section Settings */}
              <div className="admin-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-semibold">{currentSection.section_name}</h2>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 border-2 border-[#D4AF37] px-3 py-2 rounded-lg">
                      <span className="text-sm font-semibold text-[#1C1917]">Active</span>
                      <div className={`w-10 h-6 rounded-full cursor-pointer transition-colors ${currentSection.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                        onClick={() => updateSection(currentSection.section_id, { is_active: !currentSection.is_active })}>
                        <div className={`w-5 h-5 mt-0.5 rounded-full bg-white shadow-md transition-transform ${currentSection.is_active ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 border-2 border-[#D4AF37] px-3 py-2 rounded-lg">
                      <span className="text-sm font-semibold text-[#1C1917]">Show in Home</span>
                      <div className={`w-10 h-6 rounded-full cursor-pointer transition-colors ${currentSection.show_in_home !== false ? 'bg-blue-500' : 'bg-gray-300'}`}
                        onClick={() => updateSection(currentSection.section_id, { show_in_home: currentSection.show_in_home === false ? true : false })}>
                        <div className={`w-5 h-5 mt-0.5 rounded-full bg-white shadow-md transition-transform ${currentSection.show_in_home !== false ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 border-2 border-[#D4AF37] px-3 py-2 rounded-lg">
                      <span className="text-sm font-semibold text-[#1C1917]">Show in Menu</span>
                      <div className={`w-10 h-6 rounded-full cursor-pointer transition-colors ${currentSection.show_in_nav ? 'bg-[#D4AF37]' : 'bg-gray-300'}`}
                        onClick={() => updateSection(currentSection.section_id, { show_in_nav: !currentSection.show_in_nav })}>
                        <div className={`w-5 h-5 mt-0.5 rounded-full bg-white shadow-md transition-transform ${currentSection.show_in_nav ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
                      </div>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => deleteSection(currentSection.section_id)}>
                      <Trash2 size={14} className="mr-1" /> Delete
                    </Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Section Name</label>
                    <Input 
                      defaultValue={currentSection.section_name} 
                      key={currentSection.section_id}
                      onBlur={(e) => {
                        if (e.target.value !== currentSection.section_name) {
                          updateSection(currentSection.section_id, { section_name: e.target.value });
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Background Color</label>
                    <div className="flex gap-2">
                      <Input 
                        type="color" 
                        defaultValue={currentSection.background_color || "#F9F8F6"} 
                        key={`color-${currentSection.section_id}`}
                        onChange={(e) => updateSection(currentSection.section_id, { background_color: e.target.value })} 
                        className="w-12 h-9" 
                      />
                      <Input 
                        defaultValue={currentSection.background_color || "#F9F8F6"} 
                        key={`colortext-${currentSection.section_id}`}
                        onBlur={(e) => {
                          if (e.target.value !== currentSection.background_color) {
                            updateSection(currentSection.section_id, { background_color: e.target.value });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Padding</label>
                    <Select value={currentSection.padding || "py-24"} onValueChange={(v) => updateSection(currentSection.section_id, { padding: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="py-0">No Padding (0px)</SelectItem>
                        <SelectItem value="py-4">Minimal (16px)</SelectItem>
                        <SelectItem value="py-8">Extra Small (32px)</SelectItem>
                        <SelectItem value="py-12">Small (48px)</SelectItem>
                        <SelectItem value="py-24">Medium (96px)</SelectItem>
                        <SelectItem value="py-32">Large (128px)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Content Blocks */}
              <div className="admin-card">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                  <h3 className="font-heading admin-section-title">Content Blocks</h3>
                  <div className="flex gap-2 flex-wrap">
                    <Select value={selectedLang} onValueChange={setSelectedLang}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{LANGUAGE_ORDER.map(code => <SelectItem key={code} value={code}>{LANGUAGES[code]}</SelectItem>)}</SelectContent>
                    </Select>
                    <Dialog open={showAddBlock} onOpenChange={setShowAddBlock}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#D4AF37] hover:bg-[#B8962E] active:bg-[#9A7B24] text-white font-semibold focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2">
                          <Plus size={16} className="mr-1" /> Add Block
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle className="font-heading text-xl">Add Content Block</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="text-sm font-medium mb-2 block">Block Type</label>
                            <div className="grid grid-cols-4 gap-2">
                              {BLOCK_TYPES.map(t => (
                                <button
                                  key={t.value}
                                  onClick={() => setNewBlock({...newBlock, block_type: t.value})}
                                  className={`p-3 border rounded text-center transition-all ${newBlock.block_type === t.value ? "border-[#D4AF37] bg-[#FDF9EF]" : "border-[#D4AF37] hover:border-[#D4AF37]"}`}
                                >
                                  <span className="text-lg block mb-1">{t.icon}</span>
                                  <span className="text-xs">{t.label.split(" ")[0]}</span>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Text blocks: H1, H2, H3, Paragraph */}
                          {["h1", "h2", "h3", "paragraph"].includes(newBlock.block_type) && (
                            <>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Text ({LANGUAGES[selectedLang]})</label>
                                <Textarea 
                                  placeholder="Enter text content..." 
                                  value={newBlock.content?.[selectedLang] || ""} 
                                  onChange={(e) => setNewBlock({...newBlock, content: {...newBlock.content, [selectedLang]: e.target.value}})}
                                  rows={3}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Text Alignment</label>
                                <div className="flex gap-2">
                                  {TEXT_ALIGNMENTS.map(a => (
                                    <button
                                      key={a.value}
                                      onClick={() => setNewBlock({...newBlock, text_align: a.value})}
                                      className={`flex-1 p-2 border rounded flex items-center justify-center gap-2 ${newBlock.text_align === a.value ? "border-[#D4AF37] bg-[#FDF9EF]" : "border-[#D4AF37]"}`}
                                    >
                                      <a.icon size={16} />
                                      <span className="text-sm">{a.label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Text Color (optional override)</label>
                                <div className="flex gap-2 items-center">
                                  <Input 
                                    type="color" 
                                    value={newBlock.text_color || "#1C1917"} 
                                    onChange={(e) => setNewBlock({...newBlock, text_color: e.target.value})} 
                                    className="w-12 h-10" 
                                  />
                                  <Input 
                                    value={newBlock.text_color || ""} 
                                    onChange={(e) => setNewBlock({...newBlock, text_color: e.target.value})} 
                                    placeholder="Default (from typography)"
                                  />
                                  {newBlock.text_color && (
                                    <button 
                                      onClick={() => setNewBlock({...newBlock, text_color: null})}
                                      className="text-xs text-[#78716C] hover:text-red-600"
                                    >
                                      Reset
                                    </button>
                                  )}
                                </div>
                                <p className="text-xs text-[#78716C] mt-1">Leave empty to use global typography color</p>
                              </div>
                              
                              {/* Style Overrides */}
                              <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#D4AF37]">
                                <p className="text-xs uppercase tracking-wider text-[#78716C] font-semibold mb-3">Style Overrides (optional)</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Font Size</label>
                                    <Select 
                                      value={newBlock.font_size || "default"} 
                                      onValueChange={(v) => setNewBlock({...newBlock, font_size: v === "default" ? null : v})}
                                    >
                                      <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="default">Default (use global)</SelectItem>
                                        <SelectItem value="14px">14px - Small</SelectItem>
                                        <SelectItem value="16px">16px - Base</SelectItem>
                                        <SelectItem value="18px">18px - Medium</SelectItem>
                                        <SelectItem value="24px">24px - Large</SelectItem>
                                        <SelectItem value="32px">32px - XL</SelectItem>
                                        <SelectItem value="42px">42px - 2XL</SelectItem>
                                        <SelectItem value="56px">56px - 3XL</SelectItem>
                                        <SelectItem value="72px">72px - 4XL</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Font Weight</label>
                                    <Select 
                                      value={newBlock.font_weight || "default"} 
                                      onValueChange={(v) => setNewBlock({...newBlock, font_weight: v === "default" ? null : v})}
                                    >
                                      <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="default">Default (use global)</SelectItem>
                                        <SelectItem value="300">300 - Light</SelectItem>
                                        <SelectItem value="400">400 - Normal</SelectItem>
                                        <SelectItem value="500">500 - Medium</SelectItem>
                                        <SelectItem value="600">600 - Semi Bold</SelectItem>
                                        <SelectItem value="700">700 - Bold</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                {/* Vertical Padding */}
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Padding Top (px)</label>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="200"
                                      placeholder="0"
                                      value={newBlock.padding_top || ""} 
                                      onChange={(e) => setNewBlock({...newBlock, padding_top: e.target.value ? parseInt(e.target.value) : null})}
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Padding Bottom (px)</label>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="200"
                                      placeholder="0"
                                      value={newBlock.padding_bottom || ""} 
                                      onChange={(e) => setNewBlock({...newBlock, padding_bottom: e.target.value ? parseInt(e.target.value) : null})}
                                    />
                                  </div>
                                </div>
                                {/* Vertical Margin - supports negative values */}
                                <p className="text-xs uppercase tracking-wider text-[#78716C] font-semibold mt-4 mb-2">Block Margins (external spacing)</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Margin Top (px)</label>
                                    <Input 
                                      type="number" 
                                      min="-200" 
                                      max="200"
                                      placeholder="0"
                                      value={newBlock.margin_top ?? ""} 
                                      onChange={(e) => setNewBlock({...newBlock, margin_top: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                    />
                                    <p className="text-xs text-[#A8A29E] mt-1">Negative values pull element up</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Margin Bottom (px)</label>
                                    <Input 
                                      type="number" 
                                      min="-200" 
                                      max="200"
                                      placeholder="0"
                                      value={newBlock.margin_bottom ?? ""} 
                                      onChange={(e) => setNewBlock({...newBlock, margin_bottom: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                    />
                                    <p className="text-xs text-[#A8A29E] mt-1">Negative values reduce space below</p>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Image block */}
                          {newBlock.block_type === "image" && (
                            <>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Image *</label>
                                <ImageUploader 
                                  value={newBlock.image_url} 
                                  onChange={(url) => setNewBlock({...newBlock, image_url: url})}
                                  getAuthHeaders={getAuthHeaders}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Alt Text ({LANGUAGES[selectedLang]})</label>
                                <Input placeholder="Image description..." value={newBlock.image_alt?.[selectedLang] || ""} onChange={(e) => setNewBlock({...newBlock, image_alt: {...newBlock.image_alt, [selectedLang]: e.target.value}})} />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Layout</label>
                                  <Select value={newBlock.image_layout} onValueChange={(v) => setNewBlock({...newBlock, image_layout: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{IMAGE_LAYOUTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Sizing</label>
                                  <Select value={newBlock.image_sizing} onValueChange={(v) => setNewBlock({...newBlock, image_sizing: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{IMAGE_SIZING.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Button block */}
                          {newBlock.block_type === "button" && (
                            <>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Button Text ({LANGUAGES[selectedLang]})</label>
                                <Input placeholder="Click here" value={newBlock.button_text?.[selectedLang] || ""} onChange={(e) => setNewBlock({...newBlock, button_text: {...newBlock.button_text, [selectedLang]: e.target.value}})} />
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Link URL</label>
                                <Input placeholder="#contact or https://..." value={newBlock.button_link} onChange={(e) => setNewBlock({...newBlock, button_link: e.target.value})} />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Style</label>
                                  <Select value={newBlock.button_style} onValueChange={(v) => setNewBlock({...newBlock, button_style: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="primary">Primary (Dark)</SelectItem>
                                      <SelectItem value="secondary">Secondary (Outline)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Alignment</label>
                                  <Select value={newBlock.button_align || "center"} onValueChange={(v) => setNewBlock({...newBlock, button_align: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="left">Left</SelectItem>
                                      <SelectItem value="center">Center</SelectItem>
                                      <SelectItem value="right">Right</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Gallery block */}
                          {newBlock.block_type === "gallery" && (
                            <div className="space-y-4">
                              <div>
                                <label className="text-sm font-medium mb-2 block">Gallery Title ({LANGUAGES[selectedLang]})</label>
                                <Input 
                                  placeholder="Our Gallery..." 
                                  value={newBlock.gallery_title?.[selectedLang] || ""} 
                                  onChange={(e) => setNewBlock({...newBlock, gallery_title: {...newBlock.gallery_title, [selectedLang]: e.target.value}})}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Description ({LANGUAGES[selectedLang]})</label>
                                <Textarea 
                                  placeholder="A brief description of this gallery..." 
                                  value={newBlock.gallery_description?.[selectedLang] || ""} 
                                  onChange={(e) => setNewBlock({...newBlock, gallery_description: {...newBlock.gallery_description, [selectedLang]: e.target.value}})}
                                  rows={2}
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Layout</label>
                                <Select value={newBlock.gallery_layout || "grid"} onValueChange={(v) => setNewBlock({...newBlock, gallery_layout: v})}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {GALLERY_LAYOUTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-sm font-medium mb-2 block">Images</label>
                                <p className="text-xs text-[#78716C] mb-3">Add images after creating the gallery block</p>
                              </div>
                            </div>
                          )}

                          {/* Vertical Spacing - Available for all block types except spacer */}
                          {newBlock.block_type && newBlock.block_type !== "spacer" && !["h1", "h2", "h3", "paragraph"].includes(newBlock.block_type) && (
                            <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#D4AF37]">
                              <p className="text-xs uppercase tracking-wider text-[#78716C] font-semibold mb-3">Block Padding (internal spacing)</p>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Padding Top (px)</label>
                                  <Input 
                                    type="number" 
                                    min="0" 
                                    max="200"
                                    placeholder="0"
                                    value={newBlock.padding_top || ""} 
                                    onChange={(e) => setNewBlock({...newBlock, padding_top: e.target.value ? parseInt(e.target.value) : null})}
                                  />
                                </div>
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Padding Bottom (px)</label>
                                  <Input 
                                    type="number" 
                                    min="0" 
                                    max="200"
                                    placeholder="0"
                                    value={newBlock.padding_bottom || ""} 
                                    onChange={(e) => setNewBlock({...newBlock, padding_bottom: e.target.value ? parseInt(e.target.value) : null})}
                                  />
                                </div>
                              </div>
                              <p className="text-xs uppercase tracking-wider text-[#78716C] font-semibold mt-4 mb-2">Block Margins (external spacing)</p>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Margin Top (px)</label>
                                  <Input 
                                    type="number" 
                                    min="-200" 
                                    max="200"
                                    placeholder="0"
                                    value={newBlock.margin_top ?? ""} 
                                    onChange={(e) => setNewBlock({...newBlock, margin_top: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                  />
                                  <p className="text-xs text-[#A8A29E] mt-1">Negative = pull up</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Margin Bottom (px)</label>
                                  <Input 
                                    type="number" 
                                    min="-200" 
                                    max="200"
                                    placeholder="0"
                                    value={newBlock.margin_bottom ?? ""} 
                                    onChange={(e) => setNewBlock({...newBlock, margin_bottom: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                  />
                                  <p className="text-xs text-[#A8A29E] mt-1">Negative = reduce gap</p>
                                </div>
                              </div>
                            </div>
                          )}

                          <Button onClick={addBlock} className="w-full bg-[#1C1917] hover:bg-[#292524] active:bg-[#0C0A09] text-white font-semibold focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2" size="lg">Add Block to Section</Button>
                        </div>
                      </DialogContent>
                    </Dialog>

                    {/* EDIT BLOCK DIALOG */}
                    <Dialog open={showEditBlock} onOpenChange={setShowEditBlock}>
                      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-2xl">Edit Content Block</DialogTitle>
                        </DialogHeader>
                        {editingBlock && (
                          <div className="space-y-6 py-4">
                            {/* Block Type Selector */}
                            <div>
                              <label className="text-sm font-medium mb-3 block">Block Type</label>
                              <div className="grid grid-cols-4 gap-2">
                                {BLOCK_TYPES.filter(t => !["gallery", "spacer"].includes(t.value)).map(type => (
                                  <button
                                    key={type.value}
                                    onClick={() => setEditingBlock({...editingBlock, block_type: type.value})}
                                    className={`p-3 border-2 rounded-lg text-center transition-all ${editingBlock.block_type === type.value ? "border-[#D4AF37] bg-[#FEF3C7]" : "border-[#D4AF37] hover:border-[#D4AF37]"}`}
                                  >
                                    <span className="text-lg font-bold block">{type.icon}</span>
                                    <span className="text-xs">{type.label}</span>
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Text Content */}
                            {["h1", "h2", "h3", "paragraph"].includes(editingBlock.block_type) && (
                              <>
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Text Content ({LANGUAGES[selectedLang]})</label>
                                  <Textarea 
                                    value={editingBlock.content?.[selectedLang] || ""} 
                                    onChange={(e) => setEditingBlock({...editingBlock, content: {...editingBlock.content, [selectedLang]: e.target.value}})}
                                    placeholder={`Enter ${editingBlock.block_type} text...`}
                                    rows={editingBlock.block_type === "paragraph" ? 5 : 2}
                                    className="text-base"
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Text Alignment</label>
                                    <div className="flex gap-2">
                                      {TEXT_ALIGNMENTS.map(a => (
                                        <button
                                          key={a.value}
                                          onClick={() => setEditingBlock({...editingBlock, text_align: a.value})}
                                          className={`flex-1 p-3 border-2 rounded flex items-center justify-center gap-2 ${editingBlock.text_align === a.value ? "border-[#D4AF37] bg-[#FEF3C7]" : "border-[#D4AF37] hover:border-[#D4AF37]"}`}
                                        >
                                          <a.icon size={18} />
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Text Color</label>
                                    <div className="flex gap-2 items-center">
                                      <Input 
                                        type="color" 
                                        value={editingBlock.text_color || "#1C1917"} 
                                        onChange={(e) => setEditingBlock({...editingBlock, text_color: e.target.value})} 
                                        className="w-14 h-10" 
                                      />
                                      <Input 
                                        value={editingBlock.text_color || ""} 
                                        onChange={(e) => setEditingBlock({...editingBlock, text_color: e.target.value})} 
                                        placeholder="Default"
                                        className="flex-1"
                                      />
                                      {editingBlock.text_color && (
                                        <Button variant="outline" size="sm" onClick={() => setEditingBlock({...editingBlock, text_color: null})}>
                                          Reset
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Advanced Overrides - Font Size & Weight */}
                                <div className="p-4 bg-[#FDF9EF] rounded-lg border border-[#D4AF37] mt-4">
                                  <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-3">Style Overrides</p>
                                  <p className="text-xs text-[#78716C] mb-3">Leave empty to use global typography settings</p>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="text-sm font-medium mb-2 block">Font Size</label>
                                      <Select 
                                        value={editingBlock.font_size || "default"} 
                                        onValueChange={(v) => setEditingBlock({...editingBlock, font_size: v === "default" ? null : v})}
                                      >
                                        <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="default">Default (use global)</SelectItem>
                                          <SelectItem value="12px">12px - Extra Small</SelectItem>
                                          <SelectItem value="14px">14px - Small</SelectItem>
                                          <SelectItem value="16px">16px - Base</SelectItem>
                                          <SelectItem value="18px">18px - Medium</SelectItem>
                                          <SelectItem value="20px">20px</SelectItem>
                                          <SelectItem value="24px">24px - Large</SelectItem>
                                          <SelectItem value="28px">28px</SelectItem>
                                          <SelectItem value="32px">32px - XL</SelectItem>
                                          <SelectItem value="36px">36px</SelectItem>
                                          <SelectItem value="42px">42px - 2XL</SelectItem>
                                          <SelectItem value="48px">48px</SelectItem>
                                          <SelectItem value="56px">56px - 3XL</SelectItem>
                                          <SelectItem value="64px">64px</SelectItem>
                                          <SelectItem value="72px">72px - 4XL</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium mb-2 block">Font Weight</label>
                                      <Select 
                                        value={editingBlock.font_weight || "default"} 
                                        onValueChange={(v) => setEditingBlock({...editingBlock, font_weight: v === "default" ? null : v})}
                                      >
                                        <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="default">Default (use global)</SelectItem>
                                          <SelectItem value="300">300 - Light</SelectItem>
                                          <SelectItem value="400">400 - Normal</SelectItem>
                                          <SelectItem value="500">500 - Medium</SelectItem>
                                          <SelectItem value="600">600 - Semi Bold</SelectItem>
                                          <SelectItem value="700">700 - Bold</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  {/* Vertical Padding */}
                                  <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div>
                                      <label className="text-sm font-medium mb-2 block">Padding Top (px)</label>
                                      <Input 
                                        type="number" 
                                        min="0" 
                                        max="200"
                                        placeholder="0"
                                        value={editingBlock.padding_top || ""} 
                                        onChange={(e) => setEditingBlock({...editingBlock, padding_top: e.target.value ? parseInt(e.target.value) : null})}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium mb-2 block">Padding Bottom (px)</label>
                                      <Input 
                                        type="number" 
                                        min="0" 
                                        max="200"
                                        placeholder="0"
                                        value={editingBlock.padding_bottom || ""} 
                                        onChange={(e) => setEditingBlock({...editingBlock, padding_bottom: e.target.value ? parseInt(e.target.value) : null})}
                                      />
                                    </div>
                                  </div>
                                  {/* Vertical Margin - supports negative values */}
                                  <p className="text-xs uppercase tracking-wider text-[#78716C] font-semibold mt-4 mb-2">Block Margins (external spacing)</p>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <label className="text-sm font-medium mb-2 block">Margin Top (px)</label>
                                      <Input 
                                        type="number" 
                                        min="-200" 
                                        max="200"
                                        placeholder="0"
                                        value={editingBlock.margin_top ?? ""} 
                                        onChange={(e) => setEditingBlock({...editingBlock, margin_top: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                      />
                                      <p className="text-xs text-[#A8A29E] mt-1">Negative values pull element up</p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium mb-2 block">Margin Bottom (px)</label>
                                      <Input 
                                        type="number" 
                                        min="-200" 
                                        max="200"
                                        placeholder="0"
                                        value={editingBlock.margin_bottom ?? ""} 
                                        onChange={(e) => setEditingBlock({...editingBlock, margin_bottom: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                      />
                                      <p className="text-xs text-[#A8A29E] mt-1">Negative values reduce space below</p>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Button Content */}
                            {editingBlock.block_type === "button" && (
                              <>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Button Text ({LANGUAGES[selectedLang]})</label>
                                    <Input 
                                      value={editingBlock.button_text?.[selectedLang] || ""} 
                                      onChange={(e) => setEditingBlock({...editingBlock, button_text: {...editingBlock.button_text, [selectedLang]: e.target.value}})}
                                      placeholder="Button text..."
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Link URL</label>
                                    <Input 
                                      value={editingBlock.button_link || ""} 
                                      onChange={(e) => setEditingBlock({...editingBlock, button_link: e.target.value})}
                                      placeholder="#section or https://..."
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Style</label>
                                    <Select value={editingBlock.button_style || "primary"} onValueChange={(v) => setEditingBlock({...editingBlock, button_style: v})}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="primary">Primary (Gold)</SelectItem>
                                        <SelectItem value="secondary">Secondary (Outline)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Alignment</label>
                                    <Select value={editingBlock.button_align || "center"} onValueChange={(v) => setEditingBlock({...editingBlock, button_align: v})}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="left">Left</SelectItem>
                                        <SelectItem value="center">Center</SelectItem>
                                        <SelectItem value="right">Right</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Image Content */}
                            {editingBlock.block_type === "image" && (
                              <>
                                <div>
                                  <label className="text-sm font-medium mb-2 block">Image</label>
                                  <ImageUploader 
                                    value={editingBlock.image_url} 
                                    onChange={(url) => setEditingBlock({...editingBlock, image_url: url})}
                                    getAuthHeaders={getAuthHeaders}
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Layout</label>
                                    <Select value={editingBlock.image_layout || "full"} onValueChange={(v) => setEditingBlock({...editingBlock, image_layout: v})}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>{IMAGE_LAYOUTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Sizing</label>
                                    <Select value={editingBlock.image_sizing || "cover"} onValueChange={(v) => setEditingBlock({...editingBlock, image_sizing: v})}>
                                      <SelectTrigger><SelectValue /></SelectTrigger>
                                      <SelectContent>{IMAGE_SIZING.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Vertical Spacing - for non-text blocks */}
                            {editingBlock && !["h1", "h2", "h3", "paragraph", "spacer"].includes(editingBlock.block_type) && (
                              <div className="p-4 bg-[#FDF9EF] rounded-lg border border-[#D4AF37]">
                                <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-3">Block Padding (internal spacing)</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Padding Top (px)</label>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="200"
                                      placeholder="0"
                                      value={editingBlock.padding_top || ""} 
                                      onChange={(e) => setEditingBlock({...editingBlock, padding_top: e.target.value ? parseInt(e.target.value) : null})}
                                    />
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Padding Bottom (px)</label>
                                    <Input 
                                      type="number" 
                                      min="0" 
                                      max="200"
                                      placeholder="0"
                                      value={editingBlock.padding_bottom || ""} 
                                      onChange={(e) => setEditingBlock({...editingBlock, padding_bottom: e.target.value ? parseInt(e.target.value) : null})}
                                    />
                                  </div>
                                </div>
                                <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mt-4 mb-2">Block Margins (external spacing)</p>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Margin Top (px)</label>
                                    <Input 
                                      type="number" 
                                      min="-200" 
                                      max="200"
                                      placeholder="0"
                                      value={editingBlock.margin_top ?? ""} 
                                      onChange={(e) => setEditingBlock({...editingBlock, margin_top: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                    />
                                    <p className="text-xs text-[#A8A29E] mt-1">Negative = pull up</p>
                                  </div>
                                  <div>
                                    <label className="text-sm font-medium mb-2 block">Margin Bottom (px)</label>
                                    <Input 
                                      type="number" 
                                      min="-200" 
                                      max="200"
                                      placeholder="0"
                                      value={editingBlock.margin_bottom ?? ""} 
                                      onChange={(e) => setEditingBlock({...editingBlock, margin_bottom: e.target.value !== "" ? parseInt(e.target.value) : null})}
                                    />
                                    <p className="text-xs text-[#A8A29E] mt-1">Negative = reduce gap</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Save Button */}
                            <Button 
                              onClick={async () => {
                                await saveBlockWithTranslation(editingBlock);
                                setShowEditBlock(false);
                                setEditingBlock(null);
                              }} 
                              className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-white" 
                              size="lg"
                              disabled={translating}
                            >
                              {translating ? (
                                <><RefreshCw size={18} className="mr-2 animate-spin" /> Translating...</>
                              ) : (
                                <><Save size={18} className="mr-2" /> Save & Translate</>
                              )}
                            </Button>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                <div className="space-y-4">
                  {(!currentSection.blocks || currentSection.blocks.length === 0) && (
                    <div className="text-center py-12 border-2 border-dashed border-[#D4AF37] rounded-lg">
                      <ImageIcon size={48} className="mx-auto mb-4 text-[#78716C] opacity-50" />
                      <p className="text-[#78716C] font-medium">No content blocks yet</p>
                      <p className="text-sm text-[#78716C] mb-4">Add text, images, or buttons to build this section</p>
                      <Button onClick={() => setShowAddBlock(true)} className="bg-[#D4AF37] hover:bg-[#B8962E]">
                        <Plus size={16} className="mr-1" /> Add First Block
                      </Button>
                    </div>
                  )}
                  
                  {/* Block List with Drag & Drop Reordering */}
                  {currentSection.blocks && currentSection.blocks.length > 0 && (
                    <div className="mb-2 text-xs text-[#78716C] flex items-center gap-2">
                      <GripVertical size={14} />
                      <span>Drag blocks to reorder, or use arrows</span>
                    </div>
                  )}
                  
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleBlockDragEnd}
                  >
                    <SortableContext 
                      items={currentSection.blocks?.map(b => b.block_id) || []}
                      strategy={verticalListSortingStrategy}
                    >
                      {currentSection.blocks?.map((block, idx) => (
                        <SortableBlockItem
                          key={block.block_id}
                          block={block}
                          isFirst={idx === 0}
                          isLast={idx === currentSection.blocks.length - 1}
                          onMoveUp={() => moveBlock(idx, 'up')}
                          onMoveDown={() => moveBlock(idx, 'down')}
                        >
                          <div className="border border-[#D4AF37] p-4 rounded-lg bg-white">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-[#78716C] bg-[#F5F5F4] px-1.5 py-0.5 rounded">#{idx + 1}</span>
                                <span className="bg-[#D4AF37] text-white text-xs px-2 py-1 rounded font-medium">
                                  {BLOCK_TYPES.find(t => t.value === block.block_type)?.label || block.block_type}
                                </span>
                                {block.text_align && block.text_align !== "left" && (
                                  <span className="text-xs text-[#78716C] bg-[#F9F8F6] px-2 py-1 rounded">
                                    Align: {block.text_align}
                                  </span>
                                )}
                        </div>
                        <div className="flex gap-1">
                          {/* EDIT ICON */}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => { setEditingBlock({...block}); setShowEditBlock(true); }} 
                            title="Edit block"
                            className="h-8 w-8 p-0 hover:bg-[#FEF3C7] hover:border-[#D4AF37]"
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteBlock(block.block_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Text preview */}
                      {["h1", "h2", "h3", "paragraph"].includes(block.block_type) && (
                        <p className="mt-2 text-sm text-[#78716C] truncate">
                          {block.content?.[selectedLang] || block.content?.en || <span className="italic">Empty</span>}
                        </p>
                      )}
                      
                      {/* Image block editor */}
                      {block.block_type === "image" && (
                        <div className="space-y-3">
                          <ImageUploader 
                            value={block.image_url} 
                            onChange={(url) => updateBlockInSection(block.block_id, { image_url: url })}
                            getAuthHeaders={getAuthHeaders}
                          />
                          <div className="grid md:grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block">Alt Text ({LANGUAGES[selectedLang]})</label>
                              <Input 
                                value={block.image_alt?.[selectedLang] || ""} 
                                onChange={(e) => updateBlockInSection(block.block_id, { image_alt: { ...block.image_alt, [selectedLang]: e.target.value } })}
                                placeholder="Description..."
                              />
                            </div>
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block">Layout</label>
                              <Select value={block.image_layout || "full"} onValueChange={(v) => updateBlockInSection(block.block_id, { image_layout: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{IMAGE_LAYOUTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block">Sizing</label>
                              <Select value={block.image_sizing || "cover"} onValueChange={(v) => updateBlockInSection(block.block_id, { image_sizing: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{IMAGE_SIZING.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Button block editor */}
                      {block.block_type === "button" && (
                        <div className="space-y-3">
                          <div className="grid md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block font-medium">Button Text ({LANGUAGES[selectedLang]})</label>
                              <Input 
                                value={block.button_text?.[selectedLang] || ""} 
                                onChange={(e) => updateBlockInSection(block.block_id, { button_text: { ...block.button_text, [selectedLang]: e.target.value } })}
                                placeholder="Enter button text..."
                                className="border-2 border-[#D4AF37] focus:border-[#D4AF37]"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block font-medium">Link URL</label>
                              <Input 
                                value={block.button_link || ""} 
                                onChange={(e) => updateBlockInSection(block.block_id, { button_link: e.target.value })}
                                placeholder="#section-id or https://..."
                                className="border-2 border-[#D4AF37] focus:border-[#D4AF37]"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 bg-[#F9F8F6] p-3 rounded-lg">
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block font-medium">Style</label>
                              <Select value={block.button_style || "primary"} onValueChange={(v) => updateBlockInSection(block.block_id, { button_style: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="primary">Primary (Gold)</SelectItem>
                                  <SelectItem value="secondary">Secondary (Outline)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block font-medium">Alignment</label>
                              <Select value={block.button_align || "center"} onValueChange={(v) => updateBlockInSection(block.block_id, { button_align: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="left">Left</SelectItem>
                                  <SelectItem value="center">Center</SelectItem>
                                  <SelectItem value="right">Right</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Gallery block editor - ENHANCED with drag & drop */}
                      {block.block_type === "gallery" && (
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block">Title ({LANGUAGES[selectedLang]})</label>
                              <Input 
                                value={block.gallery_title?.[selectedLang] || ""} 
                                onChange={(e) => updateBlockInSection(block.block_id, { gallery_title: { ...block.gallery_title, [selectedLang]: e.target.value } })}
                                placeholder="Gallery title..."
                              />
                            </div>
                            <div>
                              <label className="text-xs text-[#78716C] mb-1 block">Layout</label>
                              <Select value={block.gallery_layout || "grid"} onValueChange={(v) => updateBlockInSection(block.block_id, { gallery_layout: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {GALLERY_LAYOUTS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-[#78716C] mb-1 block">Description ({LANGUAGES[selectedLang]})</label>
                            <Textarea 
                              value={block.gallery_description?.[selectedLang] || ""} 
                              onChange={(e) => updateBlockInSection(block.block_id, { gallery_description: { ...block.gallery_description, [selectedLang]: e.target.value } })}
                              placeholder="Gallery description..."
                              rows={2}
                            />
                          </div>
                          
                          {/* Gallery Images with Drag & Drop */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs text-[#78716C]">
                                Images ({block.gallery_images?.length || 0}) 
                                <span className="ml-2 text-[10px] text-[#D4AF37]">Drag to reorder</span>
                              </label>
                            </div>
                            
                            {/* Sortable Image Grid */}
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) => {
                                const { active, over } = event;
                                if (active.id !== over?.id) {
                                  const oldIndex = block.gallery_images.findIndex(img => img.image_id === active.id);
                                  const newIndex = block.gallery_images.findIndex(img => img.image_id === over.id);
                                  const reorderedImages = arrayMove(block.gallery_images, oldIndex, newIndex)
                                    .map((img, idx) => ({ ...img, order: idx }));
                                  updateBlockInSection(block.block_id, { gallery_images: reorderedImages });
                                }
                              }}
                            >
                              <SortableContext 
                                items={block.gallery_images?.map(img => img.image_id) || []}
                                strategy={rectSortingStrategy}
                              >
                                <div className="grid grid-cols-4 gap-2 mb-3">
                                  {block.gallery_images?.map((img, imgIdx) => (
                                    <SortableGalleryImage
                                      key={img.image_id}
                                      image={img}
                                      index={imgIdx}
                                      selectedLang={selectedLang}
                                      onDelete={(imageId) => {
                                        const newImages = block.gallery_images.filter(i => i.image_id !== imageId);
                                        updateBlockInSection(block.block_id, { gallery_images: newImages });
                                      }}
                                      onEdit={(image) => {
                                        setEditingGalleryImage(image);
                                        setGalleryImageBlockId(block.block_id);
                                      }}
                                    />
                                  ))}
                                </div>
                              </SortableContext>
                            </DndContext>
                            
                            {/* Add Image */}
                            <ImageUploader 
                              value=""
                              onChange={(url) => {
                                if (!url) return;
                                const newImage = { 
                                  image_id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, 
                                  url, 
                                  caption: {}, 
                                  alt: {},
                                  order: block.gallery_images?.length || 0 
                                };
                                updateBlockInSection(block.block_id, { 
                                  gallery_images: [...(block.gallery_images || []), newImage] 
                                });
                              }}
                              getAuthHeaders={getAuthHeaders}
                            />
                          </div>
                        </div>
                      )}

                      {/* Spacer */}
                      {block.block_type === "spacer" && (
                        <p className="text-sm text-[#78716C] text-center">Vertical spacer (adds space between blocks)</p>
                      )}
                          </div>
                        </SortableBlockItem>
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </>
          ) : !showLogoSection ? (
            <div className="admin-card text-center py-16">
              <Layers size={48} className="mx-auto mb-4 text-[#78716C] opacity-50" />
              <p className="text-[#78716C] font-medium mb-2">Select a section to edit</p>
              <p className="text-sm text-[#78716C] mb-6">Or create a new section to get started</p>
              <Button onClick={() => setShowAddSection(true)} className="bg-[#D4AF37] hover:bg-[#B8962E]">
                <Plus size={16} className="mr-2" /> Create First Section
              </Button>
            </div>
          ) : null}
        </div>
      </div>
      
      {/* Gallery Image Edit Modal */}
      <Dialog open={!!editingGalleryImage} onOpenChange={(open) => { if (!open) { setEditingGalleryImage(null); setGalleryImageBlockId(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Image Details</DialogTitle>
          </DialogHeader>
          {editingGalleryImage && (
            <div className="space-y-4 mt-4">
              {/* Image Preview */}
              <div className="flex justify-center">
                <img 
                  src={editingGalleryImage.url?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${editingGalleryImage.url}` : editingGalleryImage.url}
                  alt="Preview"
                  className="max-h-40 rounded-lg shadow-md"
                />
              </div>
              
              {/* Caption (Multilingual) */}
              <div>
                <label className="text-sm font-medium mb-2 block">Caption ({LANGUAGES[selectedLang]})</label>
                <Textarea 
                  value={editingGalleryImage.caption?.[selectedLang] || ""}
                  onChange={(e) => setEditingGalleryImage({
                    ...editingGalleryImage,
                    caption: { ...editingGalleryImage.caption, [selectedLang]: e.target.value }
                  })}
                  placeholder="Describe this image..."
                  rows={2}
                />
              </div>
              
              {/* Alt Text (Multilingual) */}
              <div>
                <label className="text-sm font-medium mb-2 block">Alt Text ({LANGUAGES[selectedLang]})</label>
                <Input 
                  value={editingGalleryImage.alt?.[selectedLang] || ""}
                  onChange={(e) => setEditingGalleryImage({
                    ...editingGalleryImage,
                    alt: { ...editingGalleryImage.alt, [selectedLang]: e.target.value }
                  })}
                  placeholder="Accessibility description..."
                />
                <p className="text-xs text-[#78716C] mt-1">Used for screen readers and SEO</p>
              </div>
              
              {/* Language Tabs for editing */}
              <div className="flex gap-1 flex-wrap">
                {LANGUAGE_ORDER.map(code => (
                  <button
                    key={code}
                    onClick={() => setSelectedLang(code)}
                    className={`px-2 py-1 text-xs rounded ${selectedLang === code ? 'bg-[#D4AF37] text-white' : 'bg-[#E7E5E4] text-[#78716C]'}`}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
              
              {/* Save Button */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => { setEditingGalleryImage(null); setGalleryImageBlockId(null); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="bg-[#D4AF37] hover:bg-[#B8962E]"
                  onClick={() => {
                    if (galleryImageBlockId && selectedSection) {
                      const block = selectedSection.blocks?.find(b => b.block_id === galleryImageBlockId);
                      if (block) {
                        const updatedImages = block.gallery_images.map(img => 
                          img.image_id === editingGalleryImage.image_id ? editingGalleryImage : img
                        );
                        updateBlockInSection(galleryImageBlockId, { gallery_images: updatedImages });
                      }
                    }
                    setEditingGalleryImage(null);
                    setGalleryImageBlockId(null);
                    toast.success("Image details updated");
                  }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Typography Settings
function TypographySettings() {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState({ headingFont: "Cormorant Garamond", bodyFont: "Manrope", h1: { size: "4rem", weight: "300", color: "#1C1917" }, h2: { size: "2.5rem", weight: "400", color: "#1C1917" }, h3: { size: "1.5rem", weight: "500", color: "#1C1917" }, paragraph: { size: "1rem", weight: "400", color: "#78716C" } });
  const [buttonConfig, setButtonConfig] = useState({
    book_now_button_color: "#D4AF37",
    book_now_button_style: "filled",
    send_message_button_color: "#1C1917",
    send_message_button_style: "filled"
  });
  
  // Track initial load to prevent auto-save on first render
  const isInitialLoadSettings = useRef(true);
  const isInitialLoadButtons = useRef(true);
  const saveSettingsTimeoutRef = useRef(null);
  const saveButtonsTimeoutRef = useRef(null);

  useEffect(() => { fetchSettings(); fetchButtonConfig(); }, []);
  
  // Auto-save settings (debounced)
  useEffect(() => {
    if (isInitialLoadSettings.current) return;
    
    if (saveSettingsTimeoutRef.current) clearTimeout(saveSettingsTimeoutRef.current);
    
    saveSettingsTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/admin/settings/typography`, settings, getAuthHeaders());
        toast.success("Tipografia salvata automaticamente", { duration: 2000 });
      } catch (error) { 
        toast.error("Errore nel salvataggio tipografia"); 
      }
    }, 1500);
    
    return () => { if (saveSettingsTimeoutRef.current) clearTimeout(saveSettingsTimeoutRef.current); };
  }, [settings, getAuthHeaders]);
  
  // Auto-save button config (debounced)
  useEffect(() => {
    if (isInitialLoadButtons.current) return;
    
    if (saveButtonsTimeoutRef.current) clearTimeout(saveButtonsTimeoutRef.current);
    
    saveButtonsTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/admin/calendar/config`, buttonConfig, getAuthHeaders());
        toast.success("Stile pulsanti salvato automaticamente", { duration: 2000 });
      } catch (error) { 
        toast.error("Errore nel salvataggio stile pulsanti"); 
      }
    }, 1500);
    
    return () => { if (saveButtonsTimeoutRef.current) clearTimeout(saveButtonsTimeoutRef.current); };
  }, [buttonConfig, getAuthHeaders]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/typography`);
      if (res.data?.settings) setSettings(res.data.settings);
      setTimeout(() => { isInitialLoadSettings.current = false; }, 500);
    } catch (error) { console.error("Error:", error); }
  };

  const fetchButtonConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/config`, getAuthHeaders());
      if (res.data) {
        setButtonConfig({
          book_now_button_color: res.data.book_now_button_color || "#D4AF37",
          book_now_button_style: res.data.book_now_button_style || "filled",
          send_message_button_color: res.data.send_message_button_color || "#1C1917",
          send_message_button_style: res.data.send_message_button_style || "filled"
        });
      }
      setTimeout(() => { isInitialLoadButtons.current = false; }, 500);
    } catch (error) { console.error("Error fetching button config:", error); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Typography</h1>
        <PageInfoInline pageKey="typography" />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="admin-card">
          <h3 className="font-heading admin-section-title mb-4">Font Families</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#78716C] mb-2 block">Heading Font</label>
              <Select value={settings.headingFont} onValueChange={(v) => setSettings({...settings, headingFont: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-[#78716C] mb-2 block">Body Font</label>
              <Select value={settings.bodyFont} onValueChange={(v) => setSettings({...settings, bodyFont: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FONTS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        {["h1", "h2", "h3", "paragraph"].map(type => (
          <div key={type} className="admin-card">
            <h3 className="font-heading admin-section-title mb-4">{type.toUpperCase()}</h3>
            <div className="space-y-4">
              <Input value={settings[type]?.size || ""} onChange={(e) => setSettings({...settings, [type]: {...settings[type], size: e.target.value}})} placeholder="Size (e.g., 4rem)" />
              <Select value={settings[type]?.weight || "400"} onValueChange={(v) => setSettings({...settings, [type]: {...settings[type], weight: v}})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FONT_WEIGHTS.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-2">
                <Input type="color" value={settings[type]?.color || "#1C1917"} onChange={(e) => setSettings({...settings, [type]: {...settings[type], color: e.target.value}})} className="w-12 h-10" />
                <Input value={settings[type]?.color || "#1C1917"} onChange={(e) => setSettings({...settings, [type]: {...settings[type], color: e.target.value}})} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Form Button Customization Section */}
      <div className="admin-card">
        <h3 className="font-heading admin-section-title mb-2">Form Button Customization</h3>
        <p className="text-sm text-[#78716C] mb-4">
          Customize the appearance of form buttons
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Book Now Button */}
          <div className="p-4 border border-[#D4AF37] rounded-lg">
            <h4 className="font-medium mb-3">Book Now Button</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Button Color</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color" 
                    value={buttonConfig.book_now_button_color}
                    onChange={(e) => setButtonConfig({...buttonConfig, book_now_button_color: e.target.value})}
                    className="w-10 h-10 rounded cursor-pointer border border-[#D4AF37]"
                  />
                  <Input 
                    type="text"
                    value={buttonConfig.book_now_button_color}
                    onChange={(e) => setButtonConfig({...buttonConfig, book_now_button_color: e.target.value})}
                    className="flex-1 font-mono text-sm"
                    placeholder="#D4AF37"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Button Style</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setButtonConfig({...buttonConfig, book_now_button_style: "filled"})}
                    className={`flex-1 px-3 py-2 text-sm rounded border transition-all ${
                      buttonConfig.book_now_button_style === "filled" 
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] font-medium" 
                        : "border-[#D4AF37] hover:border-[#D4AF37]/50"
                    }`}
                  >
                    Filled
                  </button>
                  <button
                    onClick={() => setButtonConfig({...buttonConfig, book_now_button_style: "outline"})}
                    className={`flex-1 px-3 py-2 text-sm rounded border transition-all ${
                      buttonConfig.book_now_button_style === "outline" 
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] font-medium" 
                        : "border-[#D4AF37] hover:border-[#D4AF37]/50"
                    }`}
                  >
                    Outline
                  </button>
                </div>
              </div>
              {/* Preview */}
              <div className="pt-2 border-t border-[#D4AF37]">
                <label className="text-xs text-[#78716C] mb-2 block">Preview</label>
                <button
                  className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                    buttonConfig.book_now_button_style === "filled"
                      ? "text-white"
                      : "bg-transparent border-2"
                  }`}
                  style={buttonConfig.book_now_button_style === "filled" 
                    ? { backgroundColor: buttonConfig.book_now_button_color }
                    : { borderColor: buttonConfig.book_now_button_color, color: buttonConfig.book_now_button_color }
                  }
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>

          {/* Send Message Button */}
          <div className="p-4 border border-[#D4AF37] rounded-lg">
            <h4 className="font-medium mb-3">Send Message Button</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Button Color</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="color" 
                    value={buttonConfig.send_message_button_color}
                    onChange={(e) => setButtonConfig({...buttonConfig, send_message_button_color: e.target.value})}
                    className="w-10 h-10 rounded cursor-pointer border border-[#D4AF37]"
                  />
                  <Input 
                    type="text"
                    value={buttonConfig.send_message_button_color}
                    onChange={(e) => setButtonConfig({...buttonConfig, send_message_button_color: e.target.value})}
                    className="flex-1 font-mono text-sm"
                    placeholder="#1C1917"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#78716C] mb-1 block">Button Style</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setButtonConfig({...buttonConfig, send_message_button_style: "filled"})}
                    className={`flex-1 px-3 py-2 text-sm rounded border transition-all ${
                      buttonConfig.send_message_button_style === "filled" 
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] font-medium" 
                        : "border-[#D4AF37] hover:border-[#D4AF37]/50"
                    }`}
                  >
                    Filled
                  </button>
                  <button
                    onClick={() => setButtonConfig({...buttonConfig, send_message_button_style: "outline"})}
                    className={`flex-1 px-3 py-2 text-sm rounded border transition-all ${
                      buttonConfig.send_message_button_style === "outline" 
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37] font-medium" 
                        : "border-[#D4AF37] hover:border-[#D4AF37]/50"
                    }`}
                  >
                    Outline
                  </button>
                </div>
              </div>
              {/* Preview */}
              <div className="pt-2 border-t border-[#D4AF37]">
                <label className="text-xs text-[#78716C] mb-2 block">Preview</label>
                <button
                  className={`px-4 py-2 rounded text-sm font-medium transition-all ${
                    buttonConfig.send_message_button_style === "filled"
                      ? "text-white"
                      : "bg-transparent border-2"
                  }`}
                  style={buttonConfig.send_message_button_style === "filled" 
                    ? { backgroundColor: buttonConfig.send_message_button_color }
                    : { borderColor: buttonConfig.send_message_button_color, color: buttonConfig.send_message_button_color }
                  }
                >
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Gallery Management
function GalleryManagement() {
  const { getAuthHeaders } = useAuth();
  const [images, setImages] = useState([]);
  const [newImage, setNewImage] = useState({ url: "", alt: { en: "", it: "" } });
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => { fetchImages(); }, []);

  const fetchImages = async () => {
    try { const res = await axios.get(`${API}/gallery`); setImages(res.data); }
    catch (error) { toast.error("Failed to fetch images"); }
  };

  const addImage = async () => {
    try {
      await axios.post(`${API}/admin/gallery`, newImage, getAuthHeaders());
      toast.success("Image added");
      setShowAddDialog(false);
      setNewImage({ url: "", alt: { en: "", it: "" } });
      fetchImages();
    } catch (error) { toast.error("Failed to add image"); }
  };

  const deleteImage = async (imageId) => {
    const confirmed = await showConfirm({
      title: "Elimina Immagine",
      message: "Stai per eliminare questa immagine dalla galleria.",
      variant: "danger",
      confirmText: "Elimina",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try { await axios.delete(`${API}/admin/gallery/${imageId}`, getAuthHeaders()); toast.success("Immagine eliminata"); fetchImages(); }
    catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Gallery</h1>
        <div className="flex items-center gap-3">
          <PageInfoInline pageKey="sections" />
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild><Button className="bg-[#D4AF37] hover:bg-[#B8962E]"><Plus size={16} className="mr-2" /> Add Image</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Image</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <Input placeholder="Image URL" value={newImage.url} onChange={(e) => setNewImage({...newImage, url: e.target.value})} />
                <Input placeholder="Alt Text (English)" value={newImage.alt.en} onChange={(e) => setNewImage({...newImage, alt: {...newImage.alt, en: e.target.value}})} />
                <Input placeholder="Alt Text (Italian)" value={newImage.alt.it} onChange={(e) => setNewImage({...newImage, alt: {...newImage.alt, it: e.target.value}})} />
                {newImage.url && <img src={newImage.url} alt="Preview" className="w-full h-40 object-cover" />}
                <Button onClick={addImage} className="w-full">Add Image</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {images.map((image) => (
          <div key={image.image_id} className="admin-card p-2">
            <div className="aspect-square overflow-hidden mb-2"><img src={image.url} alt="" className="w-full h-full object-cover" /></div>
            <Button variant="ghost" size="sm" className="w-full text-red-600" onClick={() => deleteImage(image.image_id)}><Trash2 size={14} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Legal Management
function LegalManagement() {
  const { getAuthHeaders } = useAuth();
  const [legal, setLegal] = useState({ 
    legal_info: { text: "" }, 
    policy_items: [], 
    contact_items: [], 
    copyright: { text: "" }
  });
  const [loading, setLoading] = useState(true);
  
  // Track initial load to prevent auto-save on first render
  const isInitialLoadLegal = useRef(true);
  const saveLegalTimeoutRef = useRef(null);

  useEffect(() => { fetchLegal(); }, []);
  
  // Auto-save legal (debounced)
  useEffect(() => {
    if (isInitialLoadLegal.current) return;
    
    if (saveLegalTimeoutRef.current) clearTimeout(saveLegalTimeoutRef.current);
    
    saveLegalTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/admin/legal`, legal, getAuthHeaders());
        toast.success("Contenuti legali salvati automaticamente", { duration: 2000 });
      } catch (error) { 
        toast.error("Errore nel salvataggio contenuti legali"); 
      }
    }, 1500);
    
    return () => { if (saveLegalTimeoutRef.current) clearTimeout(saveLegalTimeoutRef.current); };
  }, [legal, getAuthHeaders]);

  const fetchLegal = async () => {
    setLoading(true);
    try { 
      const res = await axios.get(`${API}/legal`); 
      // Migrate old format to new format if needed
      // Priority: text > longest content (to preserve real content over placeholders)
      const data = res.data || {};
      
      // Helper to get best content (longest non-empty string)
      const getBestContent = (obj) => {
        if (!obj) return "";
        if (obj.text) return obj.text;
        // Get all string values and return the longest (real content, not placeholder)
        const values = [obj.it, obj.en, obj.es, obj.fr, obj.de].filter(v => typeof v === 'string' && v.length > 0);
        if (values.length === 0) return "";
        return values.reduce((a, b) => (a.length > b.length ? a : b), "");
      };
      
      const migrated = {
        legal_info: { text: getBestContent(data.legal_info) },
        policy_items: (data.policy_items || []).map(item => ({
          ...item,
          label: { text: getBestContent(item.label) },
          content: { text: getBestContent(item.content) }
        })),
        contact_items: (data.contact_items || []).map(item => ({
          ...item,
          label: { text: getBestContent(item.label) },
          content: { text: getBestContent(item.content) }
        })),
        copyright: { text: getBestContent(data.copyright) }
      };
      setLegal(migrated); 
      setTimeout(() => { isInitialLoadLegal.current = false; }, 500);
    }
    catch (error) { 
      console.error("Error loading legal data:", error); 
      toast.error("Errore nel caricamento dei dati Legal");
    }
    finally {
      setLoading(false);
    }
  };

  // Policy items management
  const addPolicyItem = () => {
    const newItem = { id: Date.now().toString(), label: { text: "" }, content: { text: "" } };
    setLegal({...legal, policy_items: [...(legal.policy_items || []), newItem]});
  };

  const updatePolicyItem = (itemId, key, value) => {
    const updatedItems = (legal.policy_items || []).map(item => {
      if (item.id === itemId) {
        return {...item, [key]: { text: value }};
      }
      return item;
    });
    setLegal({...legal, policy_items: updatedItems});
  };

  const removePolicyItem = (itemId) => {
    const updatedItems = (legal.policy_items || []).filter(item => item.id !== itemId);
    setLegal({...legal, policy_items: updatedItems});
  };

  // Contact items management
  const addContactItem = () => {
    const newItem = { id: Date.now().toString(), label: { text: "" }, content: { text: "" } };
    setLegal({...legal, contact_items: [...(legal.contact_items || []), newItem]});
  };

  const updateContactItem = (itemId, key, value) => {
    const updatedItems = (legal.contact_items || []).map(item => {
      if (item.id === itemId) {
        return {...item, [key]: { text: value }};
      }
      return item;
    });
    setLegal({...legal, contact_items: updatedItems});
  };

  const removeContactItem = (itemId) => {
    const updatedItems = (legal.contact_items || []).filter(item => item.id !== itemId);
    setLegal({...legal, contact_items: updatedItems});
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Legal</h1>
        <PageInfoInline pageKey="legal" />
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={32} className="animate-spin text-[#D4AF37]" />
          <span className="ml-3 text-[#78716C]">Caricamento dati Legal...</span>
        </div>
      ) : (
      <>
      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800 font-medium mb-2">Struttura Footer</p>
        <div className="text-xs text-blue-700 grid grid-cols-3 gap-4">
          <div><strong>Sinistra:</strong> Legal Information (sotto il logo)</div>
          <div><strong>Centro:</strong> Policy (voci cliccabili)</div>
          <div><strong>Destra:</strong> Contact (voci cliccabili)</div>
        </div>
      </div>

      {/* 1) Legal Information - Left column */}
      <div className="admin-card">
        <h3 className="font-heading admin-section-title mb-2">Legal Information</h3>
        <p className="text-sm text-[#78716C] mb-4">
          Contenuto mostrato nel footer (colonna sinistra, sotto il logo). I caratteri a capo saranno rispettati.
        </p>
        {/* Toolbar for formatting */}
        <div className="flex gap-2 mb-2">
          <button
            type="button"
            onClick={() => {
              const textarea = document.getElementById('legal-info-textarea');
              if (!textarea) return;
              const start = textarea.selectionStart;
              const end = textarea.selectionEnd;
              const text = textarea.value;
              const selectedText = text.substring(start, end);
              if (!selectedText) { toast.error("Seleziona prima il testo"); return; }
              const newText = text.substring(0, start) + `<strong>${selectedText}</strong>` + text.substring(end);
              setLegal({...legal, legal_info: { text: newText }});
            }}
            className="px-3 py-1 text-sm font-bold bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
            title="Grassetto"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => {
              const url = prompt("Inserisci URL del link:", "https://");
              if (!url) return;
              const textarea = document.getElementById('legal-info-textarea');
              const start = textarea.selectionStart;
              const end = textarea.selectionEnd;
              const text = textarea.value;
              const selectedText = text.substring(start, end) || prompt("Inserisci testo del link:", "clicca qui");
              if (!selectedText) return;
              const newText = text.substring(0, start) + `<a href="${url}">${selectedText}</a>` + text.substring(end);
              setLegal({...legal, legal_info: { text: newText }});
            }}
            className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
            title="Inserisci link"
          >
            🔗 Link
          </button>
        </div>
        <Textarea 
          id="legal-info-textarea"
          rows={8} 
          value={legal.legal_info?.text || ""} 
          onChange={(e) => setLegal({...legal, legal_info: { text: e.target.value }})} 
          placeholder="Es: SUITE LUCINA – CASA E APPARTAMENTO PER VACANZE&#10;Piazza di San Lorenzo in Lucina, 21&#10;CAP 00186 – Roma (RM)" 
        />
      </div>

      {/* 2) Policy - Center column */}
      <div className="admin-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading admin-section-title mb-1">Policy</h3>
            <p className="text-sm text-[#78716C]">Voci mostrate nel footer (colonna centrale). La label è il link, il contenuto appare nella pagina dedicata.</p>
          </div>
          <Button onClick={addPolicyItem} className="bg-[#1C1917] hover:bg-[#292524] text-white">
            <Plus size={16} className="mr-2" /> Aggiungi Voce
          </Button>
        </div>
        
        {(legal.policy_items || []).length === 0 ? (
          <p className="text-sm text-[#A8A29E] italic py-4 text-center border border-dashed border-[#D4AF37] rounded-lg">
            Nessuna voce policy. Clicca "Aggiungi Voce" per crearne una.
          </p>
        ) : (
          <div className="space-y-4">
            {(legal.policy_items || []).map((item, index) => (
              <div key={item.id} className="p-4 border border-[#D4AF37] rounded-lg bg-[#F9F8F6]">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-[#44403C]">Policy {index + 1}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removePolicyItem(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Label (link nel footer)</label>
                    <Input 
                      value={item.label?.text || ""} 
                      onChange={(e) => updatePolicyItem(item.id, 'label', e.target.value)}
                      placeholder="Es: Privacy Policy"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Contenuto (pagina dedicata)</label>
                    {/* Toolbar for formatting */}
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById(`policy-content-${item.id}`);
                          if (!textarea) return;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = textarea.value;
                          const selectedText = text.substring(start, end);
                          if (!selectedText) { toast.error("Seleziona prima il testo"); return; }
                          const newText = text.substring(0, start) + `<strong>${selectedText}</strong>` + text.substring(end);
                          updatePolicyItem(item.id, 'content', newText);
                        }}
                        className="px-2 py-0.5 text-xs font-bold bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                        title="Grassetto"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const url = prompt("Inserisci URL del link:", "https://");
                          if (!url) return;
                          const textarea = document.getElementById(`policy-content-${item.id}`);
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = textarea.value;
                          const selectedText = text.substring(start, end) || prompt("Inserisci testo del link:", "clicca qui");
                          if (!selectedText) return;
                          const newText = text.substring(0, start) + `<a href="${url}">${selectedText}</a>` + text.substring(end);
                          updatePolicyItem(item.id, 'content', newText);
                        }}
                        className="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                        title="Inserisci link"
                      >
                        🔗
                      </button>
                    </div>
                    <Textarea 
                      id={`policy-content-${item.id}`}
                      rows={4}
                      value={item.content?.text || ""} 
                      onChange={(e) => updatePolicyItem(item.id, 'content', e.target.value)}
                      placeholder="Contenuto completo della policy..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3) Contact - Right column */}
      <div className="admin-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading admin-section-title mb-1">Contact</h3>
            <p className="text-sm text-[#78716C]">Voci mostrate nel footer (colonna destra). La label è il link, il contenuto appare nella pagina dedicata.</p>
          </div>
          <Button onClick={addContactItem} className="bg-[#1C1917] hover:bg-[#292524] text-white">
            <Plus size={16} className="mr-2" /> Aggiungi Voce
          </Button>
        </div>
        
        {(legal.contact_items || []).length === 0 ? (
          <p className="text-sm text-[#A8A29E] italic py-4 text-center border border-dashed border-[#D4AF37] rounded-lg">
            Nessuna voce contact. Clicca "Aggiungi Voce" per crearne una.
          </p>
        ) : (
          <div className="space-y-4">
            {(legal.contact_items || []).map((item, index) => (
              <div key={item.id} className="p-4 border border-[#D4AF37] rounded-lg bg-[#F9F8F6]">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-[#44403C]">Contact {index + 1}</span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeContactItem(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Label (link nel footer)</label>
                    <Input 
                      value={item.label?.text || ""} 
                      onChange={(e) => updateContactItem(item.id, 'label', e.target.value)}
                      placeholder="Es: Richiedi Prenotazione"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#78716C] mb-1 block">Contenuto (pagina dedicata)</label>
                    {/* Toolbar for formatting */}
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          const textarea = document.getElementById(`contact-content-${item.id}`);
                          if (!textarea) return;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = textarea.value;
                          const selectedText = text.substring(start, end);
                          if (!selectedText) { toast.error("Seleziona prima il testo"); return; }
                          const newText = text.substring(0, start) + `<strong>${selectedText}</strong>` + text.substring(end);
                          updateContactItem(item.id, 'content', newText);
                        }}
                        className="px-2 py-0.5 text-xs font-bold bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                        title="Grassetto"
                      >
                        B
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const url = prompt("Inserisci URL del link:", "https://");
                          if (!url) return;
                          const textarea = document.getElementById(`contact-content-${item.id}`);
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = textarea.value;
                          const selectedText = text.substring(start, end) || prompt("Inserisci testo del link:", "clicca qui");
                          if (!selectedText) return;
                          const newText = text.substring(0, start) + `<a href="${url}">${selectedText}</a>` + text.substring(end);
                          updateContactItem(item.id, 'content', newText);
                        }}
                        className="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                        title="Inserisci link"
                      >
                        🔗
                      </button>
                    </div>
                    <Textarea 
                      id={`contact-content-${item.id}`}
                      rows={4}
                      value={item.content?.text || ""} 
                      onChange={(e) => updateContactItem(item.id, 'content', e.target.value)}
                      placeholder="Contenuto completo della pagina contact..."
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Copyright */}
      <div className="admin-card">
        <h3 className="font-heading admin-section-title mb-2">Copyright</h3>
        <p className="text-sm text-[#78716C] mb-4">
          Testo del copyright nel footer (in basso al centro). L'anno è gestito automaticamente dal sistema.
        </p>
        
        {/* Anno automatico - non modificabile */}
        <div className="flex items-center gap-3 mb-4 p-3 bg-[#F5F5F4] rounded-lg border border-[#D4AF37]">
          <span className="text-sm text-[#78716C]">Anno:</span>
          <span className="px-3 py-1 bg-[#1C1917] text-white rounded font-mono font-bold">{new Date().getFullYear()}</span>
          <span className="text-xs text-[#A8A29E]">(aggiornato automaticamente ogni anno)</span>
        </div>
        
        {/* Solo testo personalizzabile - senza formattazione */}
        <label className="text-sm font-medium text-[#44403C] mb-2 block">Testo copyright:</label>
        <Input 
          id="copyright-textarea"
          value={legal.copyright?.text || ""} 
          onChange={(e) => setLegal({...legal, copyright: { text: e.target.value }})} 
          placeholder='All rights reserved.' 
        />
        
        {/* Preview */}
        <div className="mt-4 p-3 bg-[#1C1917] rounded-lg">
          <p className="text-xs text-[#78716C] mb-1">Anteprima nel footer:</p>
          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} {(legal.copyright?.text || "").trim() || "All rights reserved."}
          </p>
        </div>
        
        <p className="text-xs text-[#A8A29E] mt-2">Se il campo è vuoto, verrà mostrato: © {new Date().getFullYear()} All rights reserved.</p>
      </div>
      </>
      )}
    </div>
  );
}

// Email Logs & Booking Requests
function EmailLogs({ badgeCounts = {}, markAsVisited = () => {} }) {
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "email-customer";
  const [bookings, setBookings] = useState([]);
  const [logs, setLogs] = useState([]);
  const [emailSettings, setEmailSettings] = useState({ notification_email: "" });
  const [holdConfig, setHoldConfig] = useState({ hold_duration_minutes: 30 });
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingHold, setSavingHold] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [bookingConfirmModal, setBookingConfirmModal] = useState({ isOpen: false, newState: null });
  const [statusFilter, setStatusFilter] = useState("all");
  const [logsStatusFilter, setLogsStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  
  // Form Logs State
  const [formLogs, setFormLogs] = useState([]);
  const [loadingFormLogs, setLoadingFormLogs] = useState(false);
  const [selectedFormLogs, setSelectedFormLogs] = useState([]);
  const [formLogsSearch, setFormLogsSearch] = useState("");
  const [formLogsTypeFilter, setFormLogsTypeFilter] = useState("all");
  const [formLogsStatusFilter, setFormLogsStatusFilter] = useState("all");
  const [formLogsYear, setFormLogsYear] = useState("");
  
  // Email Templates State
  const [emailTemplates, setEmailTemplates] = useState({
    customer: { subject: "", body: "" },
    admin: { subject: "", body: "" }
  });
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState("customer");

  // Badge component for tabs
  const TabBadge = ({ count }) => {
    if (!count || count <= 0) return null;
    return (
      <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#D4AF37] rounded-full">
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  // Mark tab as visited when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "form-logs") {
      markAsVisited("logs");
    }
  };

  // Mark as visited on initial load based on default tab
  useEffect(() => {
    // Don't mark as visited on initial load
  }, []);
  
  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({length: 6}, (_, i) => currentYear - i);

  useEffect(() => { fetchData(); fetchEmailTemplates(); }, []);

  // Fetch email templates
  const fetchEmailTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`, getAuthHeaders());
      setEmailTemplates(res.data || {
        customer: { subject: "", body: "" },
        admin: { subject: "", body: "" }
      });
    } catch (error) {
      console.error("Error fetching email templates:", error);
    }
  };

  // Save email templates
  const saveEmailTemplates = async () => {
    setSavingTemplates(true);
    try {
      await axios.put(`${API}/admin/email-templates`, emailTemplates, getAuthHeaders());
      toast.success("Template email salvati con successo");
    } catch (error) {
      toast.error("Errore nel salvataggio dei template");
    } finally {
      setSavingTemplates(false);
    }
  };

  // Preview email template
  const previewEmailTemplate = async (type) => {
    setPreviewType(type);
    try {
      const res = await axios.post(`${API}/admin/email-templates/preview?template_type=${type}`, {}, getAuthHeaders());
      setPreviewData(res.data);
      setShowPreview(true);
    } catch (error) {
      toast.error("Errore nella generazione preview");
    }
  };

  // Fetch Form Logs
  const fetchFormLogs = async () => {
    setLoadingFormLogs(true);
    try {
      const res = await axios.get(`${API}/admin/email-logs`, getAuthHeaders());
      setFormLogs(res.data || []);
    } catch (error) {
      console.error("Error fetching form logs:", error);
      toast.error("Errore nel caricamento dei log");
    } finally {
      setLoadingFormLogs(false);
    }
  };

  // Delete single form log
  const deleteFormLog = async (logId, logDetails = '') => {
    const confirmed = await showConfirm({
      title: "Elimina Log",
      message: "Stai per eliminare questo log.",
      details: logDetails || null,
      variant: "danger",
      confirmText: "Elimina",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/email-logs/${logId}`, getAuthHeaders());
      toast.success("Log eliminato");
      fetchFormLogs();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Delete selected form logs (bulk)
  const deleteSelectedFormLogs = async () => {
    if (selectedFormLogs.length === 0) return;
    const confirmed = await showConfirm({
      title: "Elimina Log Selezionati",
      message: `Stai per eliminare ${selectedFormLogs.length} log email.`,
      details: "⚠️ Questa azione è IRREVERSIBILE e i dati verranno eliminati definitivamente dal database.",
      variant: "danger",
      confirmText: "Elimina Definitivamente",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/email-logs/bulk`, {
        ...getAuthHeaders(),
        data: { log_ids: selectedFormLogs }
      });
      toast.success(`${selectedFormLogs.length} log eliminati definitivamente`);
      setSelectedFormLogs([]);
      fetchFormLogs();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Toggle form log selection
  const toggleFormLog = (logId) => {
    setSelectedFormLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  // Load form logs when tab changes
  useEffect(() => {
    if (activeTab === "form-logs") {
      fetchFormLogs();
    }
  }, [activeTab]);

  // Debounce search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter bookings by status, search and year
  const filteredBookings = useMemo(() => {
    let filtered = bookings;
    
    // Apply status filter
    if (statusFilter === "message") {
      filtered = filtered.filter(b => b.status === "inquiry" || b.status === "message" || !b.hold_id);
    } else if (statusFilter !== "all") {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    // Apply year filter
    if (yearFilter) {
      filtered = filtered.filter(b => {
        const createdAt = b.created_at || b.submitted_at || "";
        return createdAt.startsWith(yearFilter);
      });
    }
    
    // Apply search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim();
      filtered = filtered.filter(b => {
        const name = String(b.name || "").toLowerCase();
        const email = String(b.email || "").toLowerCase();
        const bookingNumber = String(b.booking_number || "").toLowerCase();
        const requestId = String(b.request_id || "").toLowerCase();
        const message = String(b.message || "").toLowerCase();
        const checkIn = String(b.check_in || "").toLowerCase();
        const checkOut = String(b.check_out || "").toLowerCase();
        const createdAt = String(b.created_at || "").toLowerCase();
        
        return name.includes(query) ||
               email.includes(query) ||
               bookingNumber.includes(query) ||
               requestId.includes(query) ||
               message.includes(query) ||
               checkIn.includes(query) ||
               checkOut.includes(query) ||
               createdAt.includes(query);
      });
    }
    
    return filtered;
  }, [bookings, statusFilter, debouncedSearch, yearFilter]);
  
  // Filter logs by year and status
  const filteredLogs = useMemo(() => {
    let filtered = logs;
    
    // Apply status filter
    if (logsStatusFilter !== "all") {
      filtered = filtered.filter(log => log.status === logsStatusFilter);
    }
    
    // Apply year filter
    if (yearFilter) {
      filtered = filtered.filter(log => {
        const timestamp = log.timestamp || log.sent_at || "";
        return timestamp.startsWith(yearFilter);
      });
    }
    
    return filtered;
  }, [logs, logsStatusFilter, yearFilter]);
  
  // Export Tracking to CSV
  const exportTrackingCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (yearFilter) params.append("year", yearFilter);
      const res = await axios.get(`${API}/admin/bookings/export/csv?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Tracking_SuiteLucina_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };
  
  // Export Logs to CSV
  const exportLogsCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (logsStatusFilter !== "all") params.append("status", logsStatusFilter);
      if (yearFilter) params.append("year", yearFilter);
      const res = await axios.get(`${API}/admin/email-logs/export/csv?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Logs_SuiteLucina_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  const fetchData = async () => {
    try {
      const [logsRes, bookingsRes, settingsRes, configRes] = await Promise.all([
        axios.get(`${API}/admin/email-logs`, getAuthHeaders()),
        axios.get(`${API}/admin/bookings`, getAuthHeaders()),
        axios.get(`${API}/admin/email-settings`, getAuthHeaders()),
        axios.get(`${API}/admin/calendar/config`, getAuthHeaders())
      ]);
      setLogs(logsRes.data);
      setBookings(bookingsRes.data);
      setEmailSettings(settingsRes.data);
      if (configRes.data) {
        setHoldConfig({ hold_duration_minutes: configRes.data.hold_duration_minutes || 30 });
        setBookingEnabled(configRes.data.booking_enabled !== false);
      }
    } catch (error) { toast.error("Failed to fetch"); }
  };

  const saveEmailSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/email-settings`, emailSettings, getAuthHeaders());
      toast.success("Email settings saved");
    } catch (error) { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const saveHoldSettings = async () => {
    setSavingHold(true);
    try {
      await axios.put(`${API}/admin/calendar/config`, { hold_duration_minutes: holdConfig.hold_duration_minutes }, getAuthHeaders());
      toast.success("Hold settings saved");
    } catch (error) { toast.error("Failed to save hold settings"); }
    finally { setSavingHold(false); }
  };

  // Apre il modale di conferma per cambiare lo stato delle prenotazioni
  const handleBookingToggleClick = () => {
    setBookingConfirmModal({ isOpen: true, newState: !bookingEnabled });
  };

  // Conferma il cambio di stato delle prenotazioni
  const confirmBookingToggle = async () => {
    const newState = bookingConfirmModal.newState;
    setBookingConfirmModal({ isOpen: false, newState: null });
    setSavingBooking(true);
    try {
      await axios.put(`${API}/admin/calendar/config`, { booking_enabled: newState }, getAuthHeaders());
      setBookingEnabled(newState);
      toast.success(newState ? "Prenotazioni online attivate" : "Prenotazioni online disattivate");
    } catch (error) { toast.error("Errore nel salvare le impostazioni"); }
    finally { setSavingBooking(false); }
  };

  const updateBookingStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${requestId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) { toast.error("Failed to update booking"); }
  };

  const deleteBooking = async (id) => {
    const confirmed = await showConfirm({
      title: "Elimina Prenotazione",
      message: "Stai per eliminare questa prenotazione.",
      variant: "danger",
      confirmText: "Elimina",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, getAuthHeaders());
      toast.success("Prenotazione eliminata");
      fetchData();
    } catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  const deleteSelectedBookings = async () => {
    if (selectedBookings.length === 0) return;
    const confirmed = await showConfirm({
      title: "Elimina Prenotazioni Selezionate",
      message: `Stai per eliminare ${selectedBookings.length} prenotazioni.`,
      details: "⚠️ Questa azione è IRREVERSIBILE.",
      variant: "danger",
      confirmText: "Elimina Definitivamente",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      // Delete one by one since bulk delete for bookings may not exist
      for (const id of selectedBookings) {
        await axios.delete(`${API}/admin/bookings/${id}`, getAuthHeaders());
      }
      toast.success(`${selectedBookings.length} prenotazioni eliminate`);
      setSelectedBookings([]);
      fetchData();
    } catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  const deleteLog = async (id) => {
    const confirmed = await showConfirm({
      title: "Elimina Log",
      message: "Stai per eliminare questo log.",
      variant: "danger",
      confirmText: "Elimina",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/email-logs/${id}`, getAuthHeaders());
      toast.success("Log eliminato");
      fetchData();
    } catch (error) { toast.error("Errore nell'eliminazione"); }
  };

  const deleteSelectedLogs = async () => {
    if (selectedLogs.length === 0) return;
    const confirmed = await showConfirm({
      title: "Elimina Log Selezionati",
      message: `Stai per eliminare ${selectedLogs.length} log email.`,
      details: "⚠️ Questa azione è IRREVERSIBILE e i dati verranno eliminati definitivamente dal database.",
      variant: "danger",
      confirmText: "Elimina Definitivamente",
      cancelText: "Annulla"
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/email-logs`, { ...getAuthHeaders(), data: selectedLogs });
      toast.success(`${selectedLogs.length} log eliminati definitivamente`);
      setSelectedLogs([]);
      fetchData();
    } catch (error) { 
      toast.error("Errore nell'eliminazione"); 
    }
  };

  const toggleBooking = (id) => {
    setSelectedBookings(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleLog = (id) => {
    setSelectedLogs(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleAllBookings = () => {
    setSelectedBookings(prev => prev.length === filteredBookings.length ? [] : filteredBookings.map(b => b.request_id));
  };

  const toggleAllLogs = () => {
    setSelectedLogs(prev => prev.length === filteredLogs.length ? [] : filteredLogs.map(l => l.log_id));
  };

  return (
    <div className="space-y-8">
      <h1 className="font-heading text-3xl">Email Form</h1>
      <Tabs value={activeTab} onValueChange={handleTabChange} defaultValue="email-customer">
        <TabsList className="admin-tabs-list">
          <TabsTrigger value="email-customer" className="admin-tab-trigger flex items-center">
            <Mail size={14} className="mr-1" /> E-mail Customer Payment
          </TabsTrigger>
          <TabsTrigger value="email-admin" className="admin-tab-trigger flex items-center">
            <Mail size={14} className="mr-1" /> E-mail Admin Payment
          </TabsTrigger>
          <TabsTrigger value="email-customer-message" className="admin-tab-trigger flex items-center">
            <Mail size={14} className="mr-1" /> E-mail Customer Message
          </TabsTrigger>
          <TabsTrigger value="email-admin-message" className="admin-tab-trigger flex items-center">
            <Mail size={14} className="mr-1" /> E-mail Admin Message
          </TabsTrigger>
          <TabsTrigger value="settings" className="admin-tab-trigger flex items-center">
            <Settings size={14} className="mr-1" /> Settings
          </TabsTrigger>
        </TabsList>

        {/* Info sotto i tabs - cambia in base al tab attivo */}
        <PageInfoUnderTabs pageKey={`email_form_${activeTab === 'email-customer' ? 'customer_payment' : activeTab === 'email-admin' ? 'admin_payment' : activeTab === 'email-customer-message' ? 'customer_message' : activeTab === 'email-admin-message' ? 'admin_message' : 'settings'}`} />

        {/* E-mail to Customer Payment Tab */}
        <TabsContent value="email-customer" className="mt-6 space-y-6">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading admin-section-title">E-mail Customer Payment</h3>
                <p className="text-sm text-[#78716C] mt-1">
                  Configura l'email inviata automaticamente al cliente dopo il pagamento completato (Form e Payment Link).
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => previewEmailTemplate("customer")}>
                  <Eye size={16} className="mr-2" /> Preview
                </Button>
                <Button onClick={saveEmailTemplates} disabled={savingTemplates} className="bg-[#D4AF37] hover:bg-[#B8972E] text-white">
                  {savingTemplates ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  Salva Template
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Oggetto Email</label>
                <Input 
                  placeholder="Conferma Pagamento - Suite Lucina"
                  value={emailTemplates.customer?.subject || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    customer: { ...emailTemplates.customer, subject: e.target.value }
                  })}
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Variabili disponibili: {"{customer_name}"}, {"{booking_number}"}, {"{check_in}"}, {"{check_out}"}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Contenuto Email (Testo Personalizzato)</label>
                <Textarea 
                  placeholder="Gentile {customer_name},&#10;&#10;Grazie per aver completato il pagamento..."
                  value={emailTemplates.customer?.body || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    customer: { ...emailTemplates.customer, body: e.target.value }
                  })}
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Questo testo apparirà come prima parte dell'email. Il riepilogo prenotazione verrà aggiunto automaticamente.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* E-mail Admin Payment Tab */}
        <TabsContent value="email-admin" className="mt-6 space-y-6">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading admin-section-title">E-mail Admin Payment</h3>
                <p className="text-sm text-[#78716C] mt-1">
                  Configura l'email inviata automaticamente all'amministrazione dopo ogni pagamento (Form e Payment Link).
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => previewEmailTemplate("admin")}>
                  <Eye size={16} className="mr-2" /> Preview
                </Button>
                <Button onClick={saveEmailTemplates} disabled={savingTemplates} className="bg-[#D4AF37] hover:bg-[#B8972E] text-white">
                  {savingTemplates ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  Salva Template
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Oggetto Email</label>
                <Input 
                  placeholder="Nuovo Pagamento Ricevuto - Suite Lucina"
                  value={emailTemplates.admin?.subject || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    admin: { ...emailTemplates.admin, subject: e.target.value }
                  })}
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Variabili disponibili: {"{customer_name}"}, {"{booking_number}"}, {"{grand_total}"}, {"{check_in}"}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Contenuto Email (Testo Personalizzato)</label>
                <Textarea 
                  placeholder="È stato ricevuto un nuovo pagamento per una prenotazione.&#10;&#10;Di seguito i dettagli..."
                  value={emailTemplates.admin?.body || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    admin: { ...emailTemplates.admin, body: e.target.value }
                  })}
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Questo testo apparirà come prima parte dell'email. Il riepilogo prenotazione verrà aggiunto automaticamente.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* E-mail Customer Message Tab */}
        <TabsContent value="email-customer-message" className="mt-6 space-y-6">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading admin-section-title">E-mail Customer Message</h3>
                <p className="text-sm text-[#78716C] mt-1">
                  Configura l'email inviata automaticamente al cliente quando invia un messaggio tramite il Form.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => previewEmailTemplate("customer_message")}>
                  <Eye size={16} className="mr-2" /> Preview
                </Button>
                <Button onClick={saveEmailTemplates} disabled={savingTemplates} className="bg-[#D4AF37] hover:bg-[#B8972E] text-white">
                  {savingTemplates ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  Salva Template
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Oggetto Email</label>
                <Input 
                  placeholder="Grazie per il tuo messaggio - Suite Lucina"
                  value={emailTemplates.customer_message?.subject || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    customer_message: { ...emailTemplates.customer_message, subject: e.target.value }
                  })}
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Variabili disponibili: {"{customer_name}"}, {"{check_in}"}, {"{check_out}"}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Contenuto Email (Messaggio Personalizzato)</label>
                <Textarea 
                  placeholder="Gentile {customer_name},&#10;&#10;Grazie per averci contattato. Abbiamo ricevuto la tua richiesta e ti risponderemo il prima possibile.&#10;&#10;Di seguito trovi un riepilogo della tua richiesta..."
                  value={emailTemplates.customer_message?.body || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    customer_message: { ...emailTemplates.customer_message, body: e.target.value }
                  })}
                  rows={8}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Questo testo apparirà come prima parte dell'email. Il riepilogo della richiesta verrà aggiunto automaticamente.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* E-mail Admin Message Tab */}
        <TabsContent value="email-admin-message" className="mt-6 space-y-6">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-heading admin-section-title">E-mail Admin Message</h3>
                <p className="text-sm text-[#78716C] mt-1">
                  Configura l'email inviata all'amministrazione quando un cliente invia un messaggio tramite il Form.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => previewEmailTemplate("admin_message")}>
                  <Eye size={16} className="mr-2" /> Preview
                </Button>
                <Button onClick={saveEmailTemplates} disabled={savingTemplates} className="bg-[#D4AF37] hover:bg-[#B8972E] text-white">
                  {savingTemplates ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  Salva Template
                </Button>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Oggetto Email</label>
                <Input 
                  placeholder="Nuovo messaggio da {customer_name} - Suite Lucina"
                  value={emailTemplates.admin_message?.subject || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    admin_message: { ...emailTemplates.admin_message, subject: e.target.value }
                  })}
                />
                <p className="text-xs text-[#A8A29E] mt-1">
                  Variabili disponibili: {"{customer_name}"}, {"{customer_email}"}, {"{check_in}"}, {"{check_out}"}
                </p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#44403C] mb-1 block">Contenuto Email (Notifica Admin)</label>
                <Textarea 
                  placeholder="Nuovo messaggio ricevuto da {customer_name}.&#10;&#10;Di seguito trovi i dettagli completi della richiesta..."
                  value={emailTemplates.admin_message?.body || ""}
                  onChange={(e) => setEmailTemplates({
                    ...emailTemplates,
                    admin_message: { ...emailTemplates.admin_message, body: e.target.value }
                  })}
                  rows={6}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          {/* Online Booking Settings Section - Primary Control */}
          <div className="admin-card border-2 border-[#D4AF37]/50 bg-gradient-to-r from-[#FFFDF7] to-white">
            <h3 className="font-heading admin-section-title mb-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-[#D4AF37] rounded-full"></span>
              Abilita Prenotazioni Online
            </h3>
            <p className="text-sm text-[#78716C] mb-4">
              Controlla la visibilità del pulsante "Prenota Ora" nel form di prenotazione pubblico.
            </p>
            
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#D4AF37]">
              <div>
                <label className="font-medium text-[#1C1917] block">Prenotazioni Online</label>
                <p className="text-sm text-[#78716C]">Consenti ai clienti di prenotare e pagare direttamente sul sito</p>
              </div>
              {/* Custom Toggle with visible white thumb */}
              <button
                type="button"
                role="switch"
                aria-checked={bookingEnabled}
                disabled={savingBooking}
                onClick={handleBookingToggleClick}
                className={`relative inline-flex h-7 w-14 flex-shrink-0 cursor-pointer rounded-full border-2 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 ${
                  bookingEnabled 
                    ? 'bg-[#D4AF37] border-[#D4AF37]' 
                    : 'bg-gray-200 border-gray-300'
                } ${savingBooking ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span 
                  className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg border border-gray-200 transition-transform duration-200 ease-in-out ${
                    bookingEnabled ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            
            {!bookingEnabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Prenotazioni online disattivate.</strong> I clienti possono solo inviare messaggi. Il pulsante "Prenota Ora" è nascosto nel form pubblico.
                </p>
              </div>
            )}
            
            {bookingEnabled && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-green-800">
                  <strong>✅ Prenotazioni online attive.</strong> I clienti possono prenotare e pagare direttamente tramite il form pubblico.
                </p>
              </div>
            )}
          </div>

          {/* Notification Email Section */}
          <div className="admin-card">
            <h3 className="font-heading admin-section-title mb-2">Notification Email</h3>
            <p className="text-sm text-[#78716C] mb-4">All booking requests and admin notifications will be sent to this email address.</p>
            <Input 
              type="email" 
              placeholder="suitelucina@gmail.com" 
              value={emailSettings.notification_email || ""} 
              onChange={(e) => setEmailSettings({...emailSettings, notification_email: e.target.value})}
            />
            <Button onClick={saveEmailSettings} disabled={saving} className="bg-[#1C1917] hover:bg-[#292524] text-white mt-4">
              {saving ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Save Email Settings
            </Button>
          </div>

          {/* Hold Settings Section */}
          <div className="admin-card">
            <h3 className="font-heading admin-section-title mb-2">Hold Settings</h3>
            <p className="text-sm text-[#78716C] mb-4">
              Temporary date blocking during booking process
            </p>
            <div>
              <label className="text-sm text-[#78716C] mb-2 block">Hold Duration (minutes)</label>
              <Input 
                type="number" 
                value={holdConfig.hold_duration_minutes} 
                onChange={(e) => setHoldConfig({...holdConfig, hold_duration_minutes: parseInt(e.target.value) || 30})} 
              />
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-amber-800">
                  <strong>How it works:</strong> Defines how long the selected dates are temporarily blocked 
                  after a booking request is started via <strong>"Book now"</strong>. 
                  This prevents double bookings while the user completes the reservation or payment.
                </p>
                <p className="text-xs text-amber-700 mt-2">
                  <em>Note: Message-only inquiries ("Invia messaggio") do not block availability.</em>
                </p>
              </div>
            </div>
            <Button onClick={saveHoldSettings} disabled={savingHold} className="bg-[#1C1917] hover:bg-[#292524] text-white mt-4">
              {savingHold ? <RefreshCw size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
              Save Hold Settings
            </Button>
          </div>
        </TabsContent>

        {/* Booking Requests Tab - Removed */}

      </Tabs>

      {/* Email Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-[#D4AF37] flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl">
                  Preview: {
                    previewType === "customer" ? "E-mail Customer Payment" : 
                    previewType === "admin" ? "E-mail Admin Payment" :
                    previewType === "customer_message" ? "E-mail Customer Message" :
                    previewType === "admin_message" ? "E-mail Admin Message" :
                    "E-mail Preview"
                  }
                </h2>
                <p className="text-sm text-[#78716C] mt-1">Anteprima con dati di esempio</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <X size={20} />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4">
                <label className="text-xs font-medium text-[#78716C] uppercase">Oggetto</label>
                <div className="mt-1 p-3 bg-[#F9F8F6] rounded-lg font-medium">
                  {previewData.subject}
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-[#78716C] uppercase">Contenuto Email</label>
                <div className="mt-1 p-4 bg-[#F9F8F6] rounded-lg">
                  <div className="whitespace-pre-wrap text-sm text-[#44403C] mb-4">
                    {previewData.body}
                  </div>
                  <hr className="border-[#D4AF37] my-4" />
                  <div className="font-mono text-xs text-[#44403C] whitespace-pre-wrap bg-white p-4 rounded-lg border border-[#D4AF37]">
                    {previewData.summary}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-[#D4AF37] bg-[#F9F8F6]">
              <p className="text-xs text-[#78716C]">
                <strong>Nota:</strong> I dati mostrati sono di esempio. Nell'email reale verranno inseriti i dati effettivi della prenotazione.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Booking Toggle */}
      <ConfirmModal
        isOpen={bookingConfirmModal.isOpen}
        onClose={() => setBookingConfirmModal({ isOpen: false, newState: null })}
        onConfirm={confirmBookingToggle}
        title={bookingConfirmModal.newState ? "Attivare Prenotazioni Online?" : "Disattivare Prenotazioni Online?"}
        message={bookingConfirmModal.newState 
          ? "Il pulsante \"Prenota Ora\" sarà visibile nel form pubblico. I clienti potranno prenotare e pagare direttamente."
          : "Il pulsante \"Prenota Ora\" sarà nascosto dal form pubblico. I clienti potranno solo inviare messaggi."}
        confirmText={bookingConfirmModal.newState ? "Attiva" : "Disattiva"}
        cancelText="Annulla"
        variant={bookingConfirmModal.newState ? "success" : "warning"}
        loading={savingBooking}
      />
    </div>
  );
}

// Account Settings
// ========================
// PAYMENTS MANAGEMENT
// ========================
function PaymentsManagement({ badgeCounts = {}, markAsVisited = () => {}, showConfirm = null, emailServiceWarning = false }) {
  const { getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "transactions";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Format date with time for logs - uses centralized utility
  const formatDateTime = (dateStr) => {
    return formatDisplayDateTime(dateStr);
  };

  // Badge component for tabs
  const TabBadge = ({ count }) => {
    if (!count || count <= 0) return null;
    return (
      <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#D4AF37] rounded-full">
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  // Mark tab as visited when switching tabs
  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    if (tab === "transactions") {
      markAsVisited("transactions");
    } else if (tab === "logs") {
      // Mark logs as seen - call API to update last_seen timestamp
      try {
        await axios.post(`${API}/admin/monitoring/mark-seen`, {}, getAuthHeaders());
        // Refresh badge counts
        if (typeof markAsVisited === 'function') {
          markAsVisited("logs");
        }
      } catch (error) {
        console.error("Error marking logs as seen:", error);
      }
    }
  };
  
  // Platform config state
  const [config, setConfig] = useState({
    provider: "stripe",
    mode: "test",
    currency: "EUR",
    country: "IT",
    notification_email: "",
    stripe_configured: false
  });
  const [savingConfig, setSavingConfig] = useState(false);
  
  // Payment Links state
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [showCreateLinkModal, setShowCreateLinkModal] = useState(false);
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [newLink, setNewLink] = useState({
    amount: "",
    currency: "EUR",
    customer_name: "",
    customer_email: "",
    booking_id: "",
    check_in_date: "",
    check_out_date: "",
    // Guest info for tourist tax
    adults: 0,
    children: 0,
    infants: 0,
    children_ages: "",
    tourist_tax_total: null
  });
  const [creatingLink, setCreatingLink] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Email subject and template defaults
  const DEFAULT_EMAIL_SUBJECT = "Reservation Payment - Suite Lucina";
  const DEFAULT_EMAIL_TEMPLATE = `Dear (customer name),

Thank you for choosing Suite Lucina for your stay in Rome. You may proceed with the payment for your reservation with check-in on [check-in date] and check-out on [check-out date] by clicking the following link:

(link)

Kind regards,
Suite Lucina.`;
  
  const [emailSubject, setEmailSubject] = useState(DEFAULT_EMAIL_SUBJECT);
  const [emailTemplate, setEmailTemplate] = useState(DEFAULT_EMAIL_TEMPLATE);
  
  // Generate email preview with actual values
  const getEmailPreview = () => {
    let preview = emailTemplate;
    const customerName = newLink.customer_name || "(customer name)";
    const checkIn = newLink.check_in_date || "[check-in date]";
    const checkOut = newLink.check_out_date || "[check-out date]";
    // Show placeholder for link since we don't have it yet
    const linkPlaceholder = "https://...../api/pay/xxxxx";
    
    preview = preview.replace(/\(customer name\)/g, customerName);
    preview = preview.replace(/\[check-in date\]/g, checkIn);
    preview = preview.replace(/\[check-out date\]/g, checkOut);
    preview = preview.replace(/\(link\)/g, linkPlaceholder);
    
    return preview;
  };
  
  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionSearch, setTransactionSearch] = useState("");
  const [transactionFilter, setTransactionFilter] = useState("");
  const [transactionYear, setTransactionYear] = useState("");
  const [transactionSourceFilter, setTransactionSourceFilter] = useState("all");
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  
  // Tracking state (booking requests)
  const [trackingData, setTrackingData] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [trackingSearch, setTrackingSearch] = useState("");
  const [trackingStatusFilter, setTrackingStatusFilter] = useState("all");
  const [trackingSourceFilter, setTrackingSourceFilter] = useState("all");
  const [trackingYear, setTrackingYear] = useState("");
  const [selectedTracking, setSelectedTracking] = useState([]);
  const [showTrackingLegend, setShowTrackingLegend] = useState(false);
  
  // Logs state (email logs from booking requests)
  const [logsData, setLogsData] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsSearch, setLogsSearch] = useState("");
  const [logsStatusFilter, setLogsStatusFilter] = useState("all");
  const [logsSourceFilter, setLogsSourceFilter] = useState("all");
  const [logsYear, setLogsYear] = useState("");
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [showLogsLegend, setShowLogsLegend] = useState(false);
  
  // Email Sent state
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [emailYear, setEmailYear] = useState("");
  const [selectedEmails, setSelectedEmails] = useState([]);
  
  // Overbooking state
  const [overbookings, setOverbookings] = useState([]);
  const [overbookingCounts, setOverbookingCounts] = useState({ open: 0, acknowledged: 0, resolved: 0, total: 0 });
  const [loadingOverbookings, setLoadingOverbookings] = useState(false);
  const [overbookingStatusFilter, setOverbookingStatusFilter] = useState("open");
  const [selectedOverbookings, setSelectedOverbookings] = useState([]);
  
  // Email Stats state (for Resend tab)
  const [emailStats, setEmailStats] = useState(null);
  const [loadingEmailStats, setLoadingEmailStats] = useState(false);
  const [selectedEmailLogs, setSelectedEmailLogs] = useState([]);
  const [showDeleteEmailLogModal, setShowDeleteEmailLogModal] = useState(false);
  const [emailLogToDelete, setEmailLogToDelete] = useState(null);
  const [deletingEmailLogs, setDeletingEmailLogs] = useState(false);
  
  // Generate year options (current year and 5 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({length: 6}, (_, i) => currentYear - i);
  
  // Mode change confirmation modal state
  const [showModeConfirmModal, setShowModeConfirmModal] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  // Fetch Tracking data
  const fetchTracking = async () => {
    setLoadingTracking(true);
    try {
      // Exclude iCal bookings from tracking view - they have their own "Sync" section
      const res = await axios.get(`${API}/admin/bookings?include_ical=false`, getAuthHeaders());
      setTrackingData(res.data || []);
    } catch (error) {
      console.error("Error fetching tracking:", error);
    } finally {
      setLoadingTracking(false);
    }
  };
  
  // Fetch Logs data (email logs)
  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/admin/email-logs`, getAuthHeaders());
      setLogsData(res.data || []);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Delete single tracking entry
  const deleteTracking = async (requestId, trackingDetails = '') => {
    const confirmed = await showConfirm({
      title: 'Elimina Record Tracking',
      message: 'Sei sicuro di voler eliminare questo record di tracking?',
      details: trackingDetails || null,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/bookings/${requestId}`, getAuthHeaders());
      toast.success("Record eliminato");
      fetchTracking();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Toggle tracking selection
  const toggleTracking = (requestId) => {
    setSelectedTracking(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // Delete selected tracking entries
  const deleteSelectedTracking = async () => {
    const confirmed = await showConfirm({
      title: 'Elimina Record Selezionati',
      message: `Sei sicuro di voler eliminare ${selectedTracking.length} record?`,
      details: `Verranno eliminati permanentemente ${selectedTracking.length} record di tracking.`,
      variant: 'danger',
      confirmText: `Elimina ${selectedTracking.length}`,
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/bookings/bulk`, {
        ...getAuthHeaders(),
        data: { ids: selectedTracking }
      });
      toast.success(`${selectedTracking.length} record eliminati`);
      setSelectedTracking([]);
      fetchTracking();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Delete single log entry
  const deleteLog = async (logId, logDetails = '') => {
    const confirmed = await showConfirm({
      title: 'Elimina Log',
      message: 'Sei sicuro di voler eliminare questo log?',
      details: logDetails || null,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/email-logs/${logId}`, getAuthHeaders());
      toast.success("Log eliminato");
      fetchLogs();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Fetch functions
  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/payments/config`, getAuthHeaders());
      setConfig(res.data);
    } catch (error) {
      console.error("Error fetching config:", error);
    }
  };

  const fetchLinks = async () => {
    setLoadingLinks(true);
    try {
      const res = await axios.get(`${API}/admin/payments/links`, getAuthHeaders());
      setLinks(res.data.links || []);
    } catch (error) {
      toast.error("Errore nel caricamento dei link");
    } finally {
      setLoadingLinks(false);
    }
  };

  const fetchTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const params = new URLSearchParams();
      if (transactionSearch) params.append("search", transactionSearch);
      if (transactionFilter) params.append("status", transactionFilter);
      if (transactionYear) params.append("year", transactionYear);
      const res = await axios.get(`${API}/admin/payments/transactions?${params}`, getAuthHeaders());
      setTransactions(res.data.transactions || []);
    } catch (error) {
      toast.error("Errore nel caricamento delle transazioni");
    } finally {
      setLoadingTransactions(false);
    }
  };
  
  const fetchEmails = async () => {
    setLoadingEmails(true);
    try {
      const params = new URLSearchParams();
      if (emailSearch) params.append("search", emailSearch);
      if (emailFilter) params.append("status", emailFilter);
      if (emailYear) params.append("year", emailYear);
      const res = await axios.get(`${API}/admin/payments/emails?${params}`, getAuthHeaders());
      setEmailLogs(res.data.emails || []);
    } catch (error) {
      toast.error("Errore nel caricamento delle email");
    } finally {
      setLoadingEmails(false);
    }
  };

  // Fetch overbookings
  const fetchOverbookings = async () => {
    setLoadingOverbookings(true);
    try {
      const params = overbookingStatusFilter && overbookingStatusFilter !== "all" 
        ? `?status=${overbookingStatusFilter}` 
        : "";
      const res = await axios.get(`${API}/admin/overbookings${params}`, getAuthHeaders());
      setOverbookings(res.data.overbookings || []);
      setOverbookingCounts(res.data.counts || { open: 0, acknowledged: 0, resolved: 0, total: 0 });
    } catch (error) {
      console.error("Error fetching overbookings:", error);
    } finally {
      setLoadingOverbookings(false);
    }
  };

  // Email Stats functions for Resend tab
  const fetchEmailStats = async () => {
    setLoadingEmailStats(true);
    try {
      const res = await axios.get(`${API}/admin/email-stats`, getAuthHeaders());
      setEmailStats(res.data);
      setSelectedEmailLogs([]);
    } catch (error) {
      console.error("Failed to fetch email stats:", error);
    } finally {
      setLoadingEmailStats(false);
    }
  };

  const deleteResendEmailLog = async (logId) => {
    setDeletingEmailLogs(true);
    try {
      await axios.delete(`${API}/admin/email-logs/${logId}`, getAuthHeaders());
      toast.success("Log email eliminato");
      fetchEmailStats();
    } catch (error) {
      toast.error("Errore nell'eliminazione del log");
    } finally {
      setDeletingEmailLogs(false);
      setShowDeleteEmailLogModal(false);
      setEmailLogToDelete(null);
    }
  };

  const deleteResendEmailLogsBulk = async () => {
    if (selectedEmailLogs.length === 0) return;
    setDeletingEmailLogs(true);
    try {
      await axios.delete(`${API}/admin/email-logs/bulk/delete`, {
        ...getAuthHeaders(),
        data: { ids: selectedEmailLogs }
      });
      toast.success(`${selectedEmailLogs.length} log email eliminati`);
      setSelectedEmailLogs([]);
      fetchEmailStats();
    } catch (error) {
      toast.error("Errore nell'eliminazione dei log");
    } finally {
      setDeletingEmailLogs(false);
      setShowDeleteEmailLogModal(false);
    }
  };

  const toggleSelectAllEmailLogs = () => {
    if (!emailStats?.recent_logs) return;
    if (selectedEmailLogs.length === emailStats.recent_logs.length) {
      setSelectedEmailLogs([]);
    } else {
      setSelectedEmailLogs(emailStats.recent_logs.map(l => l.log_id));
    }
  };

  // Update overbooking status
  const updateOverbookingStatus = async (overbookingId, status, notes = null) => {
    try {
      const params = new URLSearchParams({ status });
      if (notes) params.append("notes", notes);
      await axios.put(`${API}/admin/overbookings/${overbookingId}/status?${params}`, {}, getAuthHeaders());
      toast.success(`Stato aggiornato: ${status}`);
      fetchOverbookings();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Check for overbookings manually
  const checkOverbookingsNow = async () => {
    try {
      const res = await axios.post(`${API}/admin/overbookings/check`, {}, getAuthHeaders());
      if (res.data.new_overbookings > 0) {
        toast.warning(`${res.data.new_overbookings} nuovi overbooking rilevati!`);
      } else {
        toast.success("Nessun nuovo overbooking rilevato");
      }
      fetchOverbookings();
    } catch (error) {
      toast.error("Errore nel controllo");
    }
  };

  // Delete single overbooking
  const deleteOverbooking = async (overbookingId, overbookingDetails = '') => {
    const confirmed = await showConfirm({
      title: 'Elimina Overbooking',
      message: 'Sei sicuro di voler eliminare questo overbooking?',
      details: overbookingDetails || null,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/overbookings/${overbookingId}`, getAuthHeaders());
      toast.success("Overbooking eliminato");
      fetchOverbookings();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Toggle overbooking selection
  const toggleOverbooking = (overbookingId) => {
    setSelectedOverbookings(prev => 
      prev.includes(overbookingId) 
        ? prev.filter(id => id !== overbookingId)
        : [...prev, overbookingId]
    );
  };

  // Toggle all overbookings selection
  const toggleAllOverbookings = () => {
    if (selectedOverbookings.length === overbookings.length && overbookings.length > 0) {
      setSelectedOverbookings([]);
    } else {
      setSelectedOverbookings(overbookings.map(ob => ob.overbooking_id));
    }
  };

  // Delete selected overbookings (bulk)
  const deleteSelectedOverbookings = async () => {
    if (selectedOverbookings.length === 0) return;
    const confirmed = await showConfirm({
      title: 'Elimina Overbooking Selezionati',
      message: `Sei sicuro di voler eliminare ${selectedOverbookings.length} overbooking?`,
      details: `Verranno eliminati permanentemente ${selectedOverbookings.length} record di overbooking.`,
      variant: 'danger',
      confirmText: `Elimina ${selectedOverbookings.length}`,
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/overbookings/bulk`, {
        ...getAuthHeaders(),
        data: { ids: selectedOverbookings }
      });
      toast.success(`${selectedOverbookings.length} overbooking eliminati`);
      setSelectedOverbookings([]);
      fetchOverbookings();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  useEffect(() => {
    fetchConfig();
    // Fetch overbooking counts on load for badge
    fetchOverbookingCounts();
  }, []);

  // Fetch only counts for badge
  const fetchOverbookingCounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/overbookings/count`, getAuthHeaders());
      setOverbookingCounts(prev => ({ ...prev, open: res.data.open_count }));
    } catch (error) {
      console.error("Error fetching overbooking counts:", error);
    }
  };

  // Fetch data when tab changes
  useEffect(() => {
    if (activeTab === "links") fetchLinks();
    if (activeTab === "transactions") fetchTransactions();
    if (activeTab === "emails") fetchEmails();
    if (activeTab === "tracking") fetchTracking();
    if (activeTab === "logs") fetchLogs();
    if (activeTab === "overbookings") fetchOverbookings();
    if (activeTab === "resend") fetchEmailStats();
  }, [activeTab]);
  
  // Dynamic filtering for Transactions - fetch when filters change
  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    }
  }, [transactionFilter, transactionYear]);
  
  // Dynamic filtering for Emails - fetch when filters change
  useEffect(() => {
    if (activeTab === "emails") {
      fetchEmails();
    }
  }, [emailFilter, emailYear]);

  // Save config
  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API}/admin/payments/config`, config, getAuthHeaders());
      toast.success("Configurazione salvata");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingConfig(false);
    }
  };

  // Reload overbookings when filter changes
  useEffect(() => {
    if (activeTab === "overbookings") {
      fetchOverbookings();
    }
  }, [overbookingStatusFilter]);

  // Create payment link (without sending email)
  const createPaymentLink = async (e) => {
    if (e) e.preventDefault();
    
    // Validate required fields
    if (!newLink.amount || parseFloat(newLink.amount) <= 0) {
      toast.error("Inserisci un importo valido");
      return null;
    }
    if (!newLink.check_in_date) {
      toast.error("Data Check-in obbligatoria");
      return null;
    }
    if (!newLink.check_out_date) {
      toast.error("Data Check-out obbligatoria");
      return null;
    }
    if (newLink.check_in_date >= newLink.check_out_date) {
      toast.error("La data di Check-out deve essere successiva al Check-in");
      return null;
    }
    if (!newLink.customer_name || newLink.customer_name.trim() === "") {
      toast.error("Nome cliente obbligatorio");
      return null;
    }
    if (!newLink.adults || parseInt(newLink.adults) < 1) {
      toast.error("Inserisci almeno 1 adulto");
      return null;
    }
    
    setCreatingLink(true);
    try {
      const res = await axios.post(`${API}/admin/payments/links`, {
        ...newLink,
        amount: parseFloat(newLink.amount),
        description: emailSubject // Use email subject as description for reference
      }, getAuthHeaders());
      toast.success("Link di pagamento creato!");
      fetchLinks();
      // Copy short URL to clipboard
      if (res.data.short_url) {
        navigator.clipboard.writeText(res.data.short_url);
        toast.success("Link corto copiato negli appunti!");
      }
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione del link");
      return null;
    } finally {
      setCreatingLink(false);
    }
  };
  
  // Create payment link AND send email
  const createPaymentLinkAndSendEmail = async () => {
    if (!newLink.customer_email) {
      toast.error("Email cliente richiesta per l'invio");
      return;
    }
    
    setSendingEmail(true);
    try {
      // First create the link
      const linkResult = await createPaymentLink();
      if (!linkResult) {
        setSendingEmail(false);
        return;
      }
      
      // Then send the email with custom subject
      await axios.post(`${API}/admin/payments/send-email`, {
        link_id: linkResult.link_id,
        email_body: emailTemplate,
        email_subject: emailSubject,
        customer_email: newLink.customer_email,
        customer_name: newLink.customer_name,
        check_in_date: newLink.check_in_date,
        check_out_date: newLink.check_out_date
      }, getAuthHeaders());
      
      toast.success("Email inviata con successo!");
      setShowCreateLinkModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio dell'email");
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Handle create link only (no email)
  const handleCreateLinkOnly = async () => {
    const result = await createPaymentLink();
    if (result) {
      setShowCreateLinkModal(false);
      resetForm();
    }
  };
  
  // Reset form to defaults
  const resetForm = () => {
    setNewLink({ amount: "", currency: "EUR", customer_name: "", customer_email: "", booking_id: "", check_in_date: "", check_out_date: "", adults: 0, children: 0, infants: 0, children_ages: "", tourist_tax_total: null });
    setEmailSubject(DEFAULT_EMAIL_SUBJECT);
    setEmailTemplate(DEFAULT_EMAIL_TEMPLATE);
  };

  // Delete link
  const deleteLink = async (linkId, linkDetails = '') => {
    const confirmed = await showConfirm({
      title: 'Elimina Payment Link',
      message: 'Sei sicuro di voler eliminare questo payment link?',
      details: linkDetails || null,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/payments/links/${linkId}`, getAuthHeaders());
      toast.success("Link eliminato");
      fetchLinks();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Toggle link selection
  const toggleLink = (linkId) => {
    setSelectedLinks(prev => 
      prev.includes(linkId) 
        ? prev.filter(id => id !== linkId)
        : [...prev, linkId]
    );
  };

  // Toggle all links
  const toggleAllLinks = () => {
    setSelectedLinks(prev => 
      prev.length === links.length ? [] : links.map(l => l.link_id)
    );
  };

  // Delete selected links
  const deleteSelectedLinks = async () => {
    const confirmed = await showConfirm({
      title: 'Elimina Payment Link Selezionati',
      message: `Sei sicuro di voler eliminare ${selectedLinks.length} payment link?`,
      details: `Verranno eliminati permanentemente ${selectedLinks.length} link di pagamento.`,
      variant: 'danger',
      confirmText: `Elimina ${selectedLinks.length}`,
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/payments/links/bulk`, {
        ...getAuthHeaders(),
        data: { ids: selectedLinks }
      });
      toast.success(`${selectedLinks.length} payment link eliminati`);
      setSelectedLinks([]);
      fetchLinks();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Delete transaction
  const deleteTransaction = async (transactionId, transactionDetails = '') => {
    const confirmed = await showConfirm({
      title: 'Elimina Transazione',
      message: 'Sei sicuro di voler eliminare questa transazione?',
      details: transactionDetails || null,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/payments/transactions/${transactionId}`, getAuthHeaders());
      toast.success("Transazione eliminata");
      fetchTransactions();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  // Toggle transaction selection
  const toggleTransaction = (transactionId) => {
    setSelectedTransactions(prev => 
      prev.includes(transactionId) 
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };
  
  // Toggle all transactions
  const toggleAllTransactions = () => {
    setSelectedTransactions(prev => 
      prev.length === transactions.length ? [] : transactions.map(t => t.transaction_id)
    );
  };
  
  // Delete selected transactions
  const deleteSelectedTransactions = async () => {
    const confirmed = await showConfirm({
      title: 'Elimina Transazioni Selezionate',
      message: `Sei sicuro di voler eliminare ${selectedTransactions.length} transazioni?`,
      details: `Verranno eliminate permanentemente ${selectedTransactions.length} transazioni.`,
      variant: 'danger',
      confirmText: `Elimina ${selectedTransactions.length}`,
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/payments/transactions/bulk`, {
        ...getAuthHeaders(),
        data: { ids: selectedTransactions }
      });
      toast.success(`${selectedTransactions.length} transazioni eliminate`);
      setSelectedTransactions([]);
      fetchTransactions();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  // Delete email log
  const deleteEmailLog = async (emailId, emailDetails = '') => {
    const confirmed = await showConfirm({
      title: 'Elimina Log Email',
      message: 'Sei sicuro di voler eliminare questo log email?',
      details: emailDetails || null,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/payments/emails/${emailId}`, getAuthHeaders());
      toast.success("Log email eliminato");
      fetchEmails();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  // Toggle email selection
  const toggleEmail = (emailId) => {
    setSelectedEmails(prev => 
      prev.includes(emailId) 
        ? prev.filter(id => id !== emailId)
        : [...prev, emailId]
    );
  };
  
  // Toggle all emails
  const toggleAllEmails = () => {
    setSelectedEmails(prev => 
      prev.length === emailLogs.length ? [] : emailLogs.map(e => e.email_id)
    );
  };
  
  // Delete selected emails
  const deleteSelectedEmails = async () => {
    const confirmed = await showConfirm({
      title: 'Elimina Email Selezionate',
      message: `Sei sicuro di voler eliminare ${selectedEmails.length} email?`,
      details: `Verranno eliminati permanentemente ${selectedEmails.length} log email.`,
      variant: 'danger',
      confirmText: `Elimina ${selectedEmails.length}`,
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/admin/email-logs/bulk`, {
        ...getAuthHeaders(),
        data: { log_ids: selectedEmails }
      });
      toast.success(`${selectedEmails.length} email eliminate`);
      setSelectedEmails([]);
      fetchEmails();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };
  
  // Export transactions CSV
  const exportTransactionsCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (transactionFilter) params.append("status", transactionFilter);
      const res = await axios.get(`${API}/admin/payments/transactions/export/csv?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Transazioni_SuiteLucina_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };
  
  // Export emails CSV
  const exportEmailsCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (emailFilter) params.append("status", emailFilter);
      const res = await axios.get(`${API}/admin/payments/emails/export/csv?${params}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Email_SuiteLucina_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export completato!");
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  // Copy link to clipboard (short URL preferred)
  const copyLink = (link) => {
    const urlToCopy = link.short_url || link.url;
    navigator.clipboard.writeText(urlToCopy);
    toast.success("Link copiato!");
  };

  // Check payment status
  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      toast.success(`Stato: ${res.data.payment_status}`);
      fetchLinks();
      fetchTransactions();
    } catch (error) {
      toast.error("Errore nel controllo dello stato");
    }
  };

  // Handle mode change with confirmation modal (bidirectional)
  const handleModeChange = (newMode) => {
    // Only show confirmation if mode actually changes
    if (newMode !== config.mode) {
      setPendingMode(newMode);
      setShowModeConfirmModal(true);
    }
  };
  
  // Confirm mode change
  const confirmModeChange = () => {
    if (pendingMode) {
      setConfig({...config, mode: pendingMode});
    }
    setShowModeConfirmModal(false);
    setPendingMode(null);
  };
  
  // Cancel mode change
  const cancelModeChange = () => {
    setShowModeConfirmModal(false);
    setPendingMode(null);
  };

  // Format date - use centralized utility
  const formatDate = (dateStr) => {
    return formatDisplayDateTime(dateStr);
  };

  // Status badge
  // Canonical status configuration for Tracking
  const TRACKING_STATUSES = {
    paid: { label: "Pagato", color: "bg-green-100 text-green-800" },
    confirmed: { label: "Confermato", color: "bg-blue-100 text-blue-800" },
    pending: { label: "Pagamento non eseguito", color: "bg-yellow-100 text-yellow-800" },
    pending_payment: { label: "Pagamento in attesa", color: "bg-orange-100 text-orange-800" },
    cancelled: { label: "Cancellato", color: "bg-red-100 text-red-800" },
    message: { label: "Messaggio", color: "bg-purple-100 text-purple-800" },
    // Legacy mappings
    inquiry: { label: "Messaggio", color: "bg-purple-100 text-purple-800" },
    expired: { label: "Scaduto", color: "bg-gray-100 text-gray-800" },
    failed: { label: "Fallito", color: "bg-red-100 text-red-800" },
    initiated: { label: "Iniziato", color: "bg-blue-100 text-blue-800" },
    unpaid: { label: "Non pagato", color: "bg-yellow-100 text-yellow-800" }
  };

  const StatusBadge = ({ status }) => {
    const config = TRACKING_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-8">
      <h1 className="font-heading text-3xl">Monitoring</h1>
      
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="admin-tabs-list">
          <TabsTrigger value="transactions" className="admin-tab-trigger flex items-center">
            <Receipt size={16} className="mr-2" /> Transactions
            <TabBadge count={badgeCounts.transactions} />
          </TabsTrigger>
          <TabsTrigger value="tracking" className="admin-tab-trigger flex items-center">
            <Activity size={16} className="mr-2" /> Tracking
          </TabsTrigger>
          <TabsTrigger value="logs" className="admin-tab-trigger flex items-center">
            <FileText size={16} className="mr-2" /> Logs
            <TabBadge count={badgeCounts.logs} />
          </TabsTrigger>
          <TabsTrigger value="resend" className="admin-tab-trigger flex items-center">
            <Mail size={16} className="mr-2" /> Resend
            {emailServiceWarning && (
              <span className="ml-2 w-2.5 h-2.5 rounded-full bg-[#D4AF37] animate-pulse" title="Servizio email non operativo" />
            )}
          </TabsTrigger>
          <TabsTrigger value="overbookings" className="admin-tab-trigger">
            <AlertTriangle size={16} className="mr-2" /> Overbooking
            {overbookingCounts.open > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-600 text-white rounded-full animate-pulse">
                {overbookingCounts.open}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Info sotto i tabs - cambia in base al tab attivo */}
        <PageInfoUnderTabs pageKey={`monitoring_${activeTab === 'overbookings' ? 'overbooking' : activeTab}`} />

        {/* OVERBOOKING TAB */}
        <TabsContent value="overbookings">
          <div className="admin-card">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <AlertTriangle className="text-red-600" size={24} />
                  Overbooking Alert
                </h2>
                {overbookingCounts.open > 0 && (
                  <span className="px-3 py-1 text-sm font-bold bg-red-100 text-red-700 rounded-full">
                    {overbookingCounts.open} conflitti aperti
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                {selectedOverbookings.length > 0 && (
                  <Button variant="destructive" onClick={deleteSelectedOverbookings}>
                    <Trash2 size={16} className="mr-2" /> Elimina ({selectedOverbookings.length})
                  </Button>
                )}
                <Button variant="outline" onClick={checkOverbookingsNow}>
                  <RefreshCw size={16} className="mr-2" /> Controlla Ora
                </Button>
              </div>
            </div>

            <p className="text-sm text-[#78716C] mb-4">
              <strong>Monitora i conflitti tra le prenotazioni del sito e le piattaforme di prenotazione (Airbnb, Booking, Vrbo, ecc.) tramite calendario iCal.</strong>
            </p>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <p className="text-sm text-amber-800">
                <strong>Attenzione:</strong> le piattaforme di prenotazione non distinguono tra prenotazioni reali e blocchi manuali nel calendario iCal. 
                Se ricevi una prenotazione dal sito e successivamente blocchi manualmente le stesse date sulla piattaforma per evitare overbooking, 
                il sistema può segnalare un possibile overbooking anche se non è reale. 
                <strong> Verifica sempre la piattaforma e, se non si tratta di un overbooking effettivo, segnala il caso come risolto.</strong>
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center cursor-pointer hover:bg-red-100" onClick={() => setOverbookingStatusFilter("open")}>
                <div className="text-2xl font-bold text-red-600">{overbookingCounts.open}</div>
                <div className="text-sm text-red-700">Aperti</div>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center cursor-pointer hover:bg-yellow-100" onClick={() => setOverbookingStatusFilter("acknowledged")}>
                <div className="text-2xl font-bold text-yellow-600">{overbookingCounts.acknowledged}</div>
                <div className="text-sm text-yellow-700">In gestione</div>
              </div>
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center cursor-pointer hover:bg-green-100" onClick={() => setOverbookingStatusFilter("resolved")}>
                <div className="text-2xl font-bold text-green-600">{overbookingCounts.resolved}</div>
                <div className="text-sm text-green-700">Risolti</div>
              </div>
            </div>

            {/* Filter and Select All */}
            <div className="flex justify-between items-center gap-4 mb-6">
              <select 
                value={overbookingStatusFilter} 
                onChange={(e) => setOverbookingStatusFilter(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="all">Tutti gli stati</option>
                <option value="open">Aperti</option>
                <option value="acknowledged">In gestione</option>
                <option value="resolved">Risolti</option>
              </select>
              
              {overbookings.length > 0 && (
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#78716C]">
                  <input
                    type="checkbox"
                    checked={selectedOverbookings.length === overbookings.length && overbookings.length > 0}
                    onChange={toggleAllOverbookings}
                    className="w-4 h-4 accent-[#D4AF37]"
                  />
                  Seleziona tutti ({overbookings.length})
                </label>
              )}
            </div>

            {loadingOverbookings ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-[#78716C]" />
              </div>
            ) : overbookings.length === 0 ? (
              <div className="text-center py-12 text-[#78716C]">
                <Check size={48} className="mx-auto mb-4 text-green-500" />
                <p className="text-lg font-medium text-green-700">Nessun overbooking rilevato</p>
                <p className="text-sm">Tutte le prenotazioni sono senza conflitti.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {overbookings.map((ob) => (
                  <div 
                    key={ob.overbooking_id} 
                    className={`p-4 rounded-lg border-2 ${
                      ob.status === "open" ? "bg-red-50 border-red-300" :
                      ob.status === "acknowledged" ? "bg-yellow-50 border-yellow-300" :
                      "bg-green-50 border-green-300"
                    } ${selectedOverbookings.includes(ob.overbooking_id) ? "ring-2 ring-[#D4AF37]" : ""}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedOverbookings.includes(ob.overbooking_id)}
                          onChange={() => toggleOverbooking(ob.overbooking_id)}
                          className="w-5 h-5 accent-[#D4AF37]"
                        />
                        <AlertTriangle className={`${
                          ob.status === "open" ? "text-red-600" :
                          ob.status === "acknowledged" ? "text-yellow-600" :
                          "text-green-600"
                        }`} size={20} />
                        <span className={`px-2 py-1 text-xs font-bold rounded ${
                          ob.status === "open" ? "bg-red-600 text-white" :
                          ob.status === "acknowledged" ? "bg-yellow-600 text-white" :
                          "bg-green-600 text-white"
                        }`}>
                          {ob.status === "open" ? "APERTO" : ob.status === "acknowledged" ? "IN GESTIONE" : "RISOLTO"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#78716C]">
                          Rilevato: {formatDate(ob.detected_at)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                          onClick={() => deleteOverbooking(ob.overbooking_id, `${ob.ical_event_summary} vs ${ob.booking_name}`)}
                          title="Elimina"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      {/* iCal Event */}
                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs font-medium text-[#78716C] mb-1">📅 Evento Airbnb/Booking</div>
                        <div className="font-medium">{ob.ical_event_summary}</div>
                        <div className="text-sm text-[#78716C]">
                          {ob.ical_dates?.[0]} → {ob.ical_dates?.[ob.ical_dates.length - 1]}
                        </div>
                      </div>

                      {/* Site Booking */}
                      <div className="p-3 bg-white rounded border">
                        <div className="text-xs font-medium text-[#78716C] mb-1">🏠 Prenotazione Sito</div>
                        <div className="font-medium">{ob.booking_name}</div>
                        <div className="text-sm text-[#78716C]">{ob.booking_email}</div>
                        <div className="text-sm text-[#78716C]">
                          {ob.booking_dates?.[0]} → {ob.booking_dates?.[ob.booking_dates.length - 1]}
                        </div>
                      </div>
                    </div>

                    {/* Overlap Dates */}
                    <div className="p-3 bg-red-100 rounded border border-red-300 mb-4">
                      <div className="text-xs font-medium text-red-700 mb-1">🚨 Date in Conflitto</div>
                      <div className="flex flex-wrap gap-2">
                        {ob.overlap_dates?.map(date => (
                          <span key={date} className="px-2 py-1 bg-red-600 text-white text-xs rounded font-mono">
                            {date}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    {ob.notes && (
                      <div className="p-3 bg-gray-100 rounded mb-4">
                        <div className="text-xs font-medium text-[#78716C] mb-1">📝 Note</div>
                        <div className="text-sm">{ob.notes}</div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      {ob.status === "open" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-yellow-500 text-yellow-700 hover:bg-yellow-50"
                          onClick={() => updateOverbookingStatus(ob.overbooking_id, "acknowledged")}
                        >
                          Prendi in carico
                        </Button>
                      )}
                      {(ob.status === "open" || ob.status === "acknowledged") && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-green-500 text-green-700 hover:bg-green-50"
                          onClick={() => updateOverbookingStatus(ob.overbooking_id, "resolved")}
                        >
                          Segna come Risolto
                        </Button>
                      )}
                      {ob.status === "resolved" && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-500 text-red-700 hover:bg-red-50"
                          onClick={() => updateOverbookingStatus(ob.overbooking_id, "open")}
                        >
                          Riapri
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* TRANSACTIONS TAB */}
        <TabsContent value="transactions">
          <div className="admin-card">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl">Transactions</h2>
                {transactions.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={selectedTransactions.length === transactions.length && transactions.length > 0} onChange={toggleAllTransactions} className="w-4 h-4 accent-[#D4AF37]" />
                    <span className="text-sm text-[#78716C]">Seleziona tutti ({transactions.length})</span>
                  </label>
                )}
                {selectedTransactions.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={deleteSelectedTransactions}>
                    <Trash2 size={14} className="mr-1" /> Elimina {selectedTransactions.length}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportTransactionsCsv}>
                  <FileText size={16} className="mr-2" /> Export Excel
                </Button>
                <Button variant="outline" onClick={fetchTransactions}>
                  <RefreshCw size={16} className="mr-2" /> Aggiorna
                </Button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
              <div className="flex-1 relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A8A29E]" />
                <Input 
                  placeholder="Cerca per nome cliente, ID... (Invio per cercare)" 
                  value={transactionSearch}
                  onChange={(e) => setTransactionSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && fetchTransactions()}
                  className="pl-10 h-10"
                />
              </div>
              {/* Source Filter */}
              <select 
                value={transactionSourceFilter} 
                onChange={(e) => setTransactionSourceFilter(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="all">Tutte le sorgenti</option>
                <option value="FORM">By Form</option>
                <option value="PAYMENT_LINK">By Payment Link</option>
              </select>
              <select 
                value={transactionFilter} 
                onChange={(e) => { setTransactionFilter(e.target.value); }}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="">Tutti gli stati</option>
                <option value="paid">Pagato</option>
                <option value="pending">In attesa</option>
                <option value="expired">Scaduto</option>
              </select>
              <select 
                value={transactionYear} 
                onChange={(e) => { setTransactionYear(e.target.value); }}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="">Tutti gli anni</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {loadingTransactions ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-[#78716C]" />
              </div>
            ) : transactions.filter(tx => transactionSourceFilter === "all" || tx.source === transactionSourceFilter).length === 0 ? (
              <div className="text-center py-12 text-[#78716C]">
                <Receipt size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nessuna transazione trovata</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#D4AF37]">
                      <th className="w-10 py-3 px-2"></th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Cliente</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Sorgente</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Importo</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Stato</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">Data</th>
                      <th className="text-left py-3 px-4 font-medium text-[#44403C]">ID</th>
                      <th className="text-center py-3 px-4 font-medium text-[#44403C]">Ricevuta</th>
                      <th className="text-center py-3 px-4 font-medium text-[#44403C]">Elimina</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.filter(tx => transactionSourceFilter === "all" || tx.source === transactionSourceFilter).map((tx) => (
                      <tr key={tx.transaction_id} className={`border-b border-[#D4AF37] hover:bg-[#FAFAF9] ${selectedTransactions.includes(tx.transaction_id) ? 'bg-[#FDF9EF]' : ''}`}>
                        <td className="py-3 px-2">
                          <input type="checkbox" checked={selectedTransactions.includes(tx.transaction_id)} onChange={() => toggleTransaction(tx.transaction_id)} className="w-4 h-4 accent-[#D4AF37]" />
                        </td>
                        {/* Customer Name - First column */}
                        <td className="py-3 px-4">
                          <div className="font-medium">{tx.customer_name || tx.email || "-"}</div>
                          {tx.customer_email && tx.customer_email !== tx.customer_name && (
                            <div className="text-xs text-[#78716C]">{tx.customer_email}</div>
                          )}
                        </td>
                        {/* Source Badge */}
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            tx.source === "FORM" ? "bg-blue-100 text-blue-800" : 
                            tx.source === "PAYMENT_LINK" ? "bg-purple-100 text-purple-800" : 
                            "bg-gray-100 text-gray-800"
                          }`}>
                            {tx.source === "FORM" ? "Form" : tx.source === "PAYMENT_LINK" ? "Link" : tx.source || "-"}
                          </span>
                          {tx.mode === "test" && <span className="ml-1 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">TEST</span>}
                        </td>
                        <td className="py-3 px-4 font-medium">€{tx.amount?.toFixed(2) || "0.00"}</td>
                        <td className="py-3 px-4">
                          <StatusBadge status={tx.payment_status || tx.status} />
                        </td>
                        <td className="py-3 px-4 text-[#78716C] text-sm">{formatDate(tx.created_at)}</td>
                        <td className="py-3 px-4 font-mono text-xs text-[#78716C]">
                          {tx.transaction_id.substring(0, 8)}...
                        </td>
                        <td className="py-3 px-4 text-center">
                          {tx.receipt_url ? (
                            <Button variant="ghost" size="sm" onClick={() => window.open(tx.receipt_url, "_blank")} title="Vedi ricevuta">
                              <Receipt size={16} className="text-green-600" />
                            </Button>
                          ) : (
                            <span className="text-xs text-[#A8A29E]">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Button variant="ghost" size="sm" onClick={() => deleteTransaction(tx.transaction_id)} className="text-red-600 hover:text-red-700" title="Elimina">
                            <Trash2 size={16} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-[#A8A29E] mt-4">
              Totale: {transactions.filter(tx => transactionSourceFilter === "all" || tx.source === transactionSourceFilter).length} transazioni
            </p>
          </div>
        </TabsContent>

        {/* TRACKING TAB */}
        <TabsContent value="tracking">
          <div className="admin-card">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl">Tracking</h2>
                {trackingData.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={selectedTracking.length === trackingData.length && trackingData.length > 0} 
                      onChange={() => setSelectedTracking(prev => prev.length === trackingData.length ? [] : trackingData.map(t => t.request_id))} 
                      className="w-4 h-4 accent-[#D4AF37]" 
                    />
                    <span className="text-sm text-[#78716C]">Seleziona tutti ({trackingData.length})</span>
                  </label>
                )}
                {selectedTracking.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={deleteSelectedTracking}>
                    <Trash2 size={14} className="mr-1" /> Elimina {selectedTracking.length}
                  </Button>
                )}
              </div>
              <Button variant="outline" onClick={fetchTracking}>
                <RefreshCw size={16} className="mr-2" /> Aggiorna
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
              <div className="flex-1 relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A8A29E]" />
                <Input 
                  placeholder="Cerca per nome cliente, email, booking number..." 
                  value={trackingSearch}
                  onChange={(e) => setTrackingSearch(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
              {/* Source Filter */}
              <select 
                value={trackingSourceFilter} 
                onChange={(e) => setTrackingSourceFilter(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="all">Tutte le sorgenti</option>
                <option value="FORM">Da Form</option>
                <option value="PAYMENT_LINK">Da Payment Link</option>
                <option value="ADMIN">Da Admin</option>
              </select>
              <select 
                value={trackingStatusFilter} 
                onChange={(e) => setTrackingStatusFilter(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="all">Tutti gli stati</option>
                <option value="message">Messaggio</option>
                <option value="paid">Pagato</option>
                <option value="pending">Pagamento non eseguito</option>
                <option value="pending_payment">Pagamento in attesa</option>
                <option value="confirmed">Confermato</option>
                <option value="cancelled">Cancellato</option>
              </select>
              <select 
                value={trackingYear} 
                onChange={(e) => setTrackingYear(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="">Tutti gli anni</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {/* Legend button */}
              <div className="relative">
                <button
                  onClick={() => setShowTrackingLegend(!showTrackingLegend)}
                  className="flex items-center gap-1 px-3 h-10 text-sm text-[#78716C] hover:text-[#44403C] hover:bg-[#F5F5F4] rounded-lg border border-[#D4AF37] transition-colors"
                >
                  <HelpCircle size={16} />
                  <span>Legenda stati</span>
                </button>
                {showTrackingLegend && (
                  <div className="absolute right-0 top-12 z-50 w-80 bg-white border border-[#D4AF37] rounded-lg shadow-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-[#1C1917]">Legenda Stati</h4>
                      <button onClick={() => setShowTrackingLegend(false)} className="text-[#78716C] hover:text-[#1C1917]">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">Pagato</span>
                        <span className="text-[#57534E]">Prenotazione pagata (da Form o da Payment Link)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">Confermato</span>
                        <span className="text-[#57534E]">Prenotazione confermata manualmente da Admin</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap">Pag. non eseguito</span>
                        <span className="text-[#57534E]">Form compilato, utente indirizzato al pagamento ma non completato</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap">Pag. in attesa</span>
                        <span className="text-[#57534E]">Payment Link inviato, cliente non ha ancora pagato</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 whitespace-nowrap">Cancellato</span>
                        <span className="text-[#57534E]">Cancellato da Admin</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">Messaggio</span>
                        <span className="text-[#57534E]">Invio di messaggio dal Form per informazioni</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loadingTracking ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-[#78716C]" />
              </div>
            ) : (() => {
              const filtered = trackingData.filter(b => {
                const matchSource = trackingSourceFilter === "all" || b.source === trackingSourceFilter;
                // Map "inquiry" to "message" for filtering
                const normalizedStatus = b.status === "inquiry" ? "message" : b.status;
                const matchStatus = trackingStatusFilter === "all" || normalizedStatus === trackingStatusFilter;
                const matchYear = !trackingYear || (b.created_at || "").startsWith(trackingYear);
                const matchSearch = !trackingSearch || 
                  (b.name || "").toLowerCase().includes(trackingSearch.toLowerCase()) ||
                  (b.email || "").toLowerCase().includes(trackingSearch.toLowerCase()) ||
                  (b.booking_number || "").toLowerCase().includes(trackingSearch.toLowerCase());
                return matchSource && matchStatus && matchYear && matchSearch;
              });
              return filtered.length === 0 ? (
                <div className="text-center py-12 text-[#78716C]">
                  <Activity size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nessun record di tracking trovato</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#D4AF37]">
                        <th className="w-10 py-3 px-2"></th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Cliente</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Sorgente</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Check-in</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Check-out</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Stato</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Importo</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Data</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">ID</th>
                        <th className="text-center py-3 px-2 font-medium text-[#44403C]">Elimina</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((b) => (
                        <tr key={b.request_id} className={`border-b border-[#D4AF37] hover:bg-[#FAFAF9] ${selectedTracking.includes(b.request_id) ? 'bg-[#FDF9EF]' : ''}`}>
                          <td className="py-3 px-2">
                            <input 
                              type="checkbox" 
                              checked={selectedTracking.includes(b.request_id)} 
                              onChange={() => toggleTracking(b.request_id)} 
                              className="w-4 h-4 accent-[#D4AF37]" 
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium">{b.name || "-"}</div>
                            {b.email && <div className="text-xs text-[#78716C]">{b.email}</div>}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              b.source === "FORM" ? "bg-blue-100 text-blue-800" : 
                              b.source === "PAYMENT_LINK" ? "bg-purple-100 text-purple-800" : 
                              b.source === "ADMIN" ? "bg-orange-100 text-orange-800" :
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {b.source === "FORM" ? "Form" : b.source === "PAYMENT_LINK" ? "Link" : b.source === "ADMIN" ? "Admin" : b.source || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm">{formatDisplayDate(b.check_in)}</td>
                          <td className="py-3 px-4 text-sm">{formatDisplayDate(b.check_out)}</td>
                          <td className="py-3 px-4">
                            <StatusBadge status={b.status} />
                          </td>
                          <td className="py-3 px-4 font-medium">€{b.grand_total?.toFixed(2) || "0.00"}</td>
                          <td className="py-3 px-4 text-[#78716C] text-sm">{formatDisplayDate(b.created_at)}</td>
                          <td className="py-3 px-4 font-mono text-xs text-[#78716C]">
                            {b.booking_number || b.request_id?.substring(0, 8) || "-"}
                          </td>
                          <td className="w-12 py-3 px-2 text-center">
                            <Button variant="ghost" size="sm" onClick={() => deleteTracking(b.request_id)} className="text-red-600 hover:text-red-700 p-1" title="Elimina">
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-[#A8A29E] mt-4">Totale: {filtered.length} record</p>
                </div>
              );
            })()}
          </div>
        </TabsContent>

        {/* LOGS TAB */}
        <TabsContent value="logs">
          <div className="admin-card">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl">Logs</h2>
                {logsData.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={selectedLogs.length === logsData.length && logsData.length > 0} 
                      onChange={() => setSelectedLogs(prev => prev.length === logsData.length ? [] : logsData.map(l => l.log_id))} 
                      className="w-4 h-4 accent-[#D4AF37]" 
                    />
                    <span className="text-sm text-[#78716C]">Seleziona tutti ({logsData.length})</span>
                  </label>
                )}
                {selectedLogs.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={async () => {
                    const confirmed = await showConfirm({
                      title: 'Elimina Log Selezionati',
                      message: `Sei sicuro di voler eliminare ${selectedLogs.length} log?`,
                      variant: 'danger',
                      confirmText: 'Elimina',
                      cancelText: 'Annulla'
                    });
                    if (!confirmed) return;
                    try {
                      await axios.delete(`${API}/admin/email-logs/bulk`, { 
                        ...getAuthHeaders(), 
                        data: { log_ids: selectedLogs } 
                      });
                      toast.success(`${selectedLogs.length} log eliminati`);
                      setSelectedLogs([]);
                      fetchLogs();
                    } catch (error) {
                      toast.error("Errore nell'eliminazione");
                    }
                  }}>
                    <Trash2 size={14} className="mr-1" /> Elimina {selectedLogs.length}
                  </Button>
                )}
              </div>
              <Button variant="outline" onClick={fetchLogs}>
                <RefreshCw size={16} className="mr-2" /> Aggiorna
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6 items-center">
              <div className="flex-1 relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A8A29E]" />
                <Input 
                  placeholder="Cerca per destinatario, oggetto..." 
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  className="pl-10 h-10"
                />
              </div>
              {/* Source Filter */}
              <select 
                value={logsSourceFilter} 
                onChange={(e) => setLogsSourceFilter(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="all">Tutte le sorgenti</option>
                <option value="Messaggio Form">Messaggio dal Form</option>
                <option value="FORM">Pagamento dal Form</option>
                <option value="PAYMENT_LINK">Payment Link</option>
                <option value="SYSTEM">System</option>
              </select>
              {/* Esito Filter (renamed from Stato) */}
              <select 
                value={logsStatusFilter} 
                onChange={(e) => setLogsStatusFilter(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="all">Tutti gli esiti</option>
                <option value="sent">Inviato</option>
                <option value="failed">Fallito</option>
              </select>
              <select 
                value={logsYear} 
                onChange={(e) => setLogsYear(e.target.value)}
                className="px-3 h-10 border border-[#D4AF37] rounded-lg"
              >
                <option value="">Tutti gli anni</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {/* Legenda Sorgente */}
              <div className="relative">
                <button
                  onClick={() => setShowLogsLegend && setShowLogsLegend(!showLogsLegend)}
                  className="flex items-center gap-1 px-3 h-10 text-sm text-[#78716C] hover:text-[#44403C] hover:bg-[#F5F5F4] rounded-lg border border-[#D4AF37] transition-colors"
                >
                  <HelpCircle size={16} />
                  <span>Legenda</span>
                </button>
                {showLogsLegend && (
                  <div className="absolute right-0 top-12 z-50 w-80 bg-white border border-[#D4AF37] rounded-lg shadow-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-[#1C1917]">Legenda Sorgente</h4>
                      <button onClick={() => setShowLogsLegend(false)} className="text-[#78716C] hover:text-[#1C1917]">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-[#1C1917] whitespace-nowrap">Messaggio dal Form</span>
                        <span className="text-[#57534E]">– Richiesta informazioni inviata tramite Form</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-[#1C1917] whitespace-nowrap">Pagamento dal Form</span>
                        <span className="text-[#57534E]">– Pagamento completato tramite Form di prenotazione</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-[#1C1917] whitespace-nowrap">Payment Link</span>
                        <span className="text-[#57534E]">– Pagamento completato tramite Payment Link creato da Admin</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-medium text-[#1C1917] whitespace-nowrap">System</span>
                        <span className="text-[#57534E]">– Email automatiche e alert di sistema (es. overbooking)</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loadingLogs ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-[#78716C]" />
              </div>
            ) : (() => {
              const filtered = logsData.filter(log => {
                const matchSource = logsSourceFilter === "all" || log.source === logsSourceFilter;
                const matchStatus = logsStatusFilter === "all" || log.status === logsStatusFilter;
                const matchYear = !logsYear || (log.timestamp || log.sent_at || "").startsWith(logsYear);
                const matchSearch = !logsSearch || 
                  (log.recipient || "").toLowerCase().includes(logsSearch.toLowerCase()) ||
                  (log.subject || "").toLowerCase().includes(logsSearch.toLowerCase()) ||
                  (log.guest_name || "").toLowerCase().includes(logsSearch.toLowerCase());
                return matchSource && matchStatus && matchYear && matchSearch;
              });
              
              const toggleAllLogs = () => {
                if (selectedLogs.length === filtered.length) {
                  setSelectedLogs([]);
                } else {
                  setSelectedLogs(filtered.map(l => l.log_id));
                }
              };
              
              return filtered.length === 0 ? (
                <div className="text-center py-12 text-[#78716C]">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Nessun log trovato</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#D4AF37]">
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">
                          <input 
                            type="checkbox" 
                            checked={selectedLogs.length === filtered.length && filtered.length > 0} 
                            onChange={toggleAllLogs} 
                            className="w-4 h-4 accent-[#D4AF37]" 
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Destinatario</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Sorgente</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Tipo</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Esito</th>
                        <th className="text-left py-3 px-4 font-medium text-[#44403C]">Data</th>
                        <th className="text-center py-3 px-2 font-medium text-[#44403C]">Elimina</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((log, idx) => (
                        <tr key={log.log_id || idx} className={`border-b border-[#D4AF37] hover:bg-[#FAFAF9] ${selectedLogs.includes(log.log_id) ? 'bg-[#FDF9EF]' : ''}`}>
                          <td className="py-3 px-4">
                            <input 
                              type="checkbox" 
                              checked={selectedLogs.includes(log.log_id)} 
                              onChange={() => {
                                setSelectedLogs(prev => 
                                  prev.includes(log.log_id) 
                                    ? prev.filter(id => id !== log.log_id) 
                                    : [...prev, log.log_id]
                                );
                              }} 
                              className="w-4 h-4 accent-[#D4AF37]" 
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium">{log.guest_name || log.recipient || "-"}</div>
                            {log.recipient && log.guest_name && (
                              <div className="text-xs text-[#78716C]">{log.recipient}</div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.source === "FORM" ? "bg-blue-100 text-blue-800" : 
                              log.source === "PAYMENT_LINK" ? "bg-purple-100 text-purple-800" :
                              log.source === "Messaggio Form" ? "bg-amber-100 text-amber-800" :
                              log.source === "SYSTEM" ? "bg-gray-100 text-gray-800" :
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {log.source === "FORM" ? "Pagamento dal Form" : 
                               log.source === "PAYMENT_LINK" ? "Payment Link" :
                               log.source === "Messaggio Form" ? "Messaggio dal Form" :
                               log.source === "SYSTEM" ? "System" :
                               log.source || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-[#78716C]">
                            {log.type === "booking_request_customer" ? "A Cliente" :
                             log.type === "booking_request_admin" ? "A Admin" :
                             log.type === "booking_request" ? "A Admin" :
                             log.type === "contact_customer" ? "A Cliente" :
                             log.type === "contact_admin" ? "A Admin" :
                             log.type === "contact_inquiry" ? "A Admin" :
                             log.type === "booking_paid_guest" ? "A Cliente" :
                             log.type === "booking_paid_admin" ? "A Admin" :
                             log.type || "-"}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === "sent" ? "bg-green-100 text-green-800" : 
                              log.status === "failed" ? "bg-red-100 text-red-800" : 
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {log.status === "sent" ? "Inviato" : log.status === "failed" ? "Fallito" : log.status || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-[#78716C] text-sm whitespace-nowrap">{formatDateTime(log.timestamp || log.sent_at)}</td>
                          <td className="w-12 py-3 px-2 text-center">
                            <Button variant="ghost" size="sm" onClick={() => deleteLog(log.log_id)} className="text-red-600 hover:text-red-700 p-1" title="Elimina">
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-[#A8A29E] mt-4">Totale: {filtered.length} log</p>
                </div>
              );
            })()}
          </div>
        </TabsContent>

        {/* RESEND TAB - Email System Monitoring */}
        <TabsContent value="resend">
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl">Sistema Email</h2>
                <p className="text-sm text-[#78716C]">Stato del servizio email e log invii</p>
              </div>
              <Button onClick={fetchEmailStats} variant="outline" disabled={loadingEmailStats}>
                <RefreshCw size={16} className={`mr-2 ${loadingEmailStats ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
            </div>

            {/* Status Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Provider Card */}
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#F0F0F0] rounded-lg flex items-center justify-center">
                    <Mail size={20} className="text-[#1C1917]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#78716C] uppercase tracking-wider">Provider</p>
                    <p className="font-semibold text-[#1C1917]">{emailStats?.provider || "Resend"}</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-[#E7E5E4]">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                    emailStats?.environment === "LIVE" 
                      ? "bg-green-100 text-green-700" 
                      : "bg-red-100 text-red-700"
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${emailStats?.environment === "LIVE" ? "bg-green-500" : "bg-red-500"}`}></span>
                    {emailStats?.environment || "Caricamento..."}
                  </span>
                </div>
              </div>

              {/* Sender Config Card */}
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#FDF9EF] rounded-lg flex items-center justify-center">
                    <Shield size={20} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#78716C] uppercase tracking-wider">Sender Configurato</p>
                    <p className="font-semibold text-[#1C1917]">{emailStats?.sender_name || "Suite Lucina"}</p>
                  </div>
                </div>
                <div className="pt-3 border-t border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C]">{emailStats?.sender_email || "info@suitelucina.com"}</p>
                </div>
              </div>

              {/* Status Card */}
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    emailStats?.status === "operational" ? "bg-green-100" : 
                    emailStats?.status === "warning" ? "bg-yellow-100" : "bg-red-100"
                  }`}>
                    {emailStats?.status === "operational" ? (
                      <CheckCircle size={20} className="text-green-600" />
                    ) : emailStats?.status === "warning" ? (
                      <AlertCircle size={20} className="text-yellow-600" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-[#78716C] uppercase tracking-wider">Stato Invio</p>
                    <p className={`font-semibold ${
                      emailStats?.status === "operational" ? "text-green-600" : 
                      emailStats?.status === "warning" ? "text-yellow-600" : "text-red-600"
                    }`}>
                      {emailStats?.status === "operational" ? "Operativo" : 
                       emailStats?.status === "warning" ? "Attenzione" : "Errore"}
                    </p>
                  </div>
                </div>
                <div className="pt-3 border-t border-[#E7E5E4] flex justify-between text-sm">
                  <span className="text-green-600">{emailStats?.stats?.total_sent || 0} inviate</span>
                  <span className="text-red-600">{emailStats?.stats?.total_failed || 0} fallite</span>
                </div>
              </div>
            </div>

            {/* Last Activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Last Success */}
              <div className="p-4 border rounded-lg bg-white">
                <h3 className="font-semibold text-[#1C1917] mb-3 flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-500" />
                  Ultimo Invio Riuscito
                </h3>
                {emailStats?.last_success ? (
                  <div className="space-y-2 text-sm">
                    <p><span className="text-[#78716C]">Data:</span> <span className="font-medium">{formatDisplayDateTime(emailStats.last_success.timestamp)}</span></p>
                    <p><span className="text-[#78716C]">Destinatario:</span> <span className="font-medium">{emailStats.last_success.recipient}</span></p>
                    <p><span className="text-[#78716C]">Oggetto:</span> <span className="font-medium truncate">{emailStats.last_success.subject}</span></p>
                  </div>
                ) : (
                  <p className="text-[#A8A29E] text-sm">Nessun invio registrato</p>
                )}
              </div>

              {/* Last Failure */}
              <div className="p-4 border rounded-lg bg-white">
                <h3 className="font-semibold text-[#1C1917] mb-3 flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-500" />
                  Ultimo Errore
                </h3>
                {emailStats?.last_failure ? (
                  <div className="space-y-2 text-sm">
                    <p><span className="text-[#78716C]">Data:</span> <span className="font-medium">{formatDisplayDateTime(emailStats.last_failure.timestamp)}</span></p>
                    <p><span className="text-[#78716C]">Destinatario:</span> <span className="font-medium">{emailStats.last_failure.recipient}</span></p>
                    <p><span className="text-[#78716C]">Errore:</span> <span className="font-medium text-red-600">{emailStats.last_failure.error_message || "Sconosciuto"}</span></p>
                  </div>
                ) : (
                  <p className="text-green-600 text-sm flex items-center gap-1">
                    <CheckCircle size={14} /> Nessun errore registrato
                  </p>
                )}
              </div>
            </div>

            {/* Modale conferma eliminazione email log */}
            {showDeleteEmailLogModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                  <h3 className="font-heading text-xl mb-4 text-[#1C1917]">Elimina Log</h3>
                  <p className="text-[#78716C] mb-2">
                    {emailLogToDelete 
                      ? "Sei sicuro di voler eliminare questo log?"
                      : `Sei sicuro di voler eliminare ${selectedEmailLogs.length} log selezionati?`}
                  </p>
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-6">
                    <AlertCircle size={16} className="text-red-600" />
                    <p className="text-sm text-red-700">
                      <strong>Questa azione è IRREVERSIBILE</strong> e i dati verranno eliminati definitivamente dal database.
                    </p>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setShowDeleteEmailLogModal(false);
                        setEmailLogToDelete(null);
                      }}
                    >
                      Annulla
                    </Button>
                    <Button 
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => emailLogToDelete ? deleteResendEmailLog(emailLogToDelete) : deleteResendEmailLogsBulk()}
                      disabled={deletingEmailLogs}
                    >
                      {deletingEmailLogs ? (
                        <>
                          <RefreshCw size={16} className="animate-spin mr-2" />
                          Eliminazione...
                        </>
                      ) : (
                        "Elimina"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Logs - Table format matching Monitoring → Logs style */}
            <div className="border rounded-lg overflow-hidden">
              <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
                <h3 className="font-semibold text-[#1C1917] flex items-center gap-2">
                  <History size={16} />
                  Log Recenti ({emailStats?.recent_logs?.length || 0})
                </h3>
                <div className="flex items-center gap-3">
                  {selectedEmailLogs.length > 0 && (
                    <Button
                      onClick={() => setShowDeleteEmailLogModal(true)}
                      variant="outline"
                      className="text-red-600 border-red-600 hover:bg-red-50"
                      size="sm"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Elimina selezionati ({selectedEmailLogs.length})
                    </Button>
                  )}
                  {emailStats?.recent_logs && emailStats.recent_logs.length > 0 && (
                    <label className="flex items-center gap-2 text-sm text-[#78716C] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedEmailLogs.length === emailStats.recent_logs.length && emailStats.recent_logs.length > 0}
                        onChange={toggleSelectAllEmailLogs}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                      Seleziona tutti
                    </label>
                  )}
                </div>
              </div>
              
              {emailStats?.recent_logs && emailStats.recent_logs.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-10 py-3 px-4 text-left"></th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-[#78716C] uppercase tracking-wider">Destinatario</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-[#78716C] uppercase tracking-wider">Sorgente</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-[#78716C] uppercase tracking-wider">Tipo</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-[#78716C] uppercase tracking-wider">Esito</th>
                        <th className="py-3 px-4 text-left text-xs font-medium text-[#78716C] uppercase tracking-wider">Data</th>
                        <th className="py-3 px-4 text-center text-xs font-medium text-[#78716C] uppercase tracking-wider">Elimina</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {emailStats.recent_logs.map((log, idx) => (
                        <tr key={log.log_id || idx} className={selectedEmailLogs.includes(log.log_id) ? "bg-red-50" : ""}>
                          <td className="py-3 px-4">
                            <input
                              type="checkbox"
                              checked={selectedEmailLogs.includes(log.log_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEmailLogs([...selectedEmailLogs, log.log_id]);
                                } else {
                                  setSelectedEmailLogs(selectedEmailLogs.filter(id => id !== log.log_id));
                                }
                              }}
                              className="w-4 h-4 accent-[#D4AF37]"
                            />
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium text-sm">{log.recipient}</div>
                            <div className="text-xs text-[#78716C] truncate max-w-[200px]">{log.subject}</div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.source === "FORM" ? "bg-blue-100 text-blue-800" : 
                              log.source === "PAYMENT_LINK" || log.source === "Payment Link" ? "bg-purple-100 text-purple-800" :
                              log.source === "Messaggio Form" ? "bg-amber-100 text-amber-800" :
                              log.source === "SYSTEM" ? "bg-gray-100 text-gray-800" :
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {log.source || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-[#78716C]">
                            {log.type === "booking_request_customer" ? "A Cliente" :
                             log.type === "booking_request_admin" ? "A Admin" :
                             log.type === "contact_customer" ? "A Cliente" :
                             log.type === "contact_admin" ? "A Admin" :
                             log.type === "booking_paid_guest" ? "A Cliente" :
                             log.type === "booking_paid_admin" ? "A Admin" :
                             log.type === "password_reset" ? "Reset Password" :
                             log.type === "payment_link_email" ? "Link Pagamento" :
                             log.type?.includes("admin_alert") ? "Alert Sistema" :
                             log.type || "-"}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === "sent" ? "bg-green-100 text-green-800" : 
                              log.status === "failed" ? "bg-red-100 text-red-800" : 
                              "bg-gray-100 text-gray-800"
                            }`}>
                              {log.status === "sent" ? "✓ Inviata" : log.status === "failed" ? "✗ Fallita" : log.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs text-[#78716C]">
                            {formatDisplayDateTime(log.timestamp)}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => {
                                setEmailLogToDelete(log.log_id);
                                setShowDeleteEmailLogModal(true);
                              }}
                              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Elimina log"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-[#A8A29E]">
                  Nessun log email disponibile
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Mode Change Confirmation Modal */}
      <Dialog open={showModeConfirmModal} onOpenChange={setShowModeConfirmModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingMode === "live" ? "🚀" : "🧪"} Conferma Cambio Modalità
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {pendingMode === "live" ? (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-semibold text-red-800 mb-2">⚠️ ATTENZIONE: Stai passando alla modalità LIVE!</p>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• I pagamenti saranno <strong>REALI</strong></li>
                    <li>• Le transazioni verranno elaborate effettivamente</li>
                    <li>• Assicurati di aver configurato le chiavi API Live</li>
                  </ul>
                </div>
                <p className="text-sm text-[#78716C]">Sei sicuro di voler procedere con il passaggio alla modalità Live?</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-semibold text-yellow-800 mb-2">🧪 Stai passando alla modalità TEST</p>
                  <ul className="text-sm text-yellow-700 space-y-1">
                    <li>• I pagamenti <strong>NON saranno reali</strong></li>
                    <li>• Verranno utilizzate le chiavi API di test</li>
                    <li>• Utile per verificare il funzionamento prima del go-live</li>
                  </ul>
                </div>
                <p className="text-sm text-[#78716C]">Sei sicuro di voler tornare alla modalità Test?</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={cancelModeChange}>
              Annulla
            </Button>
            <Button 
              onClick={confirmModeChange}
              className={pendingMode === "live" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-yellow-600 hover:bg-yellow-700 text-white"}
            >
              {pendingMode === "live" ? "Conferma Live" : "Conferma Test"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AccountSettings({ showConfirm }) {
  const { getAuthHeaders, user } = useAuth();
  
  // Admin Users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: "", password: "", confirmPassword: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Change password modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newPasswordData, setNewPasswordData] = useState({ currentPassword: "", password: "", confirmPassword: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  // Cleaning Users state
  const [cleaningUsers, setCleaningUsers] = useState([]);
  const [loadingCleaningUsers, setLoadingCleaningUsers] = useState(false);
  const [showAddCleaningModal, setShowAddCleaningModal] = useState(false);
  const [newCleaningUser, setNewCleaningUser] = useState({ email: "", password: "", confirmPassword: "" });
  const [addingCleaningUser, setAddingCleaningUser] = useState(false);
  const [deletingCleaningId, setDeletingCleaningId] = useState(null);
  
  // Change password modal for cleaning users
  const [showCleaningPasswordModal, setShowCleaningPasswordModal] = useState(false);
  const [selectedCleaningUser, setSelectedCleaningUser] = useState(null);
  const [cleaningPasswordData, setCleaningPasswordData] = useState({ password: "", confirmPassword: "" });
  const [changingCleaningPassword, setChangingCleaningPassword] = useState(false);

  // Fetch admin users on mount
  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/users`, getAuthHeaders());
      setAdminUsers(res.data.users || []);
    } catch (error) {
      toast.error("Errore nel caricamento utenti");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (newAdmin.password !== newAdmin.confirmPassword) {
      toast.error("Le password non corrispondono");
      return;
    }
    if (newAdmin.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setAddingAdmin(true);
    try {
      await axios.post(`${API}/admin/users`, { email: newAdmin.email, password: newAdmin.password }, getAuthHeaders());
      toast.success("Admin aggiunto con successo");
      setNewAdmin({ email: "", password: "", confirmPassword: "" });
      setShowAddModal(false);
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (userId, email) => {
    const confirmed = await showConfirm({
      title: 'Elimina Account Admin',
      message: `Sei sicuro di voler eliminare l'account di ${email}?`,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    setDeletingId(userId);
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeaders());
      toast.success("Admin eliminato");
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  const openChangePasswordModal = (adminUser) => {
    setSelectedAdmin(adminUser);
    setNewPasswordData({ currentPassword: "", password: "", confirmPassword: "" });
    setShowChangePasswordModal(true);
  };

  const handleChangeAdminPassword = async (e) => {
    e.preventDefault();
    if (newPasswordData.password !== newPasswordData.confirmPassword) {
      toast.error("Le password non corrispondono");
      return;
    }
    if (newPasswordData.password.length < 6) {
      toast.error("La nuova password deve essere di almeno 6 caratteri");
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/admin/users/${selectedAdmin.user_id}/password`, { 
        current_password: newPasswordData.currentPassword,
        new_password: newPasswordData.password 
      }, getAuthHeaders());
      toast.success(`Password di ${selectedAdmin.email} modificata con successo`);
      setShowChangePasswordModal(false);
      setSelectedAdmin(null);
      setNewPasswordData({ currentPassword: "", password: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella modifica password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Cleaning Users functions
  const fetchCleaningUsers = async () => {
    setLoadingCleaningUsers(true);
    try {
      const res = await axios.get(`${API}/admin/cleaning-users`, getAuthHeaders());
      setCleaningUsers(res.data.users || []);
    } catch (error) {
      console.error("Errore nel caricamento utenti pulizie:", error);
    } finally {
      setLoadingCleaningUsers(false);
    }
  };

  const handleAddCleaningUser = async (e) => {
    e.preventDefault();
    if (newCleaningUser.password !== newCleaningUser.confirmPassword) {
      toast.error("Le password non corrispondono");
      return;
    }
    if (newCleaningUser.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setAddingCleaningUser(true);
    try {
      await axios.post(`${API}/admin/cleaning-users`, { 
        email: newCleaningUser.email, 
        password: newCleaningUser.password 
      }, getAuthHeaders());
      toast.success("Utente Pulizie aggiunto con successo");
      setNewCleaningUser({ email: "", password: "", confirmPassword: "" });
      setShowAddCleaningModal(false);
      fetchCleaningUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setAddingCleaningUser(false);
    }
  };

  const handleDeleteCleaningUser = async (userId, email) => {
    const confirmed = await showConfirm({
      title: 'Elimina Account Pulizie',
      message: `Sei sicuro di voler eliminare l'account pulizie di ${email}?`,
      variant: 'danger',
      confirmText: 'Elimina',
      cancelText: 'Annulla'
    });
    if (!confirmed) return;
    setDeletingCleaningId(userId);
    try {
      await axios.delete(`${API}/admin/cleaning-users/${userId}`, getAuthHeaders());
      toast.success("Utente Pulizie eliminato");
      fetchCleaningUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeletingCleaningId(null);
    }
  };

  // Open change password modal for cleaning user
  const openCleaningPasswordModal = (cleaningUser) => {
    setSelectedCleaningUser(cleaningUser);
    setCleaningPasswordData({ password: "", confirmPassword: "" });
    setShowCleaningPasswordModal(true);
  };

  // Handle cleaning user password change (admin can reset without current password)
  const handleChangeCleaningPassword = async (e) => {
    e.preventDefault();
    if (cleaningPasswordData.password !== cleaningPasswordData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }
    if (cleaningPasswordData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }
    setChangingCleaningPassword(true);
    try {
      await axios.put(`${API}/admin/cleaning-users/${selectedCleaningUser.user_id}/password`, { 
        new_password: cleaningPasswordData.password 
      }, getAuthHeaders());
      toast.success(`Password di ${selectedCleaningUser.email} modificata con successo`);
      setShowCleaningPasswordModal(false);
      setSelectedCleaningUser(null);
      setCleaningPasswordData({ password: "", confirmPassword: "" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle both string and object/array error responses
      const errorMessage = typeof detail === 'string' 
        ? detail 
        : (Array.isArray(detail) ? detail[0]?.msg : JSON.stringify(detail)) || "Errore nel cambio password";
      toast.error(errorMessage);
    } finally {
      setChangingCleaningPassword(false);
    }
  };

  // Fetch cleaning users on mount
  useEffect(() => {
    fetchCleaningUsers();
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Account</h1>
        <PageInfoInline pageKey="account" />
      </div>
      
      {/* Admin Users Section */}
      <div className="admin-card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-xl">Gestione Utenti Admin</h2>
          <Button onClick={() => setShowAddModal(true)} className="bg-[#1C1917] hover:bg-[#292524] text-white">
            <UserPlus size={16} className="mr-2" /> Aggiungi Admin
          </Button>
        </div>
        
        <p className="text-sm text-[#78716C] mb-6">
          Gestisci gli account admin che possono accedere al pannello di controllo. Ogni utente ha gli stessi permessi.
        </p>

        {loadingUsers ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw size={24} className="animate-spin text-[#78716C]" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#D4AF37]">
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Data creazione</th>
                  <th className="text-left py-3 px-4 font-medium text-[#44403C]">Creato da</th>
                  <th className="text-center py-3 px-4 font-medium text-[#44403C]">Password</th>
                  <th className="text-center py-3 px-4 font-medium text-[#44403C]">Elimina</th>
                </tr>
              </thead>
              <tbody>
                {adminUsers.map((adminUser) => (
                  <tr key={adminUser.user_id} className="border-b border-[#D4AF37] hover:bg-[#FAFAF9]">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span>{adminUser.email}</span>
                        {user?.email === adminUser.email && (
                          <span className="text-xs bg-[#1C1917] text-white px-2 py-0.5 rounded">Tu</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[#78716C]">
                      {adminUser.created_at ? formatDisplayDate(adminUser.created_at) : '-'}
                    </td>
                    <td className="py-3 px-4 text-[#78716C]">
                      {adminUser.created_by || '-'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => openChangePasswordModal(adminUser)}
                        className="text-[#44403C] hover:text-[#1C1917] hover:bg-[#F5F5F4]"
                        title={`Cambia password per ${adminUser.email}`}
                      >
                        <Key size={16} />
                      </Button>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {user?.email !== adminUser.email && adminUsers.length > 1 ? (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteAdmin(adminUser.user_id, adminUser.email)}
                          disabled={deletingId === adminUser.user_id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title={`Elimina ${adminUser.email}`}
                        >
                          {deletingId === adminUser.user_id ? (
                            <RefreshCw size={16} className="animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </Button>
                      ) : adminUsers.length === 1 ? (
                        <span className="text-xs text-[#A8A29E]">Ultimo</span>
                      ) : (
                        <span className="text-xs text-[#A8A29E]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {adminUsers.length > 0 && (
          <p className="text-xs text-[#A8A29E] mt-4">
            Totale: {adminUsers.length} admin • Deve rimanere sempre almeno 1 account attivo
          </p>
        )}
      </div>

      {/* Cleaning Users Section */}
      <div className="admin-card">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading text-xl">Gestione Utenti Pulizie</h2>
          <Button onClick={() => setShowAddCleaningModal(true)} className="bg-[#1C1917] hover:bg-[#292524] text-white">
            <UserPlus size={16} className="mr-2" /> Aggiungi Utenza Cleaning
          </Button>
        </div>
        
        <p className="text-sm text-[#78716C] mb-4">
          Crea account dedicati per il personale delle pulizie con accesso limitato.
        </p>

        {/* Permessi Info Box */}
        <div className="bg-[#FFFDF7] border border-[#D4AF37] rounded-lg p-4 mb-6">
          <h4 className="font-medium text-[#44403C] mb-2">Permessi Utente Pulizie:</h4>
          <ul className="space-y-1 text-sm text-[#78716C]">
            <li>• Visualizza Dashboard limitata (prossimi 4 eventi di check-in/out)</li>
            <li>• Visualizza Calendario mensile (navigazione mesi)</li>
            <li>• Visualizza date check-in / check-out con dettagli di prenotazione</li>
            <li>• Nessuna modifica dati / nessuna creazione</li>
            <li>• Nessun accesso ad altri moduli (pagamenti, impostazioni, export)</li>
          </ul>
        </div>

        {loadingCleaningUsers ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : cleaningUsers.length === 0 ? (
          <div className="text-center py-8 bg-[#F5F5F4] rounded-lg">
            <p className="text-[#78716C]">Nessun utente pulizie creato</p>
            <p className="text-xs text-[#A8A29E] mt-1">Clicca "Aggiungi Utenza Cleaning" per crearne uno</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#D4AF37]">
                  <th className="text-left py-3 px-4 font-medium text-[#78716C]">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-[#78716C]">Creato il</th>
                  <th className="text-left py-3 px-4 font-medium text-[#78716C]">Creato da</th>
                  <th className="text-center py-3 px-4 font-medium text-[#78716C]">Password</th>
                  <th className="text-right py-3 px-4 font-medium text-[#78716C]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {cleaningUsers.map((cUser) => (
                  <tr key={cUser.user_id} className="border-b border-[#D4AF37] hover:bg-[#FFFDF7]">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-xs">
                          {cUser.email.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-[#1C1917]">{cUser.email}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-[#78716C]">
                      {cUser.created_at ? formatDisplayDate(cUser.created_at) : '—'}
                    </td>
                    <td className="py-3 px-4 text-[#78716C]">
                      {cUser.created_by || '—'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button 
                        variant="ghost"
                        size="sm" 
                        className="text-[#44403C] hover:text-[#1C1917] hover:bg-[#F5F5F4]"
                        onClick={() => openCleaningPasswordModal(cUser)}
                        title="Reset Password"
                      >
                        <Key size={16} />
                      </Button>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteCleaningUser(cUser.user_id, cUser.email)}
                        disabled={deletingCleaningId === cUser.user_id}
                      >
                        {deletingCleaningId === cUser.user_id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {cleaningUsers.length > 0 && (
          <p className="text-xs text-[#A8A29E] mt-4">
            Totale: {cleaningUsers.length} utent{cleaningUsers.length === 1 ? 'e' : 'i'} pulizie
          </p>
        )}
      </div>

      {/* Add Cleaning User Modal */}
      <Dialog open={showAddCleaningModal} onOpenChange={setShowAddCleaningModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiungi Utenza Cleaning</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCleaningUser} className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Nota:</strong> Questo utente avrà accesso solo alla Dashboard Pulizie con visualizzazione limitata ai check-in/check-out.
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Email</label>
              <Input 
                type="email" 
                placeholder="pulizie@esempio.com" 
                value={newCleaningUser.email} 
                onChange={(e) => setNewCleaningUser({...newCleaningUser, email: e.target.value})} 
                required 
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Password</label>
              <Input 
                type="password" 
                placeholder="Minimo 6 caratteri" 
                value={newCleaningUser.password} 
                onChange={(e) => setNewCleaningUser({...newCleaningUser, password: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Conferma Password</label>
              <Input 
                type="password" 
                placeholder="Ripeti la password" 
                value={newCleaningUser.confirmPassword} 
                onChange={(e) => setNewCleaningUser({...newCleaningUser, confirmPassword: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddCleaningModal(false)}>
                Annulla
              </Button>
              <Button type="submit" className="bg-[#1C1917] hover:bg-[#292524]" disabled={addingCleaningUser}>
                {addingCleaningUser ? "Creazione..." : "Crea Utente"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Cleaning User Password Modal */}
      <Dialog open={showCleaningPasswordModal} onOpenChange={setShowCleaningPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password Utente Pulizie</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangeCleaningPassword} className="space-y-4 mt-4">
            <div className="p-3 bg-[#F5F5F4] rounded-lg border border-[#D4AF37]">
              <p className="text-sm text-[#78716C]">
                Account: <strong className="text-[#1C1917]">{selectedCleaningUser?.email}</strong>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Nuova Password</label>
              <Input 
                type="password" 
                placeholder="Minimo 6 caratteri" 
                value={cleaningPasswordData.password} 
                onChange={(e) => setCleaningPasswordData({...cleaningPasswordData, password: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Conferma Nuova Password</label>
              <Input 
                type="password" 
                placeholder="Ripeti la nuova password" 
                value={cleaningPasswordData.confirmPassword} 
                onChange={(e) => setCleaningPasswordData({...cleaningPasswordData, confirmPassword: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCleaningPasswordModal(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={changingCleaningPassword} className="bg-[#1C1917] hover:bg-[#292524] text-white">
                {changingCleaningPassword ? <><RefreshCw size={16} className="animate-spin mr-2" /> Salvataggio...</> : <><Key size={16} className="mr-2" /> Reset Password</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Admin Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiungi nuovo Admin</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAdmin} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Email</label>
              <Input 
                type="email" 
                placeholder="email@esempio.com" 
                value={newAdmin.email} 
                onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})} 
                required 
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Password</label>
              <Input 
                type="password" 
                placeholder="Minimo 6 caratteri" 
                value={newAdmin.password} 
                onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Conferma Password</label>
              <Input 
                type="password" 
                placeholder="Ripeti la password" 
                value={newAdmin.confirmPassword} 
                onChange={(e) => setNewAdmin({...newAdmin, confirmPassword: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={addingAdmin} className="bg-[#1C1917] hover:bg-[#292524] text-white">
                {addingAdmin ? <><RefreshCw size={16} className="animate-spin mr-2" /> Creazione...</> : <><UserPlus size={16} className="mr-2" /> Crea Admin</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cambia Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangeAdminPassword} className="space-y-4 mt-4">
            <div className="p-3 bg-[#F5F5F4] rounded-lg border border-[#D4AF37]">
              <p className="text-sm text-[#78716C]">
                Account: <strong className="text-[#1C1917]">{selectedAdmin?.email}</strong>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Password Attuale</label>
              <Input 
                type="password" 
                placeholder="Inserisci la password attuale" 
                value={newPasswordData.currentPassword} 
                onChange={(e) => setNewPasswordData({...newPasswordData, currentPassword: e.target.value})} 
                required 
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Nuova Password</label>
              <Input 
                type="password" 
                placeholder="Minimo 6 caratteri" 
                value={newPasswordData.password} 
                onChange={(e) => setNewPasswordData({...newPasswordData, password: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#44403C] mb-1 block">Conferma Nuova Password</label>
              <Input 
                type="password" 
                placeholder="Ripeti la nuova password" 
                value={newPasswordData.confirmPassword} 
                onChange={(e) => setNewPasswordData({...newPasswordData, confirmPassword: e.target.value})} 
                required 
                minLength={6}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowChangePasswordModal(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={changingPassword} className="bg-[#1C1917] hover:bg-[#292524] text-white">
                {changingPassword ? <><RefreshCw size={16} className="animate-spin mr-2" /> Salvataggio...</> : <><Key size={16} className="mr-2" /> Cambia Password</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// System Settings Page - Area tecnica con tabs (simile a Monitoring)
function SystemSettings({ showConfirm }) {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState("database");
  
  // Database backup state
  const [exportingDatabase, setExportingDatabase] = useState(false);
  const [databaseInfo, setDatabaseInfo] = useState(null);
  const [loadingDatabaseInfo, setLoadingDatabaseInfo] = useState(false);

  // Code export state
  const [exportingCode, setExportingCode] = useState(false);
  const [codeInfo, setCodeInfo] = useState(null);
  const [loadingCodeInfo, setLoadingCodeInfo] = useState(false);

  // Fetch database info
  const fetchDatabaseInfo = async () => {
    setLoadingDatabaseInfo(true);
    try {
      const res = await axios.get(`${API}/admin/export/database/info`, getAuthHeaders());
      setDatabaseInfo(res.data);
    } catch (error) {
      console.error("Errore nel caricamento info database:", error);
    } finally {
      setLoadingDatabaseInfo(false);
    }
  };

  // Fetch code info
  const fetchCodeInfo = async () => {
    setLoadingCodeInfo(true);
    try {
      const res = await axios.get(`${API}/admin/export/code/info`, getAuthHeaders());
      setCodeInfo(res.data);
    } catch (error) {
      console.error("Errore nel caricamento info codice:", error);
    } finally {
      setLoadingCodeInfo(false);
    }
  };

  const handleExportDatabase = async () => {
    // Conferma esplicita prima del download
    const confirmed = await showConfirm({
      title: 'Esporta Backup Database',
      message: 'Stai per scaricare un backup completo del database in formato ZIP. Questo file conterrà tutti i dati delle prenotazioni, configurazioni e log. Vuoi procedere?',
      variant: 'warning',
      confirmText: 'Scarica Backup',
      cancelText: 'Annulla'
    });
    
    if (!confirmed) return;
    
    setExportingDatabase(true);
    try {
      const response = await axios.get(`${API}/admin/export/database`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Get filename from headers or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'suitelucina_backup.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup database scaricato con successo');
    } catch (error) {
      console.error("Errore export database:", error);
      toast.error(error.response?.data?.detail || "Errore nell'esportazione del database");
    } finally {
      setExportingDatabase(false);
    }
  };

  const handleExportCode = async () => {
    // Conferma esplicita prima del download
    const confirmed = await showConfirm({
      title: 'Esporta Codice Sorgente',
      message: 'Stai per scaricare il codice sorgente completo del progetto in formato ZIP (~160 MB). I file .env (backend e frontend) NON sono inclusi per motivi di sicurezza. Il download potrebbe richiedere alcuni minuti. Vuoi procedere?',
      variant: 'warning',
      confirmText: 'Scarica Codice',
      cancelText: 'Annulla'
    });
    
    if (!confirmed) return;
    
    setExportingCode(true);
    toast.info('Download in corso... attendere (file ~160 MB)');
    
    try {
      const response = await axios.get(`${API}/admin/export/code`, {
        ...getAuthHeaders(),
        responseType: 'blob',
        timeout: 300000,  // 5 minutes timeout
      });
      
      // Verify we got actual data
      if (!response.data || response.data.size === 0) {
        throw new Error('Download vuoto');
      }
      
      // Get filename from headers or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `suitelucina-code-${new Date().toISOString().slice(0,10)}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup after delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      
      toast.success(`Codice scaricato: ${(response.data.size / 1024 / 1024).toFixed(1)} MB`);
    } catch (error) {
      console.error("Errore export codice:", error);
      
      // More specific error messages
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Timeout - il file è troppo grande. Riprova o usa una connessione più veloce.");
      } else if (error.response?.status === 401) {
        toast.error("Sessione scaduta - effettua nuovamente il login");
      } else if (error.response?.status === 500) {
        toast.error("Errore server - riprova tra qualche minuto");
      } else {
        toast.error("Errore nel download. Verifica la connessione e riprova.");
      }
    } finally {
      setExportingCode(false);
    }
  };

  // Fetch info on mount and tab change
  useEffect(() => {
    fetchDatabaseInfo();
    fetchCodeInfo();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl">Sistema</h1>
        <PageInfoInline pageKey="sistema" />
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="admin-tabs-list">
          <TabsTrigger value="database" className="admin-tab-trigger flex items-center">
            <Database size={16} className="mr-2" /> Database
          </TabsTrigger>
          <TabsTrigger value="code" className="admin-tab-trigger flex items-center">
            <FileText size={16} className="mr-2" /> Codice
          </TabsTrigger>
        </TabsList>

        {/* Database Tab Content */}
        <TabsContent value="database">
          <div className="admin-card">
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h2 className="font-heading text-xl">Backup Database</h2>
                <p className="text-sm text-[#78716C]">Esporta una copia completa di tutti i dati</p>
              </div>
            
            {/* Database Info */}
            {loadingDatabaseInfo ? (
              <div className="flex items-center gap-2 text-sm text-[#78716C]">
                <RefreshCw size={14} className="animate-spin" />
                Caricamento statistiche database...
              </div>
            ) : databaseInfo && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-1">Documenti totali</p>
                  <p className="text-2xl font-heading text-[#1C1917]">{databaseInfo.total_documents?.toLocaleString()}</p>
                </div>
                <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-1">Collections</p>
                  <p className="text-2xl font-heading text-[#1C1917]">{databaseInfo.collections?.length}</p>
                </div>
                <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-1">Formato export</p>
                  <p className="text-lg font-medium text-[#1C1917]">ZIP</p>
                </div>
                <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-1">Stima dimensione</p>
                  <p className="text-lg font-medium text-[#1C1917]">{databaseInfo.estimated_size}</p>
                </div>
              </div>
            )}

            {/* Collections List */}
            {databaseInfo?.collections && (
              <div className="border border-[#E7E5E4] rounded-lg overflow-hidden">
                <div className="bg-[#F9F8F6] px-4 py-3 border-b border-[#E7E5E4]">
                  <h3 className="font-medium text-[#1C1917]">Collections incluse nel backup</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {databaseInfo.collections.map(col => (
                      <div key={col.name} className="flex items-center justify-between bg-[#FAFAF9] rounded px-3 py-2 text-sm">
                        <span className="text-[#44403C] truncate">{col.name}</span>
                        <span className="text-[#78716C] ml-2">{col.documents}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Export Button */}
            <div className="flex items-center justify-between pt-4 border-t border-[#E7E5E4]">
              <div>
                <p className="text-sm text-[#44403C]">Scarica un backup completo di tutti i dati</p>
                <p className="text-xs text-[#A8A29E] mt-1">
                  Include: prenotazioni, configurazioni, prezzi, log email, traduzioni e dati di sistema
                </p>
              </div>
              <Button 
                onClick={handleExportDatabase}
                disabled={exportingDatabase}
                size="lg"
                className="bg-[#1C1917] hover:bg-[#292524] text-white"
              >
                {exportingDatabase ? (
                  <>
                    <RefreshCw size={18} className="mr-2 animate-spin" />
                    Esportazione...
                  </>
                ) : (
                  <>
                    <Download size={18} className="mr-2" />
                    Scarica Backup Database
                  </>
                )}
              </Button>
            </div>

            {/* Portability Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800">
                <strong>Portabilità:</strong> L'export database è completamente standard e utilizza esclusivamente la connessione MongoDB. 
                Può essere importato su qualsiasi server con <code className="bg-blue-100 px-1 rounded">mongorestore</code> o manualmente.
              </p>
            </div>
            </div>
          </div>
        </TabsContent>

        {/* Code Tab Content */}
        <TabsContent value="code">
          <div className="admin-card">
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h2 className="font-heading text-xl">Export Codice Sorgente</h2>
                <p className="text-sm text-[#78716C]">Scarica il codice completo per migrazione</p>
              </div>
              
              {/* Code Info */}
              {loadingCodeInfo ? (
                <div className="flex items-center gap-2 text-sm text-[#78716C]">
                  <RefreshCw size={14} className="animate-spin" />
                  Caricamento statistiche codice...
                </div>
              ) : codeInfo && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                    <p className="text-sm text-[#78716C] mb-1">File totali</p>
                    <p className="text-2xl font-heading text-[#1C1917]">{codeInfo.total_files?.toLocaleString()}</p>
                  </div>
                  <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                    <p className="text-sm text-[#78716C] mb-1">Dimensione</p>
                  <p className="text-2xl font-heading text-[#1C1917]">{codeInfo.total_size_kb?.toLocaleString()} KB</p>
                </div>
                <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-1">Formato export</p>
                  <p className="text-lg font-medium text-[#1C1917]">{codeInfo.export_format}</p>
                </div>
                <div className="bg-[#F9F8F6] rounded-lg p-4 border border-[#E7E5E4]">
                  <p className="text-sm text-[#78716C] mb-1">Directory</p>
                  <p className="text-lg font-medium text-[#1C1917]">{codeInfo.directories?.length}</p>
                </div>
              </div>
            )}

            {/* Directories List */}
            {codeInfo?.directories && (
              <div className="border border-[#E7E5E4] rounded-lg overflow-hidden">
                <div className="bg-[#F9F8F6] px-4 py-3 border-b border-[#E7E5E4]">
                  <h3 className="font-medium text-[#1C1917]">Directory incluse nell'export</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {codeInfo.directories.map(dir => (
                      <div key={dir.name} className="flex items-center justify-between bg-[#FAFAF9] rounded px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-[#78716C]" />
                          <span className="font-medium text-[#44403C]">{dir.name}/</span>
                        </div>
                        <div className="text-right text-sm">
                          <span className="text-[#78716C]">{dir.files} file</span>
                          <span className="text-[#A8A29E] ml-2">({dir.size_kb} KB)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Security Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">File esclusi per sicurezza</p>
                  <p className="text-sm text-amber-700 mt-1">
                    I seguenti file <strong>NON</strong> sono inclusi nel download:
                  </p>
                  <ul className="text-sm text-amber-700 mt-2 space-y-1">
                    <li>• <code className="bg-amber-100 px-1 rounded">backend/.env</code> - Credenziali database, API keys</li>
                    <li>• <code className="bg-amber-100 px-1 rounded">frontend/.env</code> - URL backend</li>
                  </ul>
                  <p className="text-sm text-amber-700 mt-2">
                    Dovrai creare questi file manualmente con le tue credenziali dopo il download.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Export Button */}
            <div className="flex items-center justify-between pt-4 border-t border-[#E7E5E4]">
              <div>
                <p className="text-sm text-[#44403C]">Scarica il codice sorgente completo</p>
                <p className="text-xs text-[#A8A29E] mt-1">
                  Include: backend (FastAPI), frontend (React), file di configurazione
                </p>
              </div>
              <Button 
                onClick={handleExportCode}
                disabled={exportingCode}
                size="lg"
                className="bg-[#1C1917] hover:bg-[#292524] text-white"
              >
                {exportingCode ? (
                  <>
                    <RefreshCw size={18} className="mr-2 animate-spin" />
                    Esportazione...
                  </>
                ) : (
                  <>
                    <Download size={18} className="mr-2" />
                    Scarica Codice (ZIP)
                  </>
                )}
              </Button>
            </div>

            {/* Portability Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Portabilità:</strong> L'export è completamente standard e non ha dipendenze da Emergent. 
                Il codice può essere deployato su qualsiasi server (VPS, cloud, bare metal) con Python 3.11+ e Node.js 18+.
              </p>
            </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Calendar Management Page Wrapper
function CalendarManagementPage({ syncErrorsCount = 0, markAsVisited }) {
  const { getAuthHeaders } = useAuth();
  return <CalendarManagement getAuthHeaders={getAuthHeaders} syncErrorsCount={syncErrorsCount} markAsVisited={markAsVisited} />;
}

// Preventivo Page Wrapper
function PreventivoPageWrapper() {
  const { getAuthHeaders } = useAuth();
  return <PreventivoPage getAuthHeaders={getAuthHeaders} />;
}

function ReportsPageWrapper({ badgeCounts, refreshBadges }) {
  const { getAuthHeaders } = useAuth();
  return <ReportsPage getAuthHeaders={getAuthHeaders} badgeCounts={badgeCounts} onMarkSeen={refreshBadges} />;
}

function MessagesPageWrapper() {
  const { getAuthHeaders } = useAuth();
  return <MessagesPage getAuthHeaders={getAuthHeaders} />;
}

function PaymentLinkPageWrapper() {
  const { getAuthHeaders } = useAuth();
  return <PaymentLinkPage getAuthHeaders={getAuthHeaders} />;
}

function UpdatePageWrapper({ refreshBadges }) {
  const { getAuthHeaders } = useAuth();
  return <UpdatePage getAuthHeaders={getAuthHeaders} onMarkSeen={refreshBadges} />;
}

// Main Dashboard
export default function AdminDashboard() {
  const { logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminLogoConfig, setAdminLogoConfig] = useState({ logo_url: null, logo_size: 32 });
  const [badgeCounts, setBadgeCounts] = useState({ tracking: 0, logs: 0, transactions: 0, emails: 0, form_total: 0, payments_total: 0, overbookings: 0, updates: 0, sync_errors: 0 });
  const [emailServiceWarning, setEmailServiceWarning] = useState(false); // Warning badge for Resend/Monitoring
  
  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    details: null,
    variant: 'danger',
    confirmText: 'Conferma',
    cancelText: 'Annulla',
    onConfirm: null
  });

  // Helper function to show confirm modal (returns a Promise)
  const showConfirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        title: options.title || 'Conferma azione',
        message: options.message || 'Sei sicuro di voler procedere?',
        details: options.details || null,
        variant: options.variant || 'danger',
        confirmText: options.confirmText || 'Conferma',
        cancelText: options.cancelText || 'Annulla',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  }, []);

  // Close confirm modal
  const closeConfirmModal = useCallback(() => {
    if (confirmModal.onCancel) {
      confirmModal.onCancel();
    } else {
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
    }
  }, [confirmModal.onCancel]);
  
  // LocalStorage keys for "last visited" timestamps
  const VISIT_KEYS = {
    tracking: "admin_last_visit_tracking",
    logs: "admin_last_visit_logs",
    transactions: "admin_last_visit_transactions",
    emails: "admin_last_visit_emails",
    sync_errors: "admin_last_visit_sync_errors"
  };

  // Fetch badge counts including overbookings
  const fetchBadgeCounts = async () => {
    try {
      const since_tracking = localStorage.getItem(VISIT_KEYS.tracking) || "";
      const since_logs = localStorage.getItem(VISIT_KEYS.logs) || "";
      const since_transactions = localStorage.getItem(VISIT_KEYS.transactions) || "";
      const since_emails = localStorage.getItem(VISIT_KEYS.emails) || "";
      const since_sync = localStorage.getItem(VISIT_KEYS.sync_errors) || "";
      
      const params = new URLSearchParams();
      if (since_tracking) params.append("since_tracking", since_tracking);
      if (since_logs) params.append("since_logs", since_logs);
      if (since_transactions) params.append("since_transactions", since_transactions);
      if (since_emails) params.append("since_emails", since_emails);
      if (since_sync) params.append("since_sync", since_sync);
      
      const res = await axios.get(`${API}/admin/badge-counts?${params.toString()}`, getAuthHeaders());
      
      // Also fetch updates count
      let updatesCount = 0;
      try {
        const updRes = await axios.get(`${API}/admin/updates/count`, getAuthHeaders());
        updatesCount = updRes.data.count || 0;
      } catch (e) {
        console.error("Failed to fetch updates count:", e);
      }
      
      // Check email service status for warning badge
      try {
        const emailRes = await axios.get(`${API}/admin/email-stats`, getAuthHeaders());
        const emailStatus = emailRes.data?.status;
        // Show warning if status is not "operational" (e.g., "warning", "error", or undefined)
        setEmailServiceWarning(emailStatus !== "operational");
      } catch (e) {
        // If we can't fetch email stats, show warning
        setEmailServiceWarning(true);
      }
      
      // Badge counts now include overbookings from backend
      setBadgeCounts({
        ...res.data,
        updates: updatesCount
      });
    } catch (error) {
      console.error("Failed to fetch badge counts:", error);
    }
  };

  // Mark section as visited
  const markAsVisited = (section) => {
    const now = new Date().toISOString();
    localStorage.setItem(VISIT_KEYS[section], now);
    
    // Immediately update badge counts locally to give instant feedback
    setBadgeCounts(prev => {
      const updated = { ...prev };
      if (section === "tracking") {
        updated.tracking = 0;
        updated.form_total = 0;
      } else if (section === "logs") {
        updated.logs = 0;
        // Update payments_total: logs are now 0, keep overbookings
        updated.payments_total = prev.overbookings || 0;
      } else if (section === "transactions") {
        updated.transactions = 0;
      } else if (section === "emails") {
        updated.emails = 0;
      } else if (section === "tax_report") {
        updated.tax_report = 0;
      } else if (section === "sync_errors") {
        updated.sync_errors = 0;
      }
      return updated;
    });
    
    // Also refresh from server to ensure sync
    fetchBadgeCounts();
  };

  useEffect(() => {
    axios.get(`${API}/logo-config`).then(res => setAdminLogoConfig(res.data)).catch(() => {});
    fetchBadgeCounts();
    
    // Refresh badge counts every 30 seconds
    const interval = setInterval(fetchBadgeCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check current path and mark as visited
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/emails")) {
      // User is in Form section - will mark specific tab when they click
    } else if (path.includes("/payments")) {
      // User is in Payments section - will mark specific tab when they click
    }
  }, [location.pathname]);

  // Badge component
  const NotificationBadge = ({ count, className = "" }) => {
    if (!count || count <= 0) return null;
    return (
      <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-[#D4AF37] rounded-full ${className}`}>
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: <LayoutDashboard size={20} />, badge: 0 },
    { path: "/admin/dashboard/update", label: "Update", icon: <Bell size={20} />, badge: badgeCounts.updates },
    { path: "/admin/dashboard/calendar", label: "Booking", icon: <Calendar size={20} />, badge: badgeCounts.sync_errors },
    { path: "/admin/dashboard/payments", label: "Monitoring", icon: <CreditCard size={20} />, badge: badgeCounts.payments_total, warningBadge: emailServiceWarning },
    { path: "/admin/dashboard/preventivo", label: "Proposal", icon: <FileText size={20} />, badge: 0 },
    { path: "/admin/dashboard/payment-link", label: "Payment Link", icon: <Link2 size={20} />, badge: 0 },
    { path: "/admin/dashboard/reports", label: "Report", icon: <BarChart2 size={20} />, badge: badgeCounts.tax_report },
    { path: "/admin/dashboard/emails", label: "Email Form", icon: <MessageSquare size={20} />, badge: 0 },
    { path: "/admin/dashboard/messages", label: "Messages", icon: <Mail size={20} />, badge: 0 },
    { path: "/admin/dashboard/sections", label: "Sections", icon: <Layers size={20} />, badge: 0 },
    { path: "/admin/dashboard/typography", label: "Typography", icon: <Type size={20} />, badge: 0 },
    { path: "/admin/dashboard/legal", label: "Legal", icon: <FileText size={20} />, badge: 0 },
  ];
  
  // Account separato per posizionamento sopra divider
  const accountItem = { path: "/admin/dashboard/account", label: "Account", icon: <Settings size={20} />, badge: 0 };
  
  // Sistema - area tecnica separata
  const sistemaItem = { path: "/admin/dashboard/sistema", label: "Sistema", icon: <Database size={20} />, badge: 0 };

  return (
    <div className="min-h-screen flex overflow-x-hidden">
      <button className="fixed top-4 left-4 z-50 md:hidden bg-[#1C1917] text-white p-2 rounded" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? <X size={24} /> : <LayoutDashboard size={24} />}
      </button>
      <aside className={`admin-sidebar fixed w-64 h-screen z-[60] transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} flex flex-col top-0 left-0`}>
        {/* Header con Logo */}
        <div className="p-4 md:p-6 flex-shrink-0">
          {/* Logo - apre il sito in nuova scheda senza logout */}
          <a 
            href={window.location.origin} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center gap-2 mb-4 md:mb-8"
            onClick={(e) => {
              e.stopPropagation();
              window.open(window.location.origin, '_blank');
              e.preventDefault();
            }}
          >
            {adminLogoConfig.logo_url ? (
              <img 
                src={adminLogoConfig.logo_url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${adminLogoConfig.logo_url}` : adminLogoConfig.logo_url} 
                alt="Suite Lucina" 
                style={{ height: `${Math.min(adminLogoConfig.logo_size, 40)}px` }}
                className="invert object-contain"
              />
            ) : (
              <img src="https://customer-assets.emergentagent.com/job_lucina-suite/artifacts/zplyxqc9_logo.png" alt="Suite Lucina" className="h-8 invert" />
            )}
          </a>
        </div>
        
        {/* Menu principale */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 pb-2">
          <nav className="space-y-1 md:space-y-2">
            {navItems.map(item => (
              <Link key={item.path} to={item.path} className={`flex items-center justify-between gap-3 px-3 md:px-4 py-3 rounded transition-colors text-base ${location.pathname === item.path ? "bg-white/10 text-white" : ""}`} onClick={() => setSidebarOpen(false)}>
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Warning badge (oro) per problemi email service */}
                  {item.warningBadge && (
                    <span className="w-2.5 h-2.5 rounded-full bg-[#D4AF37] animate-pulse" title="Servizio email non operativo" />
                  )}
                  <NotificationBadge count={item.badge} />
                </div>
              </Link>
            ))}
            {/* Account - sempre sopra il divider */}
            <Link to={accountItem.path} className={`flex items-center justify-between gap-3 px-3 md:px-4 py-3 rounded transition-colors text-base ${location.pathname === accountItem.path ? "bg-white/10 text-white" : ""}`} onClick={() => setSidebarOpen(false)}>
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center">{accountItem.icon}</span>
                <span>{accountItem.label}</span>
              </div>
              <NotificationBadge count={accountItem.badge} />
            </Link>
            {/* Sistema - area tecnica */}
            <Link to={sistemaItem.path} className={`flex items-center justify-between gap-3 px-3 md:px-4 py-3 rounded transition-colors text-base ${location.pathname.startsWith(sistemaItem.path) ? "bg-white/10 text-white" : ""}`} onClick={() => setSidebarOpen(false)}>
              <div className="flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center">{sistemaItem.icon}</span>
                <span>{sistemaItem.label}</span>
              </div>
              <NotificationBadge count={sistemaItem.badge} />
            </Link>
          </nav>
        </div>
        
        {/* Footer fisso - View Site e Logout sempre visibili */}
        <div className="flex-shrink-0 px-4 md:px-6 py-3 md:py-4 mt-1 md:mt-6 border-t border-white/10">
          <a 
            href={window.location.origin} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center gap-3 px-3 md:px-4 py-3 text-white/70 hover:text-white transition-colors text-base"
            onClick={(e) => {
              e.stopPropagation();
              window.open(window.location.origin, '_blank');
              e.preventDefault();
            }}
          >
            <Eye size={20} /><span>View Site</span>
          </a>
          <button onClick={logout} className="flex items-center gap-3 px-3 md:px-4 py-3 text-white/70 hover:text-white transition-colors w-full text-base">
            <LogOut size={20} /><span>Logout</span>
          </button>
        </div>
      </aside>
      {/* Contenuto principale - margine sinistro per compensare sidebar fissa su desktop */}
      <main className="flex-1 min-w-0 p-6 md:p-12 admin-content ml-0 md:ml-64">
        <div className="max-w-6xl mx-auto w-full">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="update" element={<UpdatePageWrapper refreshBadges={fetchBadgeCounts} />} />
            <Route path="sections" element={<SectionManagement />} />
            <Route path="calendar" element={<CalendarManagementPage syncErrorsCount={badgeCounts.sync_errors} markAsVisited={markAsVisited} />} />
            <Route path="payments" element={<PaymentsManagement badgeCounts={badgeCounts} markAsVisited={markAsVisited} showConfirm={showConfirm} emailServiceWarning={emailServiceWarning} />} />
            <Route path="typography" element={<TypographySettings />} />
            {/* Gallery route rimosso - ora gestito tramite Content Block */}
            <Route path="legal" element={<LegalManagement />} />
            <Route path="emails" element={<EmailLogs badgeCounts={badgeCounts} markAsVisited={markAsVisited} />} />
            <Route path="preventivo" element={<PreventivoPageWrapper />} />
            <Route path="payment-link" element={<PaymentLinkPageWrapper />} />
            <Route path="reports" element={<ReportsPageWrapper badgeCounts={badgeCounts} refreshBadges={fetchBadgeCounts} />} />
            <Route path="messages" element={<MessagesPageWrapper />} />
            <Route path="account" element={<AccountSettings showConfirm={showConfirm} />} />
            <Route path="sistema" element={<SystemSettings showConfirm={showConfirm} />} />
          </Routes>
        </div>
      </main>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-[55] md:hidden" onClick={() => setSidebarOpen(false)} />}
      
      {/* Global Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm || (() => {})}
        title={confirmModal.title}
        message={confirmModal.message}
        details={confirmModal.details}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
      />
    </div>
  );
}
